/**
 * PDP image generation path handlers.
 *
 * Three paths for creating PDP images:
 * 1. AI Generate – fully AI-generated concept images
 * 2. Layered Image – AI-generated then auto-converted to editable layers
 * 3. From Template – uses existing PDP templates from Design Gallery
 *
 * These handlers are surface-agnostic: they talk to the chat through an
 * adapter object (see cs-pdp-adapter.js and sidebar-pdp-adapter.js).
 */
import { getTypeKeys } from '@frontend/store/creative-studio/template-showcase-tags';
import { unwrapShareUrl } from '@frontend/store/creative-studio/share-url';
import { marketplaceLanguage } from '@frontend/store/creative-studio/amazon-asin';
import { postDesignAndAwait } from '@frontend/store/creative-studio/await-design-job';
import { buildReferenceDesignPayload, extractProductBrief } from '@frontend/store/creative-studio/chat-function-handlers/pdp-reference-generation';
import { loadImageTypeCatalog, getTypeDirection } from '@frontend/store/creative-studio/chat-function-handlers/image-type-catalog';
import { findPickerWidgetMessageIndex, withPickerSequentialCursor } from '@frontend/store/creative-studio/pdp-picker-edit';
import { currentTileVersionIndex, finishedTileFromResult, patchTileVersion, tileVersions } from '@frontend/store/creative-studio/pdp-tile-versions';
import { saveImageToAiStudio } from '@frontend/store/creative-studio/ai-studio-library';
import { aiStudioFileName } from '@frontend/store/creative-studio/ai-studio-file-name';
import { buildNoBaseEditConfig, buildPdpEditPrompt } from '@frontend/store/creative-studio/pdp-edit-prompt';
import { attachedProductAsReference } from '@frontend/store/creative-studio/pdp-product-reference';
import { overlayTextPrompt } from '@frontend/store/creative-studio/overlay-text-prompt';
import { briefWidgetFields } from '@frontend/store/creative-studio/brief-widget-fields';

// Sentinel used inside pre-built result-message snapshots so the chat reload
// recovery in NetworksPageV2 knows which attachment payload to replace with
// the actual generated URL.
export const PENDING_DESIGN_JOB_PLACEHOLDER = '__PENDING_DESIGN_JOB_URL__';

// Serializes persistence of the parallel-grid message. Multiple concurrent
// pool workers (and ad-hoc tile regenerates) patch the SAME grid message; if
// their async saves resolve out of order, REPLACE_MESSAGE_BY_ID could re-apply
// a stale meta snapshot and revert a tile. We commit in-memory immediately
// (instant UI) but chain the network saves so the latest state always lands
// last. (POR-13183)
let pdpGridSaveChain = Promise.resolve();
function persistPdpGrid(adapter, gridMessageIndex) {
  pdpGridSaveChain = pdpGridSaveChain
    .then(() => {
      const idx = typeof gridMessageIndex === 'function' ? gridMessageIndex() : gridMessageIndex;
      const msg = adapter.getMessages()[idx];
      if (!msg) return null;
      // Re-save whatever is the LATEST in-memory grid state at execution time,
      // so the serialized tail always persists the freshest snapshot.
      return adapter.updateAndSaveMessage(idx, { _pdpParallelGrid: msg._pdpParallelGrid });
    })
    .catch(() => {});
  return pdpGridSaveChain;
}

/**
 * Apply an update to a parallel-grid message: compute the next grid via
 * `updater(currentGrid)`, commit it to memory immediately (instant UI), then
 * persist via the shared serialized save chain so concurrent grid mutations
 * (pool workers, tile regenerate, Stop/Skip) can't revert each other through
 * out-of-order save responses. (POR-13183)
 */
export function applyPdpGridUpdate(adapter, messageIndex, updater) {
  const msg = adapter.getMessages()[messageIndex];
  const grid = msg && msg._pdpParallelGrid;
  if (!grid) return;
  const nextGrid = updater(grid);
  adapter.updateMessage(messageIndex, { _pdpParallelGrid: nextGrid });
  persistPdpGrid(adapter, messageIndex);
}

// The one place a grid tile becomes a finished image (POR-14659): the result is
// appended as a new version and made current. Every finishing path — the pool,
// Retry, Regenerate (incl. Layered/Static), a configured "+ Add" tile and Edit —
// goes through here so none of them can overwrite the history.
function commitFinishedTile(adapter, gridMessageIndex, rowIndex, result, extra = {}) {
  const idx = typeof gridMessageIndex === 'function' ? gridMessageIndex() : gridMessageIndex;
  const msg = adapter.getMessages()[idx];
  const grid = msg && msg._pdpParallelGrid;

  if (!grid) return;

  const tiles = grid.tiles.map((t) => (t.rowIndex === rowIndex ? finishedTileFromResult(t, result, extra) : t));

  adapter.updateMessage(idx, { _pdpParallelGrid: { ...grid, tiles } });
  persistPdpGrid(adapter, gridMessageIndex);

  if (!extra.skipLibrarySave) {
    saveCurrentTileVersionToLibrary(adapter, gridMessageIndex, rowIndex);
  }
}

// Copy the tile's current version into the "AI Studio" folder in the background and
// remember the durable copy on THAT version — matched by url, because the user may
// have moved to another version meanwhile. (POR-14659)
export function saveCurrentTileVersionToLibrary(adapter, gridMessageIndex, rowIndex) {
  const resolveIndex = () => (typeof gridMessageIndex === 'function' ? gridMessageIndex() : gridMessageIndex);
  const msg = adapter.getMessages()[resolveIndex()];
  const tile = msg && msg._pdpParallelGrid && msg._pdpParallelGrid.tiles.find((t) => t.rowIndex === rowIndex);

  if (!tile || !tile.url) return;

  const savedUrl = tile.url;

  saveImageToAiStudio({
    url: savedUrl,
    overlayUrl: tile.overlayUrl,
    _mrhiCallouts: tile._mrhiCallouts,
    name: aiStudioFileName(tile.label, currentTileVersionIndex(tile) + 1),
  }).then((saved) => {
    if (!saved) return;
    const idx = resolveIndex();
    const current = adapter.getMessages()[idx];
    const grid = current && current._pdpParallelGrid;
    const owner = grid && grid.tiles.find((t) => t.rowIndex === rowIndex);
    const versionIndex = owner ? tileVersions(owner).findIndex((v) => v.url === savedUrl) : -1;

    if (versionIndex === -1) return;

    applyPdpGridUpdate(adapter, idx, (g) => ({
      ...g,
      tiles: g.tiles.map((t) => (t.rowIndex === rowIndex ? patchTileVersion(t, versionIndex, { libraryUrl: saved.url }) : t)),
    }));
  });
}

// Serializes EVERY network save of the PDP picker message (`pdp_image_set_picker`).
// SIX independent paths patch that SAME message, and every one of them must go
// through here — syncPickerSequentialCursor (below), plus these five in
// NetworksPageV2.vue: the row-picker confirm, the own-image / brand-logo /
// product-image modal results, and persistPdpRowUpdate.
// Each save POSTs the message and then applies the server echo through
// REPLACE_MESSAGE_BY_ID, which deliberately has NO `widgets` carve-out — so two
// concurrent POSTs whose responses resolve out of order let the OLDER echo land
// last and revert the widget: to a stale `sequentialCursor`, or (when the losing
// echo predates the first stamp) to no cursor at all, which reads to the user as
// every check badge and wand disappearing at once.
// Concrete trigger: when the first queued row is "use as-is", keepUseAsIsRow
// advances the index and re-enters generateNextInPdpQueue immediately, so the
// cursor is stamped twice (0 then 1) within the same tick. Same contract as
// persistPdpGrid: commit in memory immediately (instant UI), chain only the
// network saves, and re-read the latest in-memory state at execution time. Do not
// collapse this back into a bare updateAndSaveMessage, and do not add a new
// picker write that bypasses it. (POR-13869)
let pickerMessageSaveChain = Promise.resolve();

export function queuePickerMessageSave(task) {
  // `.catch` on the REASSIGNED chain: one failed save must not poison every later one.
  pickerMessageSaveChain = pickerMessageSaveChain.then(task).catch(() => {});
  return pickerMessageSaveChain;
}

/**
 * Find the most recent chat message holding a parallel-grid widget, or -1 if
 * none exists yet. Shared by startPdpParallelGrid (its in-flight tile patches)
 * and any caller that needs to locate the active grid from outside that run,
 * e.g. the "add more image types" chat-function handler.
 */
export function findLatestPdpGridIndex(adapter) {
  const msgs = adapter.getMessages() || [];

  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (msgs[i] && msgs[i]._pdpParallelGrid && (msgs[i].widgets || []).some((w) => w.id === 'pdp_parallel_grid')) return i;
  }

  return -1;
}

// POR-13504: a generation "belongs to" the conversation that was active when it
// started. If the user clicks "New" (or opens another chat) mid-generation, its
// late async tile/result writes must be discarded instead of leaking into the
// now-active chat. Capture the owner once at the start of a generation via
// makeConversationStaleCheck(adapter), then call the returned predicate before
// each writeback and between pool iterations. A null owner (a surface with no
// conversation concept, e.g. the editor sidebar whose adapter has no
// getActiveConversationId) disables the guard, preserving existing behavior.
function currentConversationId(adapter) {
  return typeof adapter.getActiveConversationId === 'function'
    ? adapter.getActiveConversationId()
    : null;
}
export function makeConversationStaleCheck(adapter) {
  const ownerConversationId = currentConversationId(adapter);
  return () => ownerConversationId != null && currentConversationId(adapter) !== ownerConversationId;
}

export const DEFAULT_SET_TYPES = ['hero', 'lifestyle', 'how_to'];

// Types that render as standalone, text-only images. In layered mode we
// neither send the product as a reference nor add it as an overlay layer -
// the AI generates the full final image and that is what the user sees.
export const STANDALONE_LAYER_TYPES = ['nutrition_facts'];

export function isStandaloneLayerType(imageType) {
  return STANDALONE_LAYER_TYPES.includes(imageType);
}

// Infographic types whose prompt tells Gemini NOT to draw the product but to
// describe its features/benefits/ingredients/usage. With only a raw product id
// as the label, Gemini hallucinates unrelated content — so we first extract a
// real product brief from the packshot (extractProductBrief) and inject it into
// these prompts. nutrition_facts is excluded: it needs real numeric label data
// and is a STANDALONE_LAYER_TYPE (no product reference passed). (POR-13307)
export const GROUNDABLE_INFOGRAPHIC_TYPES = ['feature_benefits', 'reason_to_believe', 'ingredients', 'how_to'];

export function isGroundableInfographicType(imageType) {
  return GROUNDABLE_INFOGRAPHIC_TYPES.includes(imageType);
}

// Image types whose prompt already keeps the product out of the picture. A tile
// with no product keeps their composition and only adds NO_PRODUCT_RULE; every
// other type is built around the product, so its prompt is replaced. (POR-14794)
const PRODUCT_FREE_TYPES = [...GROUNDABLE_INFOGRAPHIC_TYPES, ...STANDALONE_LAYER_TYPES];

const NO_PRODUCT_RULE = 'NO PRODUCT IMAGE WAS PROVIDED: do NOT draw, show, render or invent any product — no package, box, bottle, tube, jar, can, product label or brand logo — anywhere in the image, not even small or in the background. Plain, unbranded items the directions say are used (for example an applicator) may be shown.';

/**
 * Call /networks/design with transparent retries for transient Gemini failures.
 * The backend already retries 3 times (2/4/8s backoff) on retryable HTTP and
 * Gemini RECITATION/IMAGE_RECITATION/OTHER finishReasons, but Gemini also
 * occasionally returns empty responses or fails with non-retryable reasons
 * that succeed on a fresh request. Wrapping the call in two more attempts
 * with a short pause hides the most common intermittent failures from the
 * user — the chat only surfaces "Failed to generate" if all attempts fail.
 */
async function postDesignWithRetry(payload, { maxAttempts = 1, onUuid = null, source = 'creative_studio_chat' } = {}) {
  // The endpoint is now async (dispatches a queued job and resolves via Pusher).
  // Each attempt waits up to 5 minutes for the broadcast, so retrying on the
  // frontend would compound timeouts. We do a single attempt and surface the
  // error; transient Gemini failures that used to be hidden by the synchronous
  // backend retry are surfaced as "Failed to generate" instead.
  let lastError = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const images = await postDesignAndAwait(
        '/networks/design',
        { ...payload, source },
        false,
        onUuid,
      );
      const url = Array.isArray(images)
        ? (images[0]?.image_url ?? images[0])
        : images;
      if (url) return url;
      lastError = new Error('No image URL returned');
    } catch (e) {
      lastError = e;
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
    }
  }
  throw lastError || new Error('Design generation failed');
}

/**
 * Run `worker(item, index)` over `items` with at most `limit` concurrent
 * executions (POR-13183). Before launching each not-yet-started item, calls
 * `shouldStop()`; if it returns true, the remaining items are skipped (their
 * worker is NOT called) and `onSkipped(item, index)` is invoked instead.
 * In-flight workers always run to completion. Resolves when the pool drains.
 */
export async function runPool(items, limit, worker, { shouldStop = () => false, onSkipped = null } = {}) {
  const queue = items.map((item, index) => ({ item, index }));
  let cursor = 0;
  const runNext = async () => {
    while (cursor < queue.length) {
      const current = queue[cursor];
      cursor += 1;
      if (shouldStop()) {
        if (onSkipped) onSkipped(current.item, current.index);
        continue;
      }
      try {
        await worker(current.item, current.index);
      } catch (e) {
        // Worker-level errors are the worker's responsibility to surface (e.g.
        // mark the item failed). The pool must keep draining regardless.
      }
    }
  };
  const runners = [];
  for (let i = 0; i < Math.min(limit, queue.length); i += 1) runners.push(runNext());
  await Promise.all(runners);
}

/**
 * Run the GS1 declutter/simplify for a given approach and return the simplified
 * packshot URL (or null). Mirrors the JIT simplify block in generateNextInPdpQueue. (POR-13183)
 */
async function simplifyGs1Overlay(overlayUrl, gs1Approach) {
  if (!overlayUrl) return null;
  const approach = (gs1Approach && gs1Approach.mode) || 'auto';
  const payload = { image_url: overlayUrl, approach };
  if (approach === 'reference' && gs1Approach && gs1Approach.referenceUrl) payload.reference_url = gs1Approach.referenceUrl;
  if (approach === 'describe' && gs1Approach && gs1Approach.userInstructions) payload.user_instructions = gs1Approach.userInstructions;
  try {
    const { data } = await axios.post('/networks/simplify-product-image', payload);
    return (data && data.simplified_url) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Compose a pre-resolved scene background with the product packshot and return
 * the composite URL (or null). Mirrors regenerateSceneImage's compose call. (POR-13183)
 */
async function composeSceneTileUrl({ sceneUrl, productUrl, userInstructions }) {
  if (!sceneUrl || !productUrl) return null;
  try {
    const { data } = await axios.post('/networks/compose-scene', {
      scene_url: sceneUrl,
      product_url: productUrl,
      user_instructions: userInstructions || '',
    });
    return (data && data.composite_url) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Generate a single reference-based PDP image: place the target product
 * (baseProductUrl) into the reference scene (referenceUrl). Returns the URL.
 */
export async function generatePdpReferenceImageUrl(adapter, { baseProductUrl, referenceUrl, size, userInstructions = null, overlayText = null, overlayTextMode = 'editable', brandLogoUrl = null, forceEditHighRes = false, language = null, extraProductUrls = [] }) {
  const payload = await buildReferenceDesignPayload({ baseProductUrl, referenceUrl, userInstructions, overlayText, overlayTextMode, brandLogoUrl, language, extraProductUrls });

  return postDesignWithRetry(
    {
      image_prompt: payload.image_prompt,
      user_sizes: size,
      product_images_names: payload.product_images_names,
      reference_images_urls: payload.reference_images_urls,
      // Opt-in (A/B hero): native 2K/4K edit so hero text stays sharp. (POR-13385-5)
      ...(forceEditHighRes ? { force_edit_high_res: true } : {}),
      selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
    },
    { source: adapter.getSource?.() || 'creative_studio_chat' },
  );
}

/**
 * Generate a Hero-family reference row on a pure white background using the
 * canonical per-type hero prompt (buildPdpImagePrompt). The reference scene is
 * intentionally ignored — Hero must be the product on plain white.
 */
export async function generatePdpHeroWhiteBgImageUrl(adapter, { type, productContext, size, productUrl, userInstructions = null, overlayText = null, overlayTextMode = 'editable', brandLogoUrl = null, language = null }) {
  const prompt = buildPdpImagePrompt(type, productContext, userInstructions || null, 'integrated', null, overlayText || null, brandLogoUrl || null, null, overlayTextMode, false, language);

  return postDesignWithRetry(
    {
      image_prompt: prompt,
      user_sizes: size,
      product_images_names: productUrl ? [productUrl] : [],
      reference_images_urls: [],
      selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
    },
    { source: adapter.getSource?.() || 'creative_studio_chat' },
  );
}

// New canonical catalog of built-in slot types (POR-12701). Each entry is the
// stable key the sequence_slots table stores and that drives prompt selection.
//
// `noOverlay: true` flags AI image types whose Gemini output is a complete,
// self-contained composition (Feature Benefits grid, Reason To Believe layout,
// Ingredients callouts, How To step panels). For these types we default the
// per-image product mode to 'integrated' so the user's packshot is NOT
// stamped on top via the layered overlay — that overlay tends to cover the
// very content the AI was asked to generate. The user can still flip an
// individual image to 'separate' (Layered) via the on-image toggle if they
// want the overlay back. Gallery-row equivalent is the
// `pdp_subtype_no_overlay` tag on the template itself.
export const BUILT_IN_TYPES = {
  hero:                   { key: 'hero',                   label: 'Hero' },
  mobile_hero:            { key: 'mobile_hero',            label: 'Mobile Hero' },
  mobile_hero_cambridge:  { key: 'mobile_hero_cambridge',  label: 'Mobile Hero - Cambridge' },
  mobile_hero_gs1:        { key: 'mobile_hero_gs1',        label: 'Mobile Hero - GS1' },
  mobile_hero_reference:  { key: 'mobile_hero_reference',  label: 'Mobile Hero - Reference' },
  ingredients:            { key: 'ingredients',            label: 'Ingredients',           noOverlay: true },
  nutrition_facts:        { key: 'nutrition_facts',        label: 'Nutrition Facts',       noOverlay: true },
  lifestyle:              { key: 'lifestyle',              label: 'Lifestyle' },
  how_to:                 { key: 'how_to',                 label: 'How To',                noOverlay: true },
  feature_benefits:       { key: 'feature_benefits',       label: 'Feature-Benefits',      noOverlay: true },
  reason_to_believe:      { key: 'reason_to_believe',      label: 'Reason To Believe',     noOverlay: true },
  product_gallery:            { key: 'product_gallery',            label: 'Product Gallery',                          noOverlay: true },
  cross_sell:                 { key: 'cross_sell',                 label: 'Cross-Sell',                               noOverlay: true },
  category_gallery:           { key: 'category_gallery',           label: 'Category Gallery',                         noOverlay: true },
  feature_iconography_around: { key: 'feature_iconography_around', label: 'Feature & Iconography - Around Product',   noOverlay: true },
  feature_iconography_bottom: { key: 'feature_iconography_bottom', label: 'Feature & Iconography - Bottom of Image',  noOverlay: true },
};

// Integrated types that BAKE THE REAL PRODUCT INTO the generated image, so a supplied
// packshot must survive generation untouched. Deliberately excludes the types whose
// prompts forbid drawing the product at all (ingredients, feature_benefits,
// reason_to_believe, how_to — they use it only as a color/style cue via
// brandAccentRule), and lifestyle, which has its own full-prompt replacement.
// Hero / Cambridge / GS1 default to Layered, where the real packshot is overlaid
// afterwards and is already pixel-perfect. (POR-13881-2)
export const PRODUCT_FIDELITY_TYPES = [
  'product_gallery',
  'cross_sell',
  'category_gallery',
  'feature_iconography_around',
  'feature_iconography_bottom',
];

// Legacy key → new key aliases. Kept to avoid breaking messages persisted with
// the previous vocabulary and to tolerate the AI function schema still emitting
// old keys.
export const LEGACY_TYPE_ALIASES = {
  features: 'feature_benefits',
};

// Preset-source id for the image types attached as chips before sending. (POR-14039)
export const ATTACHED_IMAGE_TYPES_SOURCE_ID = 'attached-image-types';

// The Library's image-type vocabulary (constants/system-image-types.js) is wider
// than what CS can generate. These map the Library-only keys to their closest
// generatable equivalent so a sequence slot never silently disappears.
export const SEQUENCE_TYPE_FALLBACKS = {
  elevated_hero: 'lifestyle',
  then_now: 'lifestyle',
  icon_set: 'feature_iconography_bottom',
  rtb_image: 'reason_to_believe',
  mobile_hero_a: 'mobile_hero',
  feature_iconography: 'feature_iconography_around',
};

/**
 * Resolve a sequence slot's `key` to a canonical BUILT_IN_TYPES key.
 * Unknown / custom / display-only keys degrade to a generatable default
 * (never throws, never yields an ungeneratable type).
 */
export function resolveCanonicalKey(key) {
  if (!key) return 'lifestyle';
  if (BUILT_IN_TYPES[key]) return key;
  if (LEGACY_TYPE_ALIASES[key]) return LEGACY_TYPE_ALIASES[key];
  if (SEQUENCE_TYPE_FALLBACKS[key]) return SEQUENCE_TYPE_FALLBACKS[key];
  return 'lifestyle';
}

/**
 * Turn a Library sequence's slots into preset-source slots for the picker's
 * "Sequence preset…" dropdown. Keys CS cannot generate resolve to their closest
 * equivalent rather than being dropped — a sequence must show the same number of
 * cards here as it does through the "Build in AI Studio" handoff. The slot keeps
 * its own label so an Elevated Hero card still reads "Elevated Hero". Slots with
 * no key at all (reference / described slots) stay out: a preset source carries
 * no image, so they have nothing to become. (POR-14020)
 */
export function sequenceSlotsToPresetSlots(sequence) {
  return ((sequence && sequence.slots) || [])
    .filter((s) => s.key)
    .map((s) => {
      const key = resolveCanonicalKey(s.key);
      return {
        key,
        label: s.label || BUILT_IN_TYPES[key].label,
        // effective_source already folds the Image Type's default into the slot's
        // own override; `source` is null whenever the slot inherits. (POR-14020)
        source: (s.effective_source || s.source) === 'upload' ? 'upload' : 'ai',
        // The Library key before it was resolved to a generatable one. The
        // creative direction the company configured lives under THIS key, not
        // under whatever `key` degraded to. (POR-14039)
        libraryKey: s.key,
      };
    });
}

/**
 * Preset source built from the Image Types the user attached as chips in the
 * input dock before sending the message (Add a reference → Image Types). Same
 * role a sequence chip plays, one image type at a time: an explicit pick is the
 * strongest statement of intent, so the picker opens on it.
 *
 * Reference key format is `image-types|<key>|<label>` (see
 * NetworksDock.buildReferenceKey). Keys are deduped AFTER resolution — two
 * Library types that degrade to the same generatable key would otherwise render
 * as two checkboxes wired to one another. (POR-14039)
 */
export function attachedImageTypesPresetSource(adapter) {
  const refs = (typeof adapter.getSelectedReferences === 'function')
    ? (adapter.getSelectedReferences() || [])
    : [];

  const seen = new Set();
  const slots = [];
  refs.forEach((ref) => {
    const [kind, id, label] = String(ref || '').split('|');
    if (kind !== 'image-types' || !id) return;
    const key = resolveCanonicalKey(id);
    if (seen.has(key)) return;
    seen.add(key);
    slots.push({
      key,
      label: label || (BUILT_IN_TYPES[key] && BUILT_IN_TYPES[key].label) || TYPE_LABELS[key] || key,
      source: 'ai',
      libraryKey: id,
    });
  });

  if (!slots.length) return null;

  return {
    id: ATTACHED_IMAGE_TYPES_SOURCE_ID,
    source: 'attached',
    sequenceId: null,
    label: 'Selected image types',
    count: slots.length,
    slots,
    defaultSelectedKeys: slots.map((s) => s.key),
  };
}

export const TYPE_LABELS = {
  hero: 'Hero',
  mobile_hero: 'Mobile Hero',
  mobile_hero_cambridge: 'Mobile Hero - Cambridge',
  mobile_hero_gs1: 'Mobile Hero - GS1',
  mobile_hero_reference: 'Mobile Hero - Reference',
  ingredients: 'Ingredients',
  nutrition_facts: 'Nutrition Facts',
  lifestyle: 'Lifestyle',
  how_to: 'How To',
  feature_benefits: 'Feature-Benefits',
  reason_to_believe: 'Reason To Believe',
  product_gallery: 'Product Gallery',
  cross_sell: 'Cross-Sell',
  category_gallery: 'Category Gallery',
  feature_iconography_around: 'Feature & Iconography - Around Product',
  feature_iconography_bottom: 'Feature & Iconography - Bottom of Image',
  features: 'Feature-Benefits',
  comparison: 'Comparison',
  product_detail: 'Product Detail',
};

// Hero family always renders on a pure white background (long-standing retailer
// rule, enforced by buildPdpImagePrompt). Reference rows of these types bypass
// the scene-based reference prompt and use the canonical white-bg hero prompt.
export const HERO_WHITE_BG_TYPES = ['hero'];

/**
 * Build a default `pdp_row_selections` array from a list of image types. Items
 * are either a plain key string or a slot `{ key, label, source }`:
 *  - `label` wins over the type's own name, so a Library slot whose key resolved
 *    to a different generatable type still reads as itself — an Elevated Hero
 *    card says "Elevated Hero", not "Lifestyle". (POR-14020)
 *  - `source: 'upload'` presets the row to "yours" so the picker waits for an
 *    image from the user.
 * Everything else defaults to `mode: 'ai'`; gallery / scene stay explicit user
 * choices.
 */
export function buildDefaultRowSelections(typeKeys) {
  return (typeKeys || []).map((entry, index) => {
    const isSlot = !!entry && typeof entry === 'object';
    const key = isSlot ? entry.key : entry;
    const source = isSlot ? entry.source : null;
    return {
      rowIndex: index,
      type: key,
      // Carried so the prompt can read the creative direction configured on the
      // Library type even when `type` degraded to a generatable one. (POR-14039)
      libraryKey: (isSlot && entry.libraryKey) || key,
      label: (isSlot && entry.label)
        || (BUILT_IN_TYPES[key] && BUILT_IN_TYPES[key].label)
        || TYPE_LABELS[key]
        || key,
      mode: source === 'upload' ? 'yours' : 'ai',
      template_id: null,
      template_thumb: null,
      scene: null,
      productMode: null,
    };
  });
}

/**
 * Re-index a row-selections array so `rowIndex` matches the array position
 * after add / remove / reorder operations.
 */
export function reindexRowSelections(rows) {
  return (rows || []).map((row, i) => ({ ...row, rowIndex: i }));
}

/**
 * Find the chat message that holds the kept artifact for the row at `rowIndex`.
 * Falls back to type matching when older messages don't carry `rowIndex`
 * (legacy conversations from before the mixed widget shipped).
 */
export function findKeptMessageForRow(messages, rowIndex, fallbackType = null) {
  const reversed = [...(messages || [])].reverse();
  const byRow = reversed.find((m) =>
    m._pdpImageContext
    && m._pdpImageContext.rowIndex === rowIndex
    && m._pdpImageKept,
  );
  if (byRow) return byRow;
  if (fallbackType) {
    return reversed.find((m) =>
      m._pdpImageContext
      && m._pdpImageContext.type === fallbackType
      && m._pdpImageKept
      && m._pdpImageContext.rowIndex === undefined,
    ) || null;
  }
  return null;
}

export function normalizeTypeKey(key) {
  return LEGACY_TYPE_ALIASES[key] || key;
}

/**
 * Single source of truth for "did the user ask for a set?". Used to drive the
 * singular/plural copy throughout the PDP flow ("PDP image" vs "PDP images").
 */
export function isPdpSetIntent(parsedPrompt) {
  const imageTypes = (parsedPrompt && parsedPrompt.pdp_image_types) || [];
  return (parsedPrompt && parsedPrompt.pdp_is_set === true) || imageTypes.length > 1;
}

/**
 * Show the sequence picker for a PDP set request.
 * Singular intent → skip and return false (caller goes to size picker).
 * Set intent (explicit multi-type, or is_set=true) → render the sequence
 * picker widget and return true (caller should stop).
 */
export async function showImageSetSuggestion(adapter, pathType) {
  if (!isPdpSetIntent(adapter.getParsedPrompt())) {
    return false;
  }

  await showSequencePicker(adapter, pathType);
  return true;
}

/**
 * Render the sequence picker widget. Loads the company sequence library, the
 * per-product AI recommendation, and the user's last pick in parallel, then
 * picks the initial source:
 *   AI library match (per-product) > last library pick (global) > AI Recommended.
 */
export async function showSequencePicker(adapter, pathType) {
  const parsedPrompt = adapter.getParsedPrompt();
  const productRefs = (parsedPrompt.product_references || []).filter((r) => !r._deselected);
  const firstRef = productRefs[0] || {};
  const allRefNames = productRefs
    .map((r) => r.display_name || r.value)
    .filter(Boolean)
    .join(', ');
  const recommendBody = {
    product_name: allRefNames || parsedPrompt.parsed_prompt || '',
    product_category: firstRef.category || null,
    asin: firstRef.type === 'asin' ? firstRef.value : null,
    product_description: parsedPrompt.image_prompt
      || parsedPrompt.product_description
      || parsedPrompt.parsed_prompt
      || null,
  };

  adapter.setProcessing(true);
  let sequences = [];
  let aiTypes = [];
  let recommendedSource = 'ai';
  let recommendedSequenceId = null;
  let lastPrefs = { last_sequence_id: null, last_sequence_source: null };
  try {
    const [seqRes, recRes, prefRes] = await Promise.all([
      axios.get('/sequences'),
      axios.post('/sequences/recommend', recommendBody),
      axios.get('/users/me/chat-preferences'),
    ]);
    sequences = seqRes.data || [];
    aiTypes = (recRes.data && recRes.data.types) || [];
    recommendedSource = (recRes.data && recRes.data.recommended_source) || 'ai';
    recommendedSequenceId = (recRes.data && recRes.data.recommended_sequence_id) || null;
    lastPrefs = prefRes.data || lastPrefs;
  } catch (err) {
    console.error('Failed to load sequence picker data', err);
    aiTypes = DEFAULT_SET_TYPES.map((k) => ({
      key: k,
      label: (BUILT_IN_TYPES[k] && BUILT_IN_TYPES[k].label) || TYPE_LABELS[k] || k,
    }));
  } finally {
    adapter.setProcessing(false);
  }

  // AI Recommended shows ALL built-in types as selectable slots so the user
  // can opt into any image type — including new ones like mobile_hero
  // (generic) or mobile_hero_reference that the backend recommender does
  // not yet propose. The AI's actual recommendation is captured in
  // defaultSelectedKeys so only the recommended ones start checked, and
  // toggling between sources restores the source's defaults.
  const recommendedKeys = aiTypes.map((t) => t.key);
  const aiRecommendedSlots = Object.values(BUILT_IN_TYPES).map((t) => ({
    key: t.key,
    label: t.label,
  }));
  const sources = [{
    id: 'ai',
    source: 'ai',
    sequenceId: null,
    label: 'AI Recommended',
    count: recommendedKeys.length || aiRecommendedSlots.length,
    slots: aiRecommendedSlots,
    defaultSelectedKeys: recommendedKeys.length ? recommendedKeys : aiRecommendedSlots.map((s) => s.key),
  }];
  sequences.forEach((seq) => {
    const slots = sequenceSlotsToPresetSlots(seq);
    if (!slots.length) return;
    sources.push({
      id: `library:${seq.id}`,
      source: 'library',
      sequenceId: seq.id,
      label: seq.name,
      count: slots.length,
      slots,
      defaultSelectedKeys: slots.map((s) => s.key),
    });
  });

  // Image types picked one by one from the Library sit at the top of the
  // dropdown — the user named them, so they should not have to hunt for
  // them. (POR-14039)
  const attachedTypesSource = attachedImageTypesPresetSource(adapter);
  if (attachedTypesSource) {
    sources.unshift(attachedTypesSource);
  }

  // If the user explicitly attached a sequence as a reference (chip in the
  // input dock before sending the message), that is the strongest signal of
  // intent — beat AI recommendation and last-pick history. Reference key
  // format is `sequences|<id>|<label>` (see NetworksDock.buildReferenceKey).
  const selectedRefs = (typeof adapter.getSelectedReferences === 'function')
    ? (adapter.getSelectedReferences() || [])
    : [];
  const attachedSequenceIds = selectedRefs
    .map((k) => {
      const [kind, id] = String(k || '').split('|');
      return kind === 'sequences' && id ? id : null;
    })
    .filter(Boolean);

  // A hand-picked set of image types is more specific than a saved sequence,
  // so it wins when both kinds of chip are attached. (POR-14039)
  let initialSourceId = attachedTypesSource ? attachedTypesSource.id : null;
  if (!initialSourceId) {
    for (const id of attachedSequenceIds) {
      const matchId = `library:${id}`;
      if (sources.find((s) => s.id === matchId)) {
        initialSourceId = matchId;
        break;
      }
    }
  }
  if (!initialSourceId && recommendedSource === 'library' && recommendedSequenceId) {
    const matchId = `library:${recommendedSequenceId}`;
    if (sources.find((s) => s.id === matchId)) {
      initialSourceId = matchId;
    }
  }
  if (!initialSourceId && lastPrefs.last_sequence_source === 'library' && lastPrefs.last_sequence_id) {
    const libraryId = `library:${lastPrefs.last_sequence_id}`;
    if (sources.find((s) => s.id === libraryId)) {
      initialSourceId = libraryId;
    }
  }
  if (!initialSourceId) {
    initialSourceId = 'ai';
  }
  const initialSource = sources.find((s) => s.id === initialSourceId) || sources[0];
  const initialSelectedKeys = initialSource
    ? (initialSource.defaultSelectedKeys || initialSource.slots.map((s) => s.key))
    : [];

  return await adapter.addAndSaveMessage({
    role: 'assistant',
    content: 'Select the image types you want to create:',
    _pathType: pathType,
    widgets: [{
      type: 'sequence_picker',
      id: 'pdp_sequence_picker',
      sources,
      initialSourceId,
      initialSelectedKeys,
    }],
  });
}

/**
 * Path A: Fully AI-generated PDP images.
 * Generates one design concept per image type using the design endpoint.
 */
export async function handlePdpAiGenerate(adapter) {
  // Show suggestion/confirmation for sets (0 or multiple types)
  if (await showImageSetSuggestion(adapter, 'ai_generate')) {
    return;
  }

  // Single type — go to size picker before generating
  return await showAiGenerateSizePicker(adapter);
}

// The output sizes offered for PDP images. Shared so the chat size question and
// the set picker's inline size row can never drift apart. (POR-14023)
export const PDP_IMAGE_SIZES = ['1500x1500', '2000x2000', '2500x2500', '3000x3000'];

// Pre-selected in the set picker so Create is ready without picking a size;
// a brief's own size still takes precedence. (POR-14659, 2000 since POR-14794)
export const DEFAULT_PDP_IMAGE_SIZE = '2000x2000';

/**
 * A "1500x1500" output size, or '' when the input isn't one. Briefs can state
 * ANY W x H (see BriefStagingService::detectImageSize), so a size outside
 * PDP_IMAGE_SIZES is still valid — only the shape is enforced. (POR-14023)
 */
export function normalizePdpSize(value) {
  const m = /^\s*(\d{3,5})\s*[x×]\s*(\d{3,5})\s*$/i.exec(String(value || ''));
  return m ? `${m[1]}x${m[2]}` : '';
}

const sizeButtons = (actionPrefix) => PDP_IMAGE_SIZES.map((size) => ({
  label: size.replace('x', ' x '),
  action: `${actionPrefix}:${size}`,
  variant: 'primary',
}));

/**
 * Show the size picker for the AI Generate path.
 */
export async function showAiGenerateSizePicker(adapter) {
  const isSet = isPdpSetIntent(adapter.getParsedPrompt());
  return await adapter.addAndSaveMessage({
    role: 'assistant',
    content: isSet
      ? 'What size should the PDP images be?'
      : 'What size should the PDP image be?',
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_ai_size_select',
      buttons: sizeButtons('pdp_ai_size'),
    }],
  });
}

/**
 * Kick off the AI Generate sequential queue (Path A). Reuses the same queue
 * mechanism as the Layered Image path so Keep & next / Change work uniformly.
 */
export async function runPdpAiGenerate(adapter) {
  await startPdpQueue(adapter, 'ai_generate');
}

/**
 * Path B: Layered Image – AI-generated then auto-converted to editable layers.
 * Same generation as AI Generate, but creates a layout with editable layers
 * (background, text fields, product image) via automated Make Editable process.
 */
export async function handlePdpLayeredImage(adapter) {
  // Show suggestion/confirmation for sets (0 or multiple types)
  if (await showImageSetSuggestion(adapter, 'layered_image')) {
    return;
  }

  // Single type — go straight to size selection
  return await showLayeredImageSizePicker(adapter);
}

/**
 * Show the size picker for the Layered Image path.
 * Called directly for single type, or after set confirmation for multiple types.
 */
export async function showLayeredImageSizePicker(adapter) {
  const isSet = isPdpSetIntent(adapter.getParsedPrompt());
  return await adapter.addAndSaveMessage({
    role: 'assistant',
    content: isSet
      ? 'What size should the PDP images be?'
      : 'What size should the PDP image be?',
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_layered_size_select',
      buttons: sizeButtons('pdp_layered_size'),
    }],
  });
}

/**
 * Path C: From Template (inline template selection).
 * Reuses the original PDP template gallery flow.
 */
export async function handlePdpFromTemplate(adapter) {
  adapter.setProcessing(true);
  try {
    const { data: allTemplates } = await axios.get('/networks/templates');

    const pdpTemplates = (allTemplates || []).filter((t) =>
      getTypeKeys(t.tags).includes('pdp')
    );

    if (!pdpTemplates.length) {
      adapter.addAndSaveMessage({
        role: 'assistant',
        content:
          'No PDP templates available. Tag templates with <b>&nbsp;creative_studio&nbsp;</b> and <b>&nbsp;type_pdp&nbsp;</b> to show them here.',
      });
      return;
    }

    adapter.setShowcaseTemplates?.(allTemplates);

    const templateData = pdpTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      thumbnail: t.thumbnail,
      width: t.width,
      height: t.height,
      tags: t.tags,
    }));

    const templateCount = templateData.length;
    const recommendIntro = templateCount === 1
      ? 'I recommend this <b>PDP image</b> for a strong product listing:'
      : `I recommend these <b>${templateCount} PDP images</b> for a strong product listing. Toggle any to customize your set:`;
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: recommendIntro,
      widgets: [
        {
          type: 'template_gallery',
          id: 'pdp_image_set',
          templates: templateData,
          preselectedIds: templateData.map((t) => t.id),
        },
      ],
    });

  } catch (e) {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: 'Failed to load PDP templates. Please try again.',
    });
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Rewrite a URL so the browser can actually load it. The backend may return
 * URLs that point to an internal Docker host (e.g. http://radzs.front/share?file=...)
 * which the browser can't resolve. When the URL has a /share?file= path we
 * rebase it onto the current window origin.
 */
export function normalizeBrowserUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (!origin) return url;

  if (url.startsWith('/')) return origin + url;

  try {
    const parsed = new URL(url);
    if (parsed.origin !== origin && /\/share$/.test(parsed.pathname) && parsed.searchParams.get('file')) {
      return origin + parsed.pathname + parsed.search;
    }
  } catch (e) {
    // not a full URL — leave as-is
  }
  return url;
}

/**
 * Resolve a product image URL for preview/overlay use.
 * Tries, in order:
 *   1. Direct URL-type refs in productRefs
 *   2. Previously-shown product cards (_foundProducts) on earlier chat messages
 *   3. Silent call to /networks/search-product-images for ASIN/GTIN/name refs
 * Returns null if nothing is available.
 */
export async function resolveProductOverlayUrl(productRefs, messages = []) {
  const urlRef = (productRefs || []).find((r) => r.type === 'url' && r.value);
  if (urlRef) return normalizeBrowserUrl(urlRef.value);

  const reversed = [...messages].reverse();
  for (const msg of reversed) {
    const found = msg?._foundProducts || [];
    const hit = found.find((p) => p.url);
    if (hit) return normalizeBrowserUrl(hit.url);
  }

  const searchable = (productRefs || []).filter(
    (r) => (r.type === 'name' || r.type === 'gtin' || r.type === 'asin' || r.type === 'salsify') && r.value
  );
  if (!searchable.length) return null;

  try {
    const { data } = await axios.post('/networks/search-product-images', {
      product_references: searchable,
    });
    const results = data?.results || [];
    const found = results.find((r) => r.found && r.images && r.images[0]);
    return found?.images?.[0] ? normalizeBrowserUrl(found.images[0]) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Smart default for per-image product-mode.
 *
 * Applies in ALL paths (AI Generate and AI Layered Image). When an image is
 * Layered, the layout-creation step routes that specific image through the
 * layered endpoint so the product is added as a separate editable layer —
 * regardless of the path the user originally picked.
 *
 *   - Hero / Mobile Hero variants → Layered (plain backdrop + product overlay)
 *   - Types marked `noOverlay: true` in BUILT_IN_TYPES → Integrated
 *     (Feature Benefits, Reason To Believe, Ingredients, How To — these are
 *      self-contained AI compositions where a product overlay covers up the
 *      very content the user asked for)
 *   - Rest → Layered
 *
 * Users can override per-image via the layers-icon popover.
 */
export function getDefaultProductMode(imageType, pathType) {
  // "AI Static Image" path: every image starts as Integrated to match what
  // the path name implies. The user can still flip individual images to
  // Layered with the per-image mode toggle.
  if (pathType === 'ai_generate') return 'integrated';
  const typeDef = BUILT_IN_TYPES[imageType] || BUILT_IN_TYPES[normalizeTypeKey(imageType)];
  if (typeDef && typeDef.noOverlay) return 'integrated';
  // Lifestyle / scene composites already include the product baked into the
  // image, so they default to Integrated/static — NOT a separate product layer
  // on top of the background. (POR-13271 feedback #6a)
  if (normalizeTypeKey(imageType) === 'lifestyle') return 'integrated';
  // Cambridge / GS1 / Hero / etc default to Layered so the product becomes
  // an editable separate layer.
  return 'separate';
}

/**
 * Widget that lets the user switch an individual image between Layered
 * (separate product overlay) and Integrated (product embedded). Marks
 * the current mode as selected so the user knows the active state.
 */
export function buildPdpImageModeWidget(currentMode) {
  return {
    type: 'action_buttons',
    id: 'pdp_image_mode',
    label: 'Product image',
    selectedAction: `pdp_image_mode:${currentMode}`,
    buttons: [
      { label: 'Layered', action: 'pdp_image_mode:separate', variant: 'primary' },
      { label: 'Static', action: 'pdp_image_mode:integrated', variant: 'primary' },
    ],
  };
}

/**
 * Widget shown below a generated PDP image.
 *
 * Buttons:
 *   - "Keep & next" (or "Keep" when it's the last/only image): advances the
 *     queue and triggers generation of the next image.
 *   - ↻ "Refine with feedback" icon: focuses the main chat input so the user
 *     can type what they want different and hit send.
 *   - ⏭ "Skip" icon: advance the queue without keeping this image.
 *   - "Finish" (only shown from image 2 onward, not the last): stops the queue
 *     early and finalizes with the images kept so far.
 */
export function buildPdpImageDecisionWidget({ isLast = false, showFinish = false, totalCount = 1 } = {}) {
  const isSingle = totalCount === 1;
  const primaryLabel = isSingle || isLast ? 'Keep' : 'Keep & next';
  // Skip skips the current image and moves to the next. Hide it when there is
  // no next image (single-image case or the last image of a set).
  const showSkip = !isSingle && !isLast;

  const buttons = [
    { label: primaryLabel, action: 'pdp_image_keep_next', variant: 'primary' },
    {
      iconOnly: true,
      iconClass: 'bi bi-arrow-counterclockwise',
      tooltip: 'Refine with feedback',
      action: 'pdp_image_change',
    },
  ];

  if (showSkip) {
    buttons.push({
      iconOnly: true,
      iconClass: 'bi bi-skip-end',
      tooltip: 'Skip',
      action: 'pdp_image_skip',
    });
  }

  if (showFinish) {
    buttons.push({
      label: 'Finish',
      tooltip: 'Create the layout with images so far',
      action: 'pdp_image_finish',
      variant: 'plain',
    });
  }

  return {
    type: 'action_buttons',
    id: 'pdp_image_decision',
    buttons,
  };
}

/**
 * Regenerate a single PDP image in place using its stored _pdpImageContext.
 * Optionally overrides the product mode (when the user switches Layered/Integrated)
 * and/or appends extra user guidance to the prompt.
 * Returns true on success, false on failure.
 */
export async function regeneratePdpImage(adapter, messageIndex, { extraUserPrompt = null, overrideMode = null } = {}) {
  const message = adapter.getMessages()[messageIndex];
  if (!message?._pdpImageContext) return false;

  const ctx = message._pdpImageContext;

  // Keep the regenerated image in the original product's marketplace language
  // (this path skips buildPdpQueueContext where targetLanguage is derived). (POR-13612-5)
  const targetLanguage = ctx.targetLanguage || marketplaceLanguage(
    ((adapter.getParsedPrompt() || {}).product_references || []).find((r) => r.marketplace)?.marketplace,
  ) || null;

  if (ctx.rowMode === 'scene') {
    return regenerateSceneImage(adapter, messageIndex, { extraUserPrompt });
  }

  // A tile generated without a product regenerates without one. (POR-14794)
  const noProduct = !!ctx.noProduct || !queueHasProduct(ctx);

  if (ctx.referenceImageUrl) {
    try {
      // The core's reference branch, as the row was first built (white-bg hero,
      // standalone layer, product into the reference, or style-only with no
      // product), keeping the row's direction and overlay text. (POR-14794)
      const newUrl = await regenerateImageUrlThroughCore(adapter, ctx, { extraUserPrompt, productMode: 'integrated', targetLanguage, noProduct });
      const attachments = (message.attachments || []).map((att, i) => {
        if (i !== 0) return att;
        const updated = { ...att, payload: newUrl };
        delete updated.overlayUrl;
        return updated;
      });
      adapter.updateAndSaveMessage(messageIndex, {
        attachments,
        widgets: [buildPdpImageDecisionWidget({ isLast: !!ctx.isLast, showFinish: !!ctx.showFinish, totalCount: ctx.totalCount || 1 })],
        _pdpImageContext: { ...ctx, productMode: 'integrated' },
        _pdpImageKept: false,
        disabled: false,
      });
      return true;
    } catch (e) {
      adapter.updateAndSaveMessage(messageIndex, {
        widgets: [buildPdpImageDecisionWidget({ isLast: !!ctx.isLast, showFinish: !!ctx.showFinish, totalCount: ctx.totalCount || 1 })],
      });
      return false;
    }
  }

  const productMode = noProduct ? 'integrated' : (overrideMode || ctx.productMode);
  const prompt = buildPdpImagePrompt(ctx.type, ctx.productContext, extraUserPrompt, productMode, null, null, null, null, 'editable', false, targetLanguage);

  // GS1: the visible packshot with the bottom band comes from the simplify
  // service, NOT the design call below. Re-call simplify so Gemini produces
  // a fresh attempt (the service no longer caches results). When simplify
  // returns null (band-validation failed for all retries), fall back to the
  // RAW source URL — better than showing the previous bad simplified result.
  //
  // Refinement text from "Refine with feedback" is forwarded as
  // `user_instructions` regardless of the original approach (auto / reference
  // / describe). The mode itself is NEVER changed — the user's selection at
  // generation time stays in effect, so GS1 styling rules baked into auto /
  // reference modes survive a refine. The backend prompts inject
  // user_instructions as override directives on top of the mode's base rules.
  let regenOverlayUrl = ctx.overlayUrl;
  if (ctx.type === 'mobile_hero_gs1' && ctx.rawOverlayUrl && (ctx.gs1Approach?.mode || extraUserPrompt)) {
    try {
      const approach = ctx.gs1Approach?.mode || 'auto';
      const userInstructions = extraUserPrompt || ctx.gs1Approach?.userInstructions || null;
      const payload = {
        image_url: ctx.rawOverlayUrl,
        approach,
      };
      if (ctx.gs1Approach?.referenceUrl) {
        payload.reference_url = ctx.gs1Approach.referenceUrl;
      }
      if (userInstructions) payload.user_instructions = userInstructions;
      const { data } = await axios.post('/networks/simplify-product-image', payload);
      if (data?.simplified_url) {
        regenOverlayUrl = data.simplified_url;
      } else {
        regenOverlayUrl = data?.fallback || ctx.rawOverlayUrl;
      }
    } catch (e) {
      console.warn('Could not re-simplify product image on regenerate; falling back to raw source', e);
      regenOverlayUrl = ctx.rawOverlayUrl;
    }
  }

  // Hero / Cambridge / GS1 + Layered: keep the backdrop truly empty by not
  // passing product refs to Gemini (otherwise Gemini "art-directs" a themed
  // backdrop around where it expects the product to live, e.g. swirls,
  // patterns, watermarks). GS1 in particular MUST be a pure white canvas —
  // the simplified packshot is overlaid as a separate layer on top.
  const suppressProductRefs = (
    (ctx.type === 'hero' || ctx.type === 'mobile_hero_cambridge' || ctx.type === 'mobile_hero_gs1')
    && productMode === 'separate'
  ) || isStandaloneLayerType(ctx.type);
  let productNamesPayload = suppressProductRefs ? [] : (ctx.productImageNames || []);
  let referenceUrlsPayload = suppressProductRefs ? [] : (ctx.referenceImageUrls || []);

  // GS1 integrated: pass the simplified packshot as a reference image so
  // Gemini sees the actual product instead of hallucinating one. See the
  // matching block in generateNextInPdpQueue for details.
  if (ctx.type === 'mobile_hero_gs1' && productMode === 'integrated' && regenOverlayUrl) {
    productNamesPayload = [];
    referenceUrlsPayload = [regenOverlayUrl];
  }

  // GS1 + integrated: skip the design call. See generateNextInPdpQueue
  // for the rationale — the simplified packshot IS the desired final image.
  const skipDesignCall = ctx.type === 'mobile_hero_gs1'
    && productMode === 'integrated'
    && !!regenOverlayUrl;

  try {
    let newUrl;
    if (skipDesignCall) {
      newUrl = regenOverlayUrl;
    } else if (ctx.type === 'mobile_hero_gs1') {
      newUrl = await postDesignWithRetry(
        {
          image_prompt: prompt,
          user_sizes: ctx.size,
          product_images_names: productNamesPayload,
          reference_images_urls: referenceUrlsPayload,
          selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
        },
        { source: adapter.getSource?.() || 'creative_studio_chat' },
      );
    } else {
      newUrl = await regenerateImageUrlThroughCore(adapter, ctx, { extraUserPrompt, productMode, targetLanguage, noProduct });
    }

    const attachments = (message.attachments || []).map((att, i) => {
      if (i !== 0) return att;
      const updated = { ...att, payload: newUrl };
      if (productMode === 'separate' && regenOverlayUrl && !isStandaloneLayerType(ctx.type)) {
        updated.overlayUrl = regenOverlayUrl;
      } else {
        delete updated.overlayUrl;
      }
      // Cambridge MRHI callouts (PRODUCT OR FLAVOR / COUNT rectangles) only
      // belong on the Layered preview — in Integrated mode the rectangles
      // are part of the AI-generated image itself, so the CSS overlay must
      // be removed when the user switches mode and regenerates.
      if (ctx.type === 'mobile_hero_cambridge' && productMode === 'separate') {
        updated._mrhiCallouts = {
          type: 'cambridge',
          flavorText: 'PRODUCT OR FLAVOR',
          countText: 'Count',
        };
      } else {
        delete updated._mrhiCallouts;
      }
      return updated;
    });

    adapter.updateAndSaveMessage(messageIndex, {
      attachments,
      widgets: [buildPdpImageDecisionWidget({ isLast: !!ctx.isLast, showFinish: !!ctx.showFinish, totalCount: ctx.totalCount || 1 })],
      _pdpImageContext: { ...ctx, productMode, overlayUrl: regenOverlayUrl, simplifiedOverlayUrl: regenOverlayUrl },
      _pdpImageKept: false,
      // Re-enable the message: a fresh regeneration always restores Keep /
      // Refine / Skip / mode-toggle, regardless of any prior disabled state
      // (e.g. a downstream "Create layout" click that disabled this card).
      disabled: false,
    });

    const allMessages = adapter.getMessages();
    for (let i = 0; i < allMessages.length; i++) {
      const msg = allMessages[i];
      if (Array.isArray(msg?._pdpGeneratedImages)) {
        // Match on rowIndex, not type: a set can now hold two rows of the SAME type
        // (duplicated slots), and a type match would rewrite both twins' urls — the
        // layout would get the refined image twice and lose the other. Older messages
        // without a rowIndex still fall back to the type match. (POR-13854)
        const updated = msg._pdpGeneratedImages.map((img) => {
          const isMatch = (img.rowIndex != null && ctx.rowIndex != null)
            ? img.rowIndex === ctx.rowIndex
            : img.type === ctx.type;

          return isMatch ? { ...img, url: newUrl } : img;
        });
        adapter.updateAndSaveMessage(i, { _pdpGeneratedImages: updated });
      }
    }

    return true;
  } catch (e) {
    adapter.updateAndSaveMessage(messageIndex, {
      widgets: [buildPdpImageDecisionWidget({ isLast: !!ctx.isLast, showFinish: !!ctx.showFinish, totalCount: ctx.totalCount || 1 })],
    });
    return false;
  }
}

/**
 * A one-at-a-time result regenerated through the same core as its first build
 * (generateSinglePdpImage), so it keeps that build's product handling: an
 * attached packshot as the preserved base, the infographic product reference,
 * or no product at all, and the row's direction and overlay text. The payload
 * built by hand here before sent no image for an attached packshot, so Gemini drew
 * a fake pack, and only the typed text as the direction. (POR-14794)
 */
async function regenerateImageUrlThroughCore(adapter, ctx, { extraUserPrompt, productMode, targetLanguage, noProduct }) {
  const queue = {
    pathType: ctx.pathType || 'mixed',
    types: [ctx.type],
    rowSelections: [{ rowIndex: 0, type: ctx.type, label: ctx.label, mode: 'ai', libraryKey: ctx.libraryKey || null, reference_image_url: ctx.referenceImageUrl || null }],
    currentIndex: 0,
    size: ctx.size,
    targetLanguage,
    overlayUrl: ctx.rawOverlayUrl || ctx.overlayUrl || null,
    simplifiedOverlayUrl: null,
    productContext: ctx.productContext,
    productGrounding: ctx.productGrounding || null,
    productImageNames: ctx.productImageNames || [],
    referenceImageUrls: ctx.referenceImageUrls || [],
    productImageUrls: ctx.productImageUrls || [],
    hasProductRefs: ctx.hasProductRefs,
    // The row's own direction, overlay text and logo, with the typed text on top.
    _extraUserPrompt: ctx.userInstructions || null,
    _refinePrompt: extraUserPrompt,
    _overlayText: ctx.overlayText || null,
    _overlayTextMode: ctx.overlayTextMode || 'editable',
    _brandLogoUrl: ctx.brandLogoUrl || null,
    _imageTypeCatalog: await loadImageTypeCatalog(),
    productModeOverride: productMode || null,
  };
  const result = await generateSinglePdpImage(adapter, withoutProductWhenNone(queue, noProduct, false), 0);
  if (!result.ok) {
    throw result.error || new Error('Regeneration failed');
  }

  return result.imageUrl;
}

/**
 * Regenerate a Scene-mode PDP image. Re-calls /networks/compose-scene with
 * the original (sceneUrl, productUrl) plus any extra refinement text the
 * user typed in via the Refine-with-feedback flow. Returns true on success.
 */
async function regenerateSceneImage(adapter, messageIndex, { extraUserPrompt }) {
  const message = adapter.getMessages()[messageIndex];
  const ctx = message?._pdpImageContext;
  if (!ctx || !ctx.sceneUrl || !ctx.productUrl) return false;

  const combinedInstructions = [ctx.userInstructions, extraUserPrompt].filter(Boolean).join(' ');

  try {
    const { data } = await axios.post('/networks/compose-scene', {
      scene_url: ctx.sceneUrl,
      product_url: ctx.productUrl,
      user_instructions: combinedInstructions,
    });
    const newUrl = data?.composite_url;
    if (!newUrl) return false;

    const attachments = (message.attachments || []).map((att, i) => (
      i === 0 ? { ...att, payload: newUrl } : att
    ));

    adapter.updateAndSaveMessage(messageIndex, {
      attachments,
      widgets: [buildPdpImageDecisionWidget({
        isLast: !!ctx.isLast,
        showFinish: !!ctx.showFinish,
        totalCount: ctx.totalCount || 1,
      })],
      _pdpImageContext: { ...ctx, userInstructions: combinedInstructions },
      _pdpImageKept: false,
      disabled: false,
    });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Resolve the shared `_pdpQueue` data-context for a PDP run, independent of how
 * the run is presented (sequential queue or parallel grid). Performs the same
 * data-resolution `startPdpQueue` previously did inline — resolving product
 * refs, size, the product overlay URL, product/reference image lists, etc. — and
 * returns the exact object literal that gets stored under `_pdpQueue`.
 *
 * NOTE: this intentionally does NOT add chat messages or toggle processing —
 * callers own their own UX framing (e.g. startPdpQueue's "Preparing..." status
 * message). It only does data resolution so it can be shared with the parallel
 * grid orchestrator (POR-13183).
 */
async function buildPdpQueueContext(adapter, pathType, { withGrounding = true, deferGrounding = false } = {}) {
  const parsedPrompt = adapter.getParsedPrompt();
  const rowSelections = pathType === 'mixed' && Array.isArray(parsedPrompt.pdp_row_selections)
    ? parsedPrompt.pdp_row_selections
    : null;
  const imageTypes = rowSelections
    ? rowSelections.map((r) => r.type)
    : ((parsedPrompt.pdp_image_types || []).length > 0
      ? parsedPrompt.pdp_image_types
      : ['product_detail']);

  // A single explicit PDP request ("create a <type> image for X: <description>")
  // carries the user's intent in parsedPrompt.image_prompt. Unlike the row-picker
  // wand (row.userInstructions), this text was never threaded into the Gemini
  // prompt, so the description (background, overlay text, benefit-icon list) was
  // lost and the new input-driven types (product_gallery, cross_sell,
  // category_gallery, feature_iconography_*) fell back to their default template.
  // Thread it as `_extraUserPrompt` for single-image, non-set, non-row requests so
  // buildPdpImagePrompt appends it as "Additional context". Excluded: the white-bg
  // hero family (hero / Cambridge / GS1 / reference), which must stay white and
  // structured by spec and ignore free-form scene/overlay text. The set/grid flow
  // keeps using the per-row wand (richer, per-image) and is unaffected. (POR-13366)
  const HERO_WHITE_BG_FAMILY = ['hero', 'mobile_hero_cambridge', 'mobile_hero_gs1', 'mobile_hero_reference'];
  const isSingleImageRequest = !rowSelections
    && !isPdpSetIntent(parsedPrompt)
    && imageTypes.length === 1;
  const singleExtraUserPrompt = (isSingleImageRequest
    && !HERO_WHITE_BG_FAMILY.includes(normalizeTypeKey(imageTypes[0])))
    ? (parsedPrompt.image_prompt || null)
    : null;

  let productRefs = (parsedPrompt.product_references || []).filter((r) => !r._deselected);

  if (!productRefs.length) {
    productRefs = getLatestUserAttachmentRefs(adapter.getMessages() || []);
  }

  // The generated PDP copy must read in the ORIGINAL product's language (derived
  // from its Amazon marketplace, e.g. amazon.com.mx → Spanish), NOT the language
  // of the reference image it's styled after. null when unknown. (POR-13612-3)
  const targetLanguage = marketplaceLanguage(productRefs.find((r) => r.marketplace)?.marketplace) || null;
  const size = pathType === 'ai_generate'
    ? (parsedPrompt.pdp_ai_size || '2048x2048')
    : (parsedPrompt.pdp_layered_size || '1500x1500');

  const overlayUrl = await resolveProductOverlayUrl(productRefs, adapter.getMessages() || []);
  // A brief with no product image still names its product. (POR-14794)
  const productContext = productRefs.map((r) => r.display_name || r.value).join(', ')
    || parsedPrompt.pdp_brief_product_name
    || '';
  const productImageNames = productRefs
    .filter((r) => r.type === 'name' || r.type === 'gtin' || r.type === 'asin' || r.type === 'salsify')
    .map((r) => r.value);
  const referenceImageUrls = [
    ...((parsedPrompt.reference_images_urls || []).filter(Boolean)),
  ];
  let productImageUrls = [
    ...productRefs
      .filter((r) => r.type === 'url' && r.value)
      .map((r) => r.value),
  ];
  const hasProductRefs = productRefs.length > 0;

  if (!productImageUrls.length && overlayUrl) {
    productImageUrls = [overlayUrl];
  }

  // GS1 simplification now depends on the user's per-item approach choice
  // (auto / reference / describe), which is collected by showGs1ApproachPicker
  // at runtime. Defer simplification entirely to the JIT-prep block in
  // generateNextInPdpQueue once gs1Approach is set.
  const simplifiedOverlayUrl = null;

  // When the set includes an infographic type that describes (but does not draw)
  // the product, read a real product brief from the packshot ONCE so those
  // prompts are grounded in the actual product instead of a raw id. (POR-13307)
  // deferGrounding (parallel grid): stash the inputs and let startPdpParallelGrid
  // resolve the brief in the BACKGROUND so it overlaps image generation instead of
  // blocking the whole set up-front; only grounded infographics await it (via
  // ensureGrounding). Sequential leaves deferGrounding off → byte-identical. (POR-13612)
  let productGrounding = null;
  let groundingInputs = null;
  if (withGrounding && overlayUrl && imageTypes.some(isGroundableInfographicType)) {
    groundingInputs = { productUrl: overlayUrl, productName: productContext };
    if (!deferGrounding) {
      productGrounding = await extractProductBrief(groundingInputs);
    }
  }

  // Pre-load the image-type catalog once so all per-type prompt calls in this
  // generation run can read configurable creative directions without extra
  // network round-trips. On error, loadImageTypeCatalog() silently returns []
  // and getTypeDirection() returns null → behavior is byte-identical to before.
  // (POR-13484)
  const _imageTypeCatalog = await loadImageTypeCatalog();

  return {
    pathType,
    types: imageTypes,
    rowSelections,
    currentIndex: 0,
    size,
    overlayUrl,
    simplifiedOverlayUrl,
    productContext,
    productImageNames,
    referenceImageUrls,
    productImageUrls,
    hasProductRefs,
    productGrounding,
    targetLanguage,
    // The user-chosen second product for a cross-sell (Product 2), asked for when a
    // cross-sell is in the set. Used as the cross-sell tile's extra product. (POR-13612-5-2)
    _crossSellProduct: (parsedPrompt.pdp_cross_sell_product && parsedPrompt.pdp_cross_sell_product.image) || null,
    _groundingInputs: groundingInputs,
    _extraUserPrompt: singleExtraUserPrompt,
    _imageTypeCatalog,
  };
}

/**
 * Resolve the deferred product brief for a queue, at most once, caching the result
 * on the queue. In the parallel grid, startPdpParallelGrid kicks the brief off
 * up-front so it resolves WHILE the non-grounded tiles generate; grounded
 * infographics await it here instead of the whole set blocking up-front (POR-13612).
 * Concurrent tiles — and the shallow queue copies generatePdpGridTile makes — share
 * the one in-flight `_groundingPromise` (copies inherit it by reference), so the
 * brief runs a single time. In the sequential path productGrounding is already
 * resolved, so this returns immediately. Never rejects (falls back to null).
 */
function ensureGrounding(queue) {
  if (!queue) return Promise.resolve(null);
  if (queue.productGrounding) return Promise.resolve(queue.productGrounding);
  if (queue._groundingPromise) return queue._groundingPromise;
  if (!queue._groundingInputs) return Promise.resolve(null);
  queue._groundingPromise = extractProductBrief(queue._groundingInputs)
    .then((g) => {
      queue.productGrounding = g || null;
      return queue.productGrounding;
    })
    .catch(() => null);
  return queue._groundingPromise;
}

/**
 * Start a sequential PDP image queue for either path:
 *   - 'ai_generate'  → flat AI images, final button is "Create layout"
 *   - 'layered_image' → editable layered layout, final button is
 *     "Create editable layout"
 *
 * The queue state lives on parsedPrompt under `_pdpQueue`. Generates the first
 * image and waits for the user to click "Keep & next" before continuing.
 */
export async function startPdpQueue(adapter, pathType) {
  const parsedPrompt = adapter.getParsedPrompt();

  // The "Preparing..." status message must appear before the (potentially slow)
  // product-image resolution inside buildPdpQueueContext, so derive its
  // singular/plural copy from the same imageTypes logic up front. This mirrors
  // the resolution buildPdpQueueContext performs and stays side-effect free.
  const rowSelections = pathType === 'mixed' && Array.isArray(parsedPrompt.pdp_row_selections)
    ? parsedPrompt.pdp_row_selections
    : null;
  const imageTypes = rowSelections
    ? rowSelections.map((r) => r.type)
    : ((parsedPrompt.pdp_image_types || []).length > 0
      ? parsedPrompt.pdp_image_types
      : ['product_detail']);

  // Show the spinner + a status message immediately so the user has feedback
  // while we resolve the product image URL (can take 30s+ for ASINs that need
  // to hit the Amazon scraper).
  adapter.setProcessing(true);
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: imageTypes.length > 1
      ? 'Preparing your PDP images...'
      : 'Preparing your PDP image...',
  });

  const ctx = await buildPdpQueueContext(adapter, pathType);

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: ctx,
  });
  adapter.saveConversationState();

  await generateNextInPdpQueue(adapter);
}

// Backwards-compat alias used by the Layered Image caller.
export async function startPdpLayeredQueue(adapter) {
  return await startPdpQueue(adapter, 'layered_image');
}

export const PDP_GRID_CONCURRENCY = 3;

/**
 * Per-tile Product image (POR-13766): replace the set-level product with the
 * user-chosen packshot as the BASE the AI preserves for THIS tile. Route it as an
 * img2img base by clearing the name-lookup path (productImageNames) so the core's
 * `productRoutedAsBase` branch fires (reference_images_urls → backend
 * original_image_url + preserve_base_product) and Gemini keeps the real product
 * while building the scene/background around it. Drops the stale derived overlay.
 */
function withTileProductImage(queue, productImageUrls) {
  // Accepts one URL or a list — types like cross-sell composite several products,
  // and every picked packshot has to reach the payload. Attached product images
  // carry NO lookup name: they route as base images via reference_images_urls, so
  // productImageNames is always cleared. A display label left here would be treated
  // as a GTIN/ASIN/product-name lookup, fail, and leave Gemini with no image. (POR-13766-4)
  const urls = (Array.isArray(productImageUrls) ? productImageUrls : [productImageUrls]).filter(Boolean);
  if (!urls.length) return queue;
  return {
    ...queue,
    overlayUrl: urls[0],
    productImageUrls: urls,
    productImageNames: [],
    simplifiedOverlayUrl: null,
  };
}

/** A packshot, product image(s), or a name/ASIN the backend can look the product up by. */
function queueHasProduct(queue) {
  return !!(queue.overlayUrl
    || (Array.isArray(queue.productImageUrls) && queue.productImageUrls.length)
    || (Array.isArray(queue.productImageNames) && queue.productImageNames.length));
}

/**
 * The queue for a tile that shows NO product (POR-14794): the brief said so for this
 * tile ("No product image should be shown"), or there is no product anywhere — no
 * packshot, no product image, nothing to look up. Every product prompt assumes a
 * product image, so with none Gemini invented one: a fake pack, or a real brand that
 * fits the scene. A product image picked for the tile itself still wins over the
 * brief's "no product". Unchanged when the tile does show a product.
 */
function withoutProductWhenNone(queue, noProductRequested, tileHasProduct) {
  if (tileHasProduct || (!noProductRequested && queueHasProduct(queue))) return queue;
  return {
    ...queue,
    overlayUrl: null,
    simplifiedOverlayUrl: null,
    productImageUrls: [],
    productImageNames: [],
    hasProductRefs: false,
    _noProduct: true,
  };
}

/**
 * The text typed for a regenerate, on top of the row's own direction — and ahead
 * of it where the two disagree. (POR-14794)
 */
function withRefinement(direction, refinement) {
  if (!refinement) return direction || null;
  if (!direction) return refinement;

  return `${direction} Change requested for this version, which takes priority over the above: ${refinement}`;
}

/**
 * The queue one row of a one-at-a-time run builds from: its direction, overlay
 * text, brand logo and product image(s), as the grid's generatePdpGridTile does.
 */
function sequentialRowQueue(queue, seqRow) {
  // Same multi-packshot handling as the grid: keep every picked product. (POR-13766-4)
  const seqProductUrls = (seqRow && Array.isArray(seqRow.productImageUrls) && seqRow.productImageUrls.length)
    ? seqRow.productImageUrls
    : ((seqRow && seqRow.productImageUrl) ? [seqRow.productImageUrl] : []);
  const seqQueue = (seqRow && (seqRow.userInstructions || seqRow.overlayText || seqRow.brandLogoUrl || seqProductUrls.length))
    ? {
        ...withTileProductImage(queue, seqProductUrls),
        _extraUserPrompt: seqRow.userInstructions
          ? [queue._extraUserPrompt, seqRow.userInstructions].filter(Boolean).join(' ')
          : queue._extraUserPrompt,
        _overlayText: seqRow.overlayText || queue._overlayText || null,
        _overlayTextMode: seqRow.overlayTextMode || queue._overlayTextMode || 'editable',
        _brandLogoUrl: seqRow.brandLogoUrl || queue._brandLogoUrl || null,
      }
    : queue;

  // Same "no product" rule as the grid's generatePdpGridTile. (POR-14794)
  return withoutProductWhenNone(seqQueue, !!(seqRow && seqRow.noProduct), seqProductUrls.length > 0);
}

/**
 * Generate one grid tile by type (POR-13183). AI tiles use the shared queue +
 * generateSinglePdpImage unchanged. GS1 tiles run a per-tile simplify (from the
 * tile's gs1Approach, default 'auto') and pass a per-tile queue clone so the
 * core sees the simplified overlay. Scene tiles compose a pre-resolved
 * sceneUrl with the product. Returns { ok:false } when prerequisites are absent.
 */
async function generatePdpGridTile(adapter, queue, tile) {
  // A per-tile Product image (POR-13766) overrides the set-level product as the
  // base this tile builds from — apply it before any branch so scene / GS1 / AI
  // tiles all preserve the chosen packshot.
  const tileProductUrls = (Array.isArray(tile.productImageUrls) && tile.productImageUrls.length)
    ? tile.productImageUrls
    : (tile.productImageUrl ? [tile.productImageUrl] : []);
  if (tileProductUrls.length) {
    queue = withTileProductImage(queue, tileProductUrls);
  }
  // Per-image direction (the wand text, POR-13271), overlay text and brand logo,
  // forwarded into the design prompt via the `_`-prefixed queue fields that
  // buildPdpImagePrompt / generateReferenceRowUrl read. Applied BEFORE the branches
  // so a GS1 tile gets them too — it returns early, so previously anything typed
  // for one was silently dropped. Scene passes userInstructions to
  // composeSceneTileUrl separately, so it is unaffected. (POR-13271 / POR-13766-6)
  if (tile.userInstructions || tile.overlayText || tile.brandLogoUrl) {
    queue = {
      ...queue,
      _extraUserPrompt: tile.userInstructions
        ? [queue._extraUserPrompt, tile.userInstructions].filter(Boolean).join(' ')
        : queue._extraUserPrompt,
      _overlayText: tile.overlayText || queue._overlayText || null,
      _overlayTextMode: tile.overlayTextMode || queue._overlayTextMode || 'editable',
      _brandLogoUrl: tile.brandLogoUrl || queue._brandLogoUrl || null,
    };
  }
  // Scene: compose product into the pre-resolved background.
  if (tile.mode === 'scene') {
    const productUrl = queue.overlayUrl || (queue.productImageUrls && queue.productImageUrls[0]) || null;
    const sceneUrl = tile.sceneUrl || (tile.context && tile.context.sceneUrl) || null;
    const userInstructions = tile.userInstructions || '';
    const url = await composeSceneTileUrl({ sceneUrl, productUrl, userInstructions });
    if (!url) return { ok: false, imageUrl: null, attachment: null, context: null };
    return {
      ok: true,
      imageUrl: url,
      attachment: { type: 'url', payload: url, name: tile.label },
      context: {
        type: tile.type,
        label: tile.label,
        pathType: 'mixed',
        rowMode: 'scene',
        sceneUrl,
        productUrl,
        userInstructions,
        size: queue.size,
        rowIndex: tile.rowIndex,
      },
    };
  }

  queue = withoutProductWhenNone(queue, !!tile.noProduct, tileProductUrls.length > 0);

  // GS1: per-tile simplify (approach from the tile, default auto), then the core.
  if (tile.type === 'mobile_hero_gs1') {
    let tileQueue = queue;
    if (queue.overlayUrl) {
      const gs1Approach = tile.gs1Approach || { mode: 'auto' };
      const simplifiedOverlayUrl = await simplifyGs1Overlay(queue.overlayUrl, gs1Approach);
      tileQueue = { ...queue, simplifiedOverlayUrl: simplifiedOverlayUrl || queue.simplifiedOverlayUrl || null, gs1Approach };
    }
    return generateSinglePdpImage(adapter, tileQueue, tile.rowIndex);
  }

  // Default AI tile: the queue already carries the tile's direction / overlay text
  // / brand logo (applied above).
  return generateSinglePdpImage(adapter, queue, tile.rowIndex);
}

/**
 * Parallel PDP image generation (POR-13183). Builds a grid of tiles from the
 * row selections and generates the AI rows concurrently (limit PDP_GRID_CONCURRENCY)
 * via generateSinglePdpImage + runPool, writing tile state onto a single chat
 * message under `_pdpParallelGrid`. Gallery rows are seeded as ready tiles.
 * Stop halts launching new tiles; in-flight tiles finish.
 */
export async function startPdpParallelGrid(adapter) {
  const isStaleConversation = makeConversationStaleCheck(adapter);
  adapter.setProcessing(true);
  // Show an immediate status message so the user has feedback while
  // buildPdpQueueContext resolves the product image URL (Amazon scraper, 30s+ for
  // a first-seen ASIN) and reads the product brief (GPT-4o vision). Without this
  // the parallel path sat on a bare spinner with nothing on screen until the grid
  // widget appeared — the reported "long time before I see the images-generation
  // widget". Mirrors the sequential path's preparing message. (POR-13612)
  await adapter.addAndSaveMessage({
    role: 'assistant',
    content: 'Preparing your PDP images...',
  });
  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed', { deferGrounding: true });

    // The user left this chat while the (30s+) context resolved — abandon the run
    // before writing the grid message / product state into the now-active chat. (POR-13504)
    if (isStaleConversation()) return;

    // Kick the product brief off NOW (non-blocking) so it resolves WHILE the
    // non-grounded tiles (hero, lifestyle, gallery…) generate; grounded
    // infographics await it via ensureGrounding, and every tile + shallow queue
    // copy shares this one in-flight promise so the brief runs a single time.
    // This removes the ~5-15s GPT-4o brief from the up-front critical path that
    // delayed the whole set. (POR-13612)
    if (queue._groundingInputs && !queue.productGrounding) {
      ensureGrounding(queue);
    }

    // Expose the resolved product packshot URL for the layout-creation step.
    // `handleCreateMixedPdpLayout` fills gallery-template product slots from
    // `_pdpQueue.overlayUrl`, which only the sequential flow sets — the parallel
    // grid doesn't use `_pdpQueue`. The URL is a `/share?file=files/...` packshot,
    // which the backend's `resolveProductFileName` handles by basename (no HTTP
    // fetch), so passing it is safe and won't abort the layout. (POR-13183)
    if (queue.overlayUrl) {
      adapter.setParsedPrompt({ ...adapter.getParsedPrompt(), _pdpResolvedOverlayUrl: queue.overlayUrl });
      adapter.saveConversationState();
    }

    const rows = queue.rowSelections
      || (queue.types || []).map((t, i) => ({ rowIndex: i, type: t, label: (t || '').replace(/_/g, ' '), mode: 'ai' }));

    // A gallery row whose product gets composited into the template (async, a
    // few seconds via Gemini) is seeded in a loading state ('generating') so the
    // user sees a spinner instead of the template author's PLACEHOLDER packshot
    // until the real composite lands. noOverlay info-panel types and rows with no
    // resolved product keep the bare template thumbnail and are 'done'. (POR-13183)
    const galleryProductUrl = queue.overlayUrl || null;
    const willCompositeGallery = (row) => {
      if (row.mode !== 'gallery' || !row.template_id || !galleryProductUrl) return false;
      const typeDef = BUILT_IN_TYPES[row.type];
      return !(typeDef && typeDef.noOverlay);
    };

    // Use-as-is rows (Hero "Original", user-supplied "Yours") carry a fixed
    // image URL and skip Gemini entirely — they seed straight to a done tile,
    // mirroring how gallery rows are seeded. (POR-13271)
    const useAsIsUrl = (row) => {
      if (row.mode === 'original') return row.original_image_url || galleryProductUrl || null;
      if (row.mode === 'yours') return row.own_image_url || null;
      return null;
    };
    const isUseAsIsMode = (row) => row.mode === 'original' || row.mode === 'yours';

    const tiles = rows.map((row) => ({
      rowIndex: row.rowIndex,
      type: row.type,
      label: row.label,
      mode: row.mode || 'ai',
      state: row.mode === 'gallery'
        ? (willCompositeGallery(row) ? 'generating' : 'done')
        : (isUseAsIsMode(row) ? 'done' : 'queued'),
      url: row.mode === 'gallery'
        ? (row.template_image_url || row.template_thumb || null)
        : (isUseAsIsMode(row) ? useAsIsUrl(row) : null),
      overlayUrl: null,
      _mrhiCallouts: null,
      template_id: row.template_id || null,
      template_image_url: row.template_image_url || null,
      sceneUrl: row.sceneUrl || null,
      gs1Approach: row.gs1Approach || null,
      userInstructions: row.userInstructions || null,
      overlayText: row.overlayText || null,
      overlayTextMode: row.overlayTextMode || 'editable',
      brandLogoUrl: row.brandLogoUrl || null,
      productImageUrl: row.productImageUrl || null,
      // Types like cross-sell carry SEVERAL packshots; keep the whole list so the
      // tile composites every one, not just the first. (POR-13766-4)
      productImageUrls: Array.isArray(row.productImageUrls) ? row.productImageUrls : [],
      productImageNames: Array.isArray(row.productImageNames) ? row.productImageNames : [],
      noProduct: !!row.noProduct,
      own_image_source: row.own_image_source || null,
      context: null,
    }));

    const hasAiOrScene = rows.some((r) => r.mode === 'ai' || r.mode === 'scene');

    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: tiles.length === 1
        ? 'Generating image.'
        : `Generating <b>${tiles.length}</b> all at once.`,
      // briefId: a set started from a brief offers it in the grid header. (POR-14794)
      widgets: [{ type: 'pdp_parallel_grid', id: 'pdp_parallel_grid', ...briefWidgetFields(adapter) }],
      _pdpParallelGrid: { concurrency: PDP_GRID_CONCURRENCY, stopped: false, hasAiOrScene, tiles },
    });

    const findGridIndex = () => {
      const idx = findLatestPdpGridIndex(adapter);

      return idx === -1 ? (adapter.getMessages() || []).length - 1 : idx;
    };

    const patchTile = (rowIndex, patch) => {
      // A tile that resolves after the user left this chat must not write into
      // whatever grid now occupies the active chat. (POR-13504)
      if (isStaleConversation()) return;
      const idx = findGridIndex();
      const msg = adapter.getMessages()[idx];
      const grid = msg && msg._pdpParallelGrid;
      if (!grid) return;
      const nextTiles = grid.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...patch } : t));
      // Commit to Vuex in-memory immediately (synchronous) for instant UI, then
      // chain the network save so concurrent saves land in order (POR-13183).
      adapter.updateMessage(idx, { _pdpParallelGrid: { ...grid, tiles: nextTiles } });
      persistPdpGrid(adapter, findGridIndex);
    };

    const isStopped = () => {
      const idx = findGridIndex();
      const m = adapter.getMessages()[idx];
      return !!(m && m._pdpParallelGrid && m._pdpParallelGrid.stopped);
    };

    // Compose the user's product into each gallery template's UPC/GTIN slot — the
    // same `preview-template-with-product` call the SEQUENTIAL gallery flow makes
    // (showTemplateGalleryDecision). The composited tiles were seeded 'generating'
    // (loader) above; flip them to 'done' with the real preview when it lands, or
    // to 'done' with the bare template thumbnail on failure. noOverlay info-panel
    // types are already 'done' and skipped. (POR-13183)
    const galleryPreviewWork = Promise.all(
      tiles
        .filter((t) => t.mode === 'gallery' && t.template_id && galleryProductUrl)
        .map(async (tile) => {
          const typeDef = BUILT_IN_TYPES[tile.type];
          if (typeDef && typeDef.noOverlay) return;
          try {
            const { data } = await axios.post('/networks/preview-template-with-product', {
              template_id: tile.template_id,
              product_image_url: galleryProductUrl,
            });
            patchTile(tile.rowIndex, (data && data.preview_url)
              ? { state: 'done', url: data.preview_url }
              : { state: 'done' });
          } catch (e) {
            console.warn('PDP grid: gallery preview composite failed, keeping template thumbnail', e);
            patchTile(tile.rowIndex, { state: 'done' });
          }
        }),
    );

    const genTiles = tiles.filter((t) => t.state === 'queued');

    await runPool(
      genTiles,
      PDP_GRID_CONCURRENCY,
      async (tile) => {
        patchTile(tile.rowIndex, { state: 'generating' });
        try {
          const result = await generatePdpGridTile(adapter, queue, tile);
          if (result.ok) {
            // Same stale-chat guard patchTile applies (POR-13504).
            if (!isStaleConversation()) {
              commitFinishedTile(adapter, findGridIndex, tile.rowIndex, result);
            }
          } else {
            patchTile(tile.rowIndex, { state: 'error' });
          }
        } catch (e) {
          // A worker throw must never strand the tile or kill the pool runner.
          // Surface it as a retryable error tile and log the cause so we can see
          // which layer failed (generation prep vs design call vs compose).
          console.error('PDP parallel grid: tile generation threw', tile && tile.type, tile && tile.rowIndex, e);
          patchTile(tile.rowIndex, { state: 'error' });
        }
      },
      {
        // Stop launching new tiles once the user hits Stop OR leaves this chat
        // ("New" / opened another conversation) — the in-flight ones no-op their
        // patch and the pool drains without touching the now-active chat. (POR-13504)
        shouldStop: () => isStopped() || isStaleConversation(),
        onSkipped: (tile) => patchTile(tile.rowIndex, { state: 'stopped' }),
      },
    );

    // Let the gallery product-composite previews (fired concurrently above)
    // finish before the grid run resolves. (POR-13183)
    await galleryPreviewWork;
  } finally {
    adapter.setProcessing(false);
  }
}

// The A/B hero test always generates `lifestyle` variants: `hero` forces a
// plain white background (and ignores free-form scene/overlay direction),
// whereas `lifestyle` honors each variant's style direction — exactly what an
// A/B test needs. (POR-13385)
export const AB_VARIANT_TYPE = 'lifestyle';

// Serialize `_abHeroResult` saves the same way persistPdpGrid serializes
// `_pdpParallelGrid`: concurrent pool workers patch the SAME message, so we
// commit in-memory immediately (instant UI) but chain the network saves so the
// latest state always lands last. Mirrors persistPdpGrid's real contract
// (re-save the message's meta via updateAndSaveMessage), NOT a bare
// saveConversationState, so the `_abHeroResult` meta actually persists. (POR-13385)
function persistAbHero(adapter, gridMessageIndex) {
  pdpGridSaveChain = pdpGridSaveChain
    .then(() => {
      const idx = typeof gridMessageIndex === 'function' ? gridMessageIndex() : gridMessageIndex;
      const msg = adapter.getMessages()[idx];
      if (!msg) return null;
      return adapter.updateAndSaveMessage(idx, { _abHeroResult: msg._abHeroResult });
    })
    .catch((e) => console.warn('A/B hero: persist failed', e));
  return pdpGridSaveChain;
}

// Generate ONE A/B variant image. Unlike the type-template path
// (buildPdpImagePrompt), the variant's OWN creative direction is the PRIMARY
// driver of the image — so an open-ended brief like "bold gradient background,
// floating product" is honored instead of being diluted to "Additional context"
// on top of a fixed lifestyle scene template. A variant that supplied a
// reference IMAGE styles from that reference instead of from text. (POR-13385)

// A/B variants deliberately do NOT force a white background the way the `hero`
// type does — a variant is meant to be able to test a lifestyle/colored scene.
// But when the user's direction explicitly asks for a white/plain background —
// the retailer rule for primary/hero images — the generic edit prompt honored it
// only by luck: Gemini routinely returned a soft grey/shaded backdrop on the first
// run and only sometimes produced pure white on a regenerate (the reported bug —
// same payload both times, so it was pure model non-determinism). Detect that
// intent and enforce a pure-white BACKDROP deterministically. (POR-13586)
function abDirectionWantsWhiteBackground(text) {
  const t = String(text || '').toLowerCase();
  if (!t) return false;
  return /#f{3}(?:f{3})?\b/.test(t) // #fff / #ffffff
    || /\b(?:solid|pure|plain|clean|seamless|flat|bright|stark)\s+white\b/.test(t)
    || /\bwhite[^.]{0,20}(?:back\s?ground|backdrop|\bbg\b)/.test(t)
    || /(?:back\s?ground|backdrop|\bbg\b)[^.]{0,20}white\b/.test(t);
}

// Targeted white-BACKDROP enforcement for A/B variants (POR-13586). Unlike the
// hero `noBgRule`, this does NOT ban props — the user often wants decorative
// foreground elements (florals, sprigs) AND a solid white backdrop, so it only
// pins the backdrop to pure #FFFFFF while leaving the requested props in place.
const AB_WHITE_BACKDROP_RULE = ' BACKGROUND REQUIREMENT (non-negotiable, highest priority): the backdrop behind and around the product AND behind any props MUST be solid pure white (#FFFFFF) — flat, even, uniform white, edge to edge. NOT grey, NOT off-white, NOT beige or cream, NOT a gradient, NOT shaded, NO soft grey vignette or halo, NO gradient falloff, NO tinted or colored wash, NO texture, NO pattern. SHADOWS — cast NO shadows onto the background: NO drop shadow, NO contact shadow, NO grounding shadow, NO reflection, NO grey or darkened patch of any kind on or around the product or props. The product and any props must sit on the pure white backdrop as if cleanly cut out, so that every pixel of the background is a completely clean, shadow-free #FFFFFF. Keep only the natural surface shading and highlights ON the product and props themselves for realism — never let that shading spill onto the background. Any decorative elements the creative direction requests (e.g. florals, sprigs, roses, props) are clean foreground objects on that pure white backdrop — they must NOT tint, shade, or darken it. This must read as a retailer-compliant (Amazon main-image) pure-white-background hero.';

async function generateAbVariantImageUrl(adapter, queue, tile) {
  const direction = String(tile.userInstructions || tile.description || '').trim();
  const baseProductUrl = queue.overlayUrl
    || (Array.isArray(queue.productImageUrls) && queue.productImageUrls[0])
    || null;

  // PDP images are square. Guard against an empty/malformed size ever reaching the
  // backend: an empty `user_sizes` makes createDesignConcept pass 'auto' + no
  // post-resize, so in edit mode Gemini returns the source's native resolution
  // (often 1024) instead of the requested square — the "I chose 2000 but got 1024"
  // bug. Always send a valid WxH, defaulting to 2000x2000. (POR-13385)
  const userSize = /^\d+x\d+$/.test(String(queue.size || '')) ? queue.size : '2000x2000';

  // Variant that supplied its OWN reference image → reuse the reference-styling
  // path (product kept accurate, style/scene copied from that reference image).
  if (tile.referenceImageUrl && baseProductUrl) {
    return generatePdpReferenceImageUrl(adapter, {
      baseProductUrl: unwrapShareUrl(baseProductUrl),
      referenceUrl: unwrapShareUrl(tile.referenceImageUrl),
      size: userSize,
      userInstructions: direction || null,
      forceEditHighRes: true,
    });
  }

  // Text-direction variant. The source product hero is the BASE IMAGE TO EDIT:
  // routing it through reference_images_urls makes the backend hand it to Gemini
  // as `original_image_url` ("IMAGE 1: Base image to edit. Preserve this image as
  // the primary composition unless instructed otherwise"), so each variant is a
  // genuine transformation of the SAME hero rather than a fresh hallucination.
  //
  // The creative direction MUST be the dominant instruction. The previous copy
  // over-stressed "reproduce the product EXACTLY", which — on top of the backend's
  // "preserve this image" framing — made Gemini echo the source unchanged (the
  // reported "I provided a prompt but got back the source image, no changes" bug).
  // Now: direction first, "make it visibly different / do not return the original",
  // and product-identity preservation is a constraint, not an override. (POR-13385)
  const change = direction
    || 'Restyle it into a clean, premium retail hero on a complementary background.';
  const imagePrompt = `You are given a product hero image as the base. Produce a NEW, retail-ready PDP hero image by applying this creative direction to the base image: ${change} `
    + 'The creative direction defines the change — apply it fully and make the result clearly, visibly different from the input. Do NOT return the original image unchanged. '
    + 'Keep the SAME real product: its packaging, brand, logo, label text, shape, proportions and colors must stay accurate and recognizable — do not swap in a different product, do not relabel it, and never paint the word "source" or any placeholder text onto it. '
    + 'Keep the product the clear focal point. Photo-real, high quality, sharp focus, accurate colors, square 1:1 composition. Add no extra text, captions, watermarks or logos beyond what the direction explicitly requests.'
    // When the direction asks for a white/plain background, pin the backdrop to pure
    // #FFFFFF so it stops coming back grey/shaded on the first run. (POR-13586)
    + (abDirectionWantsWhiteBackground(direction) ? AB_WHITE_BACKDROP_RULE : '');

  // Prefer EDITING the resolved source packshot (works for ASIN/name sources too:
  // resolveProductOverlayUrl already fetched their packshot into queue.overlayUrl).
  // Only when no image resolved do we fall back to product_images_names so the
  // backend resolves an identifier into a product reference. We deliberately do NOT
  // mix the two — product_images_names is the "PRODUCT REFERENCE (identity)" bucket
  // that yields a fresh generation, not an edit of the supplied hero. (POR-13385)
  const editRefs = baseProductUrl ? [unwrapShareUrl(baseProductUrl)] : [];
  const productNames = baseProductUrl ? [] : (queue.productImageNames || []);

  return postDesignWithRetry(
    {
      image_prompt: imagePrompt,
      user_sizes: userSize,
      product_images_names: productNames,
      reference_images_urls: editRefs,
      // A/B hero only: generate the edit at native 2K/4K (matching the chosen size)
      // so hero text is sharp instead of generated at ~1024 and upscaled. (POR-13385-5)
      force_edit_high_res: true,
      selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
    },
    { source: adapter.getSource?.() || 'creative_studio_chat' },
  );
}

// Per-attempt timing for an A/B variant. A healthy generation finishes in ~30s. The
// underlying /networks/design job occasionally hangs or drops its completion event
// with no error (sporadic) — leaving the tile stuck until the 5-min backend await.
// So if an attempt yields nothing within AB_ATTEMPT_TIMEOUT_MS we ABANDON it and
// AUTO-RETRY a fresh design job (up to AB_MAX_ATTEMPTS). AB_SLOW_HINT_MS also surfaces
// a manual Retry next to the spinner before the auto-retry fires. (POR-13385)
const AB_SLOW_HINT_MS = 45000;
const AB_ATTEMPT_TIMEOUT_MS = 80000;
const AB_MAX_ATTEMPTS = 3;
// How many previous versions a variant tile keeps for the result widget's history
// strip (newest first). The strip scrolls, so this only bounds how far back the user
// can step — kept generous so multiple regenerations stay reachable. (POR-13544)
const AB_HISTORY_MAX = 12;

/**
 * Generate (or regenerate) ONE A/B variant tile in place, with auto-retry, a slow-hint
 * and an attempt guard. Shared by the initial parallel run (startAbHeroGrid) and the
 * per-tile Retry (retryAbHeroTile).
 *
 * Each call owns ONE `attempt` token covering all of its internal auto-retries. A
 * manual Retry is a SEPARATE call that bumps `tile.attempt`, so the guard here drops
 * this call's (possibly late) results once it's been superseded — keeping retries
 * race-safe against a stale attempt that finally resolves.
 */
async function runAbTileGeneration(adapter, queue, idxResolver, rowIndex) {
  const isStaleConversation = makeConversationStaleCheck(adapter);
  const resolveIdx = () => (typeof idxResolver === 'function' ? idxResolver() : idxResolver);
  const getTile = () => {
    const m = adapter.getMessages()[resolveIdx()];
    const g = m && m._abHeroResult;
    return g ? g.tiles.find((t) => t.rowIndex === rowIndex) : null;
  };
  const patch = (p) => {
    // A variant that resolves after the user left this chat must not write into
    // the now-active chat. (POR-13504)
    if (isStaleConversation()) return;
    const idx = resolveIdx();
    const m = adapter.getMessages()[idx];
    const g = m && m._abHeroResult;
    if (!g) return;
    const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...p } : t));
    adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
    persistAbHero(adapter, resolveIdx);
  };

  const tile = getTile();
  if (!tile) return;
  const myAttempt = (tile.attempt || 0) + 1;

  let imageUrl = null;
  for (let attempt = 1; attempt <= AB_MAX_ATTEMPTS; attempt += 1) {
    // Keep any existing `url` (a previously-done image) so a Regenerate shows the old
    // image blurred under a "Regenerating…" overlay instead of an empty spinner; a
    // fresh/initial run has no url yet and shows the spinner. (POR-13385-2)
    patch({ state: 'generating', slow: false, attempt: myAttempt });

    const slowTimer = setTimeout(() => {
      const t = getTile();
      if (t && t.state === 'generating' && t.attempt === myAttempt) patch({ slow: true });
    }, AB_SLOW_HINT_MS);

    let outcome;
    try {
      // Race the generation against a timeout. If the timeout wins, the design job is
      // abandoned (it may still finish server-side — harmless, nothing awaits it) and
      // the loop fires a brand-new job.
      outcome = await Promise.race([
        generateAbVariantImageUrl(adapter, queue, tile).then((url) => ({ url })).catch((err) => ({ err })),
        new Promise((resolve) => { setTimeout(() => resolve({ timedOut: true }), AB_ATTEMPT_TIMEOUT_MS); }),
      ]);
    } finally {
      clearTimeout(slowTimer);
    }

    // Superseded by a manual Retry (or a newer run) → drop this call entirely.
    const cur = getTile();
    if (!cur || cur.attempt !== myAttempt) return;

    if (outcome && outcome.url) { imageUrl = outcome.url; break; }
    if (outcome && outcome.err) console.error('A/B hero: tile attempt failed', rowIndex, attempt, outcome.err);
    // else: timed out — auto-retry on the next loop iteration (if any remain).
  }

  const cur2 = getTile();
  if (cur2 && cur2.attempt === myAttempt) {
    if (imageUrl) {
      // Keep EVERY version in history (newest first, deduped, capped) INCLUDING the new
      // current, so the result widget's strip lists them all with the in-use one checked.
      // Prior versions already sit in history from when they were current. (POR-13544)
      const prevHistory = Array.isArray(cur2.history) ? cur2.history : [];
      const history = [imageUrl, ...prevHistory.filter((u) => u && u !== imageUrl)].slice(0, AB_HISTORY_MAX);
      patch({ state: 'done', url: imageUrl, slow: false, history });
    } else {
      patch({ state: 'error', slow: false });
    }
  }
}

/**
 * A/B hero parallel generation (POR-13385). Resolves the shared product/size
 * context once (buildPdpQueueContext, 'mixed' path) and generates TWO hero
 * variants concurrently (limit 2) via generateAbVariantImageUrl + runPool,
 * writing tile state under `_abHeroResult` onto the SAME message the user
 * submitted (`formIndex`, carrying `_abHeroForm`) — mutated in place rather
 * than appended as a new message, so the unified `ab_hero` widget transitions
 * collecting -> generating -> ready on one card (POR-13765). Each variant's
 * free-text direction (or reference image) drives its generation directly so
 * the two outputs are genuinely distinct. Mirrors startPdpParallelGrid's
 * tile/patch/pool structure but is intentionally minimal: two AI rows, no
 * gallery / use-as-is seeding and no Stop control.
 */
// Load an image for canvas use without tainting it: external URLs go through the
// same-origin /share proxy so toBlob() stays allowed. (POR-13544)
function loadImageForCanvas(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    let src = url;
    if (!/^data:|^blob:/.test(url) && typeof window !== 'undefined') {
      const origin = window.location.origin;
      try {
        const u = new URL(url, origin);
        if (u.origin !== origin) src = `${origin}/share?file=${encodeURIComponent(url)}`;
      } catch (e) { /* relative → already same-origin */ }
    }
    img.src = src;
  });
}

// Letterbox a (possibly non-square) source image onto a SIZE×SIZE white canvas — the
// product stays fully visible and undistorted — then upload it as a temp file and
// return its URL. Used to square the "Use original" variant so Create Layout / Optix,
// which expect square inputs like the AI variants, don't stretch it. (POR-13544)
async function squareImageToTempUrl(url, size) {
  if (!url || typeof document === 'undefined') return null;
  const img = await loadImageForCanvas(url);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const iw = img.naturalWidth || img.width || 1;
  const ih = img.naturalHeight || img.height || 1;
  const scale = Math.min(size / iw, size / ih); // contain
  const dw = Math.round(iw * scale);
  const dh = Math.round(ih * scale);
  ctx.drawImage(img, Math.round((size - dw) / 2), Math.round((size - dh) / 2), dw, dh);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  if (!blob) return null;
  const form = new FormData();
  form.append('file', new File([blob], 'ab-original.png', { type: 'image/png' }));
  const { data } = await axios.post('/upload-temp-file', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  // Use the stored `path` (e.g. "temp/<uuid>.png") as a host-relative "/temp/…" URL:
  // it's served the same way as the design endpoint's AI images (so Create Layout's
  // public_path() resolver and Optix's absolute-URL fetch both handle it), unlike the
  // disk's `url` which resolves to a non-served "/public/temp/…" in this env. (POR-13544)
  if (data && data.path) return `/${String(data.path).replace(/^\/+/, '')}`;
  return (data && data.url) || null;
}

// Replace an "original" tile's raw source url with a squared version (see above), in
// place. On any failure it keeps the raw url (the thumbnail still fits via CSS; only the
// downstream square consumers degrade). (POR-13544)
async function squareOriginalTile(adapter, idxResolver, rowIndex, sourceUrl, sizeStr) {
  try {
    const [w, h] = String(sizeStr || '2000x2000').split('x').map((n) => parseInt(n, 10));
    const size = (w && h) ? Math.max(w, h) : 2000;
    const squaredUrl = await squareImageToTempUrl(sourceUrl, size);
    if (!squaredUrl) return;
    const idx = typeof idxResolver === 'function' ? idxResolver() : idxResolver;
    const m = adapter.getMessages()[idx];
    const g = m && m._abHeroResult;
    if (!g) return;
    const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex
      ? { ...t, url: squaredUrl, history: [squaredUrl] }
      : t));
    adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
    persistAbHero(adapter, idxResolver);
  } catch (e) {
    console.warn('A/B hero: squaring original source failed, keeping raw image', e);
  }
}

export async function startAbHeroGrid(adapter, formIndex) {
  const isStaleConversation = makeConversationStaleCheck(adapter);
  adapter.setProcessing(true);
  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed');

    // The user left this chat while the context resolved — abandon the run before
    // writing `_abHeroResult` onto a message in the now-active chat. (POR-13504)
    if (isStaleConversation()) return;

    const rows = queue.rowSelections
      || (queue.types || []).map((t, i) => ({ rowIndex: i, type: t, label: `Variant ${i === 0 ? 'A' : 'B'}`, mode: 'ai' }));

    // A variant set to "Use original" reuses the SOURCE image with no AI applied: seed
    // it straight to a DONE tile (url = its source URL, falling back to the resolved
    // packshot) and keep it OUT of the generation pool, mirroring the PDP grid's
    // use-as-is rows. Everything else is an AI variant that generates. (POR-13544)
    const sourceUrl = queue.overlayUrl
      || (Array.isArray(queue.productImageUrls) && queue.productImageUrls[0])
      || null;
    const tiles = rows.map((row) => {
      const isOriginal = row.mode === 'original';
      const originalUrl = isOriginal ? (row.original_image_url || sourceUrl) : null;
      return {
        rowIndex: row.rowIndex,
        type: row.type || AB_VARIANT_TYPE,
        label: row.label,
        description: row.description || row.userInstructions || '',
        mode: isOriginal ? 'original' : 'ai',
        state: isOriginal ? (originalUrl ? 'done' : 'error') : 'queued',
        url: originalUrl,
        userInstructions: row.userInstructions || null,
        referenceImageUrl: row.reference_image_url || null,
        // All versions of this variant, newest first (current included). An AI tile
        // starts empty and gets its first url on generation; an original tile starts
        // with the source image so the strip can show it. (POR-13544)
        history: isOriginal ? (originalUrl ? [originalUrl] : []) : [],
      };
    });

    // Write the tiles-in-progress onto the SAME message as `_abHeroForm` (the form the
    // user just submitted) instead of appending a new message: the unified `ab_hero`
    // widget already lives on this message and re-renders itself from `_abHeroResult`
    // (collecting -> generating -> ready), matching the mechanism `runAbTileGeneration`/
    // `persistAbHero` use to patch tiles in place. (POR-13765)
    adapter.updateMessage(formIndex, { _abHeroResult: { stopped: false, tiles } });
    persistAbHero(adapter, formIndex);

    const findGridIndex = () => formIndex;

    // Both AI tiles enter generation at once (concurrency 2) so the two /networks/design
    // jobs are dispatched in parallel; runAbTileGeneration owns per-tile state, slow-hint
    // and the attempt guard. Any "Use original" tile is already done — square its source
    // (letterbox to the chosen size) CONCURRENTLY so Create Layout / Optix get a square
    // image instead of a stretched one. (POR-13544)
    const userSize = /^\d+x\d+$/.test(String(queue.size || '')) ? queue.size : '2000x2000';
    const originalTiles = tiles.filter((t) => t.mode === 'original' && t.url);
    await Promise.all([
      runPool(
        tiles.filter((t) => t.state === 'queued'),
        2,
        async (tile) => { await runAbTileGeneration(adapter, queue, findGridIndex, tile.rowIndex); },
      ),
      ...originalTiles.map((t) => squareOriginalTile(adapter, findGridIndex, t.rowIndex, t.url, userSize)),
    ]);
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Retry ONE A/B variant tile (POR-13385). Re-resolves the shared product/size
 * context from parsedPrompt (unchanged since generation) and regenerates just the
 * single tile in place on the existing `_abHeroResult` message. Backs the per-tile
 * Retry button so a variant that failed or stalled can be re-run without redoing
 * the whole form. The two variants are generated in parallel up front; this is the
 * recovery path for the occasional slow/dropped one.
 */
export async function retryAbHeroTile(adapter, gridMessageIndex, rowIndex, newPrompt = null) {
  const idx = typeof gridMessageIndex === 'function' ? gridMessageIndex() : gridMessageIndex;
  const msg = adapter.getMessages()[idx];
  if (!msg || !msg._abHeroResult) return;

  // A "same as source" (mode: 'original') tile carries no AI prompt and must NEVER be
  // regenerated by AI — that would silently turn "same as source" into an AI image. Its
  // error state instead means the source packshot never resolved (e.g. a typed ASIN that
  // timed out) — a Retry here re-resolves the shared context and re-seeds the tile from
  // the source, mirroring startAbHeroGrid's own seeding. (POR-13765)
  const targetTile = msg._abHeroResult.tiles.find((t) => t.rowIndex === rowIndex);
  if (targetTile && targetTile.mode === 'original') {
    {
      const grid = msg._abHeroResult;
      const busyTiles = grid.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, state: 'generating', slow: false } : t));
      adapter.updateMessage(idx, { _abHeroResult: { ...grid, tiles: busyTiles } });
    }

    adapter.setProcessing(true);
    try {
      const queue = await buildPdpQueueContext(adapter, 'mixed');
      const sourceUrl = queue.overlayUrl
        || (Array.isArray(queue.productImageUrls) && queue.productImageUrls[0])
        || null;
      const m = adapter.getMessages()[idx];
      const g = m && m._abHeroResult;
      if (!g) return;
      if (sourceUrl) {
        const cur = g.tiles.find((t) => t.rowIndex === rowIndex);
        const prevHistory = Array.isArray(cur && cur.history) ? cur.history : [];
        const history = [sourceUrl, ...prevHistory.filter((u) => u && u !== sourceUrl)].slice(0, AB_HISTORY_MAX);
        const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex
          ? { ...t, state: 'done', url: sourceUrl, slow: false, history }
          : t));
        adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
        const userSize = /^\d+x\d+$/.test(String(queue.size || '')) ? queue.size : '2000x2000';
        await squareOriginalTile(adapter, idx, rowIndex, sourceUrl, userSize);
        persistAbHero(adapter, idx);
      } else {
        const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, state: 'error', slow: false } : t));
        adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
        persistAbHero(adapter, idx);
      }
    } catch (e) {
      // Don't leave the card stuck on "Regenerating…" if re-resolution throws. (POR-13765)
      console.error('A/B hero: retry (same as source) failed', rowIndex, e);
      const m = adapter.getMessages()[idx];
      const g = m && m._abHeroResult;
      if (g) {
        const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, state: 'error', slow: false } : t));
        adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
      }
    } finally {
      adapter.setProcessing(false);
    }
    return;
  }

  // Apply the prompt edit (if any) AND flip the tile to "generating" SYNCHRONOUSLY,
  // before the async buildPdpQueueContext below. Otherwise the "Regenerating…" overlay
  // only appears once runAbTileGeneration runs — 7–10s after the click — so the card
  // looks frozen/broken. The url is kept so the overlay blurs the previous image. When
  // the prompt actually CHANGED, also drop any pasted reference image on this variant:
  // the reference path "recreates the first image's composition + adapts the product
  // into it" (buildReferenceDesignPayload) and treats the text only as a weak
  // "Additional user instruction" — so a new scene prompt like "place it in an 80s
  // kitchen" would be ignored and the old pasted background kept. Clearing the
  // reference routes it through the text-edit path so the new prompt actually drives
  // the regeneration. (A no-edit re-roll keeps the reference.) (POR-13385-2, -3)
  const trimmed = typeof newPrompt === 'string' ? newPrompt.trim() : '';
  {
    const grid = msg._abHeroResult;
    const current = grid.tiles.find((t) => t.rowIndex === rowIndex);
    const promptChanged = !!trimmed && trimmed !== String((current && current.description) || '').trim();
    const nextTiles = grid.tiles.map((t) => (t.rowIndex === rowIndex
      ? {
        ...t,
        state: 'generating',
        slow: false,
        ...(trimmed ? { userInstructions: trimmed, description: trimmed } : {}),
        ...(promptChanged ? { referenceImageUrl: null } : {}),
      }
      : t));
    adapter.updateMessage(idx, { _abHeroResult: { ...grid, tiles: nextTiles } });
  }

  adapter.setProcessing(true);
  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed');
    await runAbTileGeneration(adapter, queue, idx, rowIndex);
  } catch (e) {
    // If context-building throws before runAbTileGeneration takes ownership of the
    // tile state, don't leave the card stuck on "Regenerating…". (POR-13385-3)
    console.error('A/B hero: retry failed before generation', rowIndex, e);
    const m = adapter.getMessages()[idx];
    const g = m && m._abHeroResult;
    if (g) {
      const nextTiles = g.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, state: 'error', slow: false } : t));
      adapter.updateMessage(idx, { _abHeroResult: { ...g, tiles: nextTiles } });
    }
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Which up-front choice (if any) a row still needs before the parallel grid can
 * generate it. Scene rows need a background URL; generic mobile_hero needs a
 * style pick; GS1 rows need an approach (auto / describe / reference). (POR-13183)
 */
export function pdpRowPrepKind(row) {
  if (!row) return null;
  // Use-as-is (original/yours) and gallery rows don't run a generation, so they
  // need NO up-front prep — no scene pick, no mobile-hero style, and no GS1
  // approach prompt even for a Mobile Hero - GS1 row the user supplied their own
  // image for. (POR-13271)
  if (row.mode === 'original' || row.mode === 'yours' || row.mode === 'gallery') return null;
  if (row.mode === 'scene') return row.sceneUrl ? null : 'scene';
  if (row.type === 'mobile_hero') return 'mobile_hero_style';
  if (row.type === 'mobile_hero_gs1') return row.gs1Approach ? null : 'gs1_approach';
  return null;
}

/**
 * Begin the up-front prep for a parallel set. Resolves the shared product/size
 * context once (for scene compositing later), then shows the first needed picker
 * (or launches the grid if none). GS1 rows are NOT auto-seeded: an unresolved
 * GS1 row keeps gs1Approach: null so the prep asks for the approach. (POR-13183)
 */
export async function startPdpParallelPrep(adapter) {
  adapter.setProcessing(true);
  // Prep only resolves the shared overlay/size for the pickers; the real queue
  // (with the grounded product brief) is rebuilt by startPdpParallelGrid. Skip
  // the brief extraction here to avoid a redundant call. (POR-13307)
  const ctx = await buildPdpQueueContext(adapter, 'mixed', { withGrounding: false });
  adapter.setProcessing(false);

  const rows = (ctx.rowSelections || []).map((r) => ({ ...r, gs1Approach: r.gs1Approach || null }));

  const parsedPrompt = adapter.getParsedPrompt();
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpParallelPrep: {
      active: true,
      rows,
      size: ctx.size,
      overlayUrl: ctx.overlayUrl || (ctx.productImageUrls && ctx.productImageUrls[0]) || null,
      productImageUrls: ctx.productImageUrls || [],
      pendingRowIndex: null,
      pendingKind: null,
    },
  });
  adapter.saveConversationState();

  return prepResolveNextRow(adapter);
}

/**
 * Show the next interactive picker for the first row that still needs a choice;
 * if all rows are resolved, write them back to pdp_row_selections and launch the
 * parallel grid.
 */
export async function prepResolveNextRow(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep || !prep.active) return;

  const next = prep.rows.find((r) => pdpRowPrepKind(r));
  if (!next) {
    // All resolved → hand off to the grid.
    const resolvedRows = reindexRowSelections(prep.rows);
    adapter.setParsedPrompt({
      ...adapter.getParsedPrompt(),
      pdp_row_selections: resolvedRows,
      pdp_image_types: resolvedRows.map((r) => r.type),
      _pdpParallelPrep: null,
    });
    adapter.saveConversationState();
    return startPdpParallelGrid(adapter);
  }

  const kind = pdpRowPrepKind(next);
  adapter.setParsedPrompt({
    ...adapter.getParsedPrompt(),
    _pdpParallelPrep: { ...prep, pendingRowIndex: next.rowIndex, pendingKind: kind },
  });

  if (kind === 'scene') {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `Pick a background for <b>${next.label}</b>:`,
      widgets: [{ type: 'pdp_scene_picker', id: 'pdp_scene_picker', rowIndex: next.rowIndex, defaultScenePrompt: '' }],
      _pdpPrepPicker: { kind: 'scene', rowIndex: next.rowIndex, label: next.label },
    });
    adapter.setProcessing(false);
    return;
  }

  if (kind === 'mobile_hero_style') {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `<b>${next.label}</b> · pick a style to continue.`,
      widgets: [{
        type: 'mobile_hero_style_picker',
        id: 'mobile_hero_style_picker',
        header: next.label,
        cards: [
          // Reference isn't supported in the parallel prep yet (it would fall
          // through to a plain tile), so the prep picker only offers Cambridge
          // and GS1. The sequential showMobileHeroStylePicker still offers all
          // three. (POR-13183)
          { key: 'cambridge', title: 'Cambridge', subtitle: 'Callout style', thumbnail: '/img/creative-studio/mobile-hero-cambridge.svg', action: 'mobile_hero_style_pick:cambridge' },
          { key: 'gs1', title: 'GS1', subtitle: 'Enlarged elements', thumbnail: '/img/creative-studio/mobile-hero-gs1.svg', action: 'mobile_hero_style_pick:gs1' },
        ],
        skipAction: 'mobile_hero_skip',
        skipLabel: 'Skip this image',
      }],
      _pdpPrepPicker: { kind: 'mobile_hero_style', rowIndex: next.rowIndex, label: next.label },
    });
    adapter.setProcessing(false);
    return;
  }

  if (kind === 'gs1_approach') {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `<b>${next.label}</b> · GS1 typically enlarges key elements (like quantity and variant). How do you want to approach it?`,
      widgets: [{
        type: 'action_buttons',
        id: 'gs1_approach_picker',
        buttons: [
          { label: 'Use a reference image', iconClass: 'bi bi-image', action: 'gs1_approach:reference', variant: 'primary' },
          { label: 'Describe the changes', iconClass: 'bi bi-pencil', action: 'gs1_approach:describe', variant: 'primary' },
          { label: "Use Optix's judgment", iconClass: 'bi bi-stars', action: 'gs1_approach:auto', variant: 'primary' },
        ],
      }],
      _pdpPrepPicker: { kind: 'gs1_approach', rowIndex: next.rowIndex, label: next.label },
    });
    adapter.setProcessing(false);
    return;
  }
}

/**
 * Store a resolved choice onto a prep row, then advance to the next needed
 * picker (or launch the grid). Called by the Vue picker handlers (P3).
 *   patch examples: { sceneUrl, userInstructions } | { type: 'mobile_hero_cambridge' }
 */
export async function setPdpPrepRowChoice(adapter, rowIndex, patch) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep || !prep.active) return;
  const rows = prep.rows.map((r) => (r.rowIndex === rowIndex ? { ...r, ...patch } : r));
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpParallelPrep: { ...prep, rows, pendingRowIndex: null, pendingKind: null },
  });
  adapter.saveConversationState();
  return prepResolveNextRow(adapter);
}

/** Prep: ask the user to describe the GS1 changes in the chat. (POR-13183) */
export function showGs1DescribeForPrep(adapter, rowIndex) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep) return;
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpParallelPrep: { ...prep, awaitingInput: { kind: 'gs1_describe', rowIndex } },
  });
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: 'Describe the GS1 changes, then press Enter.',
  });
  // Drive the dock input highlight + the clickable sample link (POR-13271).
  const gs1Row = (prep.rows || []).find((r) => r.rowIndex === rowIndex);
  if (adapter.setPendingPdpRefine) {
    adapter.setPendingPdpRefine({
      rowIndex,
      label: (gs1Row && gs1Row.label) || 'Mobile Hero - GS1',
      example: 'make the quantity and flavor larger, keep the brand',
    });
  }
  adapter.setProcessing(false);
}

/** Prep: ask the user for a GS1 reference image (upload/paste/browse). (POR-13183) */
export function showGs1ReferenceSubPickerForPrep(adapter, rowIndex) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep) return;
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpParallelPrep: { ...prep, awaitingInput: { kind: 'gs1_reference', rowIndex } },
  });
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: 'Add a reference image for the GS1 styling:',
    widgets: [{
      type: 'action_buttons',
      id: 'gs1_reference_subpicker',
      buttons: [
        { label: 'Upload', iconClass: 'bi bi-upload', action: 'gs1_reference_upload', variant: 'secondary' },
        { label: 'Paste', iconClass: 'bi bi-clipboard', action: 'gs1_reference_paste', variant: 'secondary' },
        { label: 'Browse', iconClass: 'bi bi-folder2-open', action: 'gs1_reference_browse', variant: 'secondary' },
      ],
    }],
    _pdpPrepPicker: { kind: 'gs1_reference', rowIndex, label: '' },
  });
  adapter.setProcessing(false);
}

/** Prep resume: store the described GS1 approach on the row and advance. (POR-13183) */
export async function tryResumeGs1DescribeForPrep(adapter, userText) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep || !prep.active || prep.awaitingInput?.kind !== 'gs1_describe') return false;
  const trimmed = (userText || '').trim();
  if (!trimmed) {
    adapter.addAndSaveMessage({ role: 'assistant', content: 'I need a short description to continue. What should change on the GS1 image?' });
    return true;
  }
  if (adapter.setPendingPdpRefine) adapter.setPendingPdpRefine(null);
  const rowIndex = prep.awaitingInput.rowIndex;
  // clear awaitingInput, store the approach, advance
  adapter.setParsedPrompt({ ...adapter.getParsedPrompt(), _pdpParallelPrep: { ...adapter.getParsedPrompt()._pdpParallelPrep, awaitingInput: null } });
  await setPdpPrepRowChoice(adapter, rowIndex, { gs1Approach: { mode: 'describe', userInstructions: trimmed } });
  return true;
}

/** Prep resume: store the reference GS1 approach on the row and advance. (POR-13183) */
export async function tryResumeGs1ReferenceForPrep(adapter, candidateUrls) {
  const parsedPrompt = adapter.getParsedPrompt();
  const prep = parsedPrompt._pdpParallelPrep;
  if (!prep || !prep.active || prep.awaitingInput?.kind !== 'gs1_reference') return false;
  const url = (candidateUrls || []).find(Boolean);
  if (!url) {
    adapter.addAndSaveMessage({ role: 'assistant', content: "I didn't get a usable reference image. Upload, paste, or browse one." });
    return true;
  }
  const rowIndex = prep.awaitingInput.rowIndex;
  adapter.setParsedPrompt({ ...adapter.getParsedPrompt(), _pdpParallelPrep: { ...adapter.getParsedPrompt()._pdpParallelPrep, awaitingInput: null } });
  await setPdpPrepRowChoice(adapter, rowIndex, { gs1Approach: { mode: 'reference', referenceUrl: url } });
  return true;
}

/**
 * Assemble the _pdpGeneratedImages array from a finished grid message, skipping
 * skipped/stopped tiles. Same shape finalizePdpQueue produces so the existing
 * create_pdp_mixed_layout path consumes it unchanged.
 */
export function assembleGridGeneratedImages(gridMessage) {
  const grid = gridMessage && gridMessage._pdpParallelGrid;
  if (!grid) return { generated: [], hasAiOrScene: false };
  const generated = [];
  let hasAiOrScene = false;
  for (const tile of grid.tiles) {
    if (tile.state !== 'done') continue;
    generated.push({
      rowIndex: tile.rowIndex,
      type: tile.type,
      label: tile.label,
      rowMode: tile.mode,
      url: tile.url,
      overlayUrl: tile.overlayUrl || (tile.context && (tile.context.overlayUrl || tile.context.simplifiedOverlayUrl)) || null,
      template_id: tile.template_id || null,
      template_image_url: tile.template_image_url || null,
      // Scene composites always have the product baked in (composeSceneTileUrl),
      // so they must stay 'integrated' — sending a scene through the separate
      // path (e.g. via Create Editable Layout's makeEditable) would stamp the
      // product a SECOND time on top of the composite. (POR-13271)
      productMode: tile.mode === 'scene'
        ? 'integrated'
        : ((tile.context && tile.context.productMode) || null),
      overlayText: tile.overlayText || null,
      overlayTextMode: tile.overlayTextMode || 'editable',
    });
    if (tile.mode === 'ai' || tile.mode === 'scene') hasAiOrScene = true;
  }
  return { generated, hasAiOrScene };
}

/**
 * Regenerate a single grid tile in place (POR-13183). Rebuilds a minimal queue
 * context from the tile's stored context and re-runs generateSinglePdpImage.
 * Safe to call mid-batch. Returns true on success.
 */
export async function regeneratePdpGridTile(adapter, gridMessageIndex, rowIndex, { extraUserPrompt = null, productModeOverride = null, referenceImageUrls = [] } = {}) {
  const msg = adapter.getMessages()[gridMessageIndex];
  const grid = msg && msg._pdpParallelGrid;
  if (!grid) return false;
  const tile = grid.tiles.find((t) => t.rowIndex === rowIndex);
  if (!tile || !tile.context) return false;

  const setState = (patch) => {
    const cur = adapter.getMessages()[gridMessageIndex]._pdpParallelGrid;
    const tiles = cur.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...patch } : t));
    // Commit in-memory immediately (synchronous) for instant UI, then chain the
    // network save through the shared grid serializer so a tile regenerate and
    // any in-flight pool saves can't revert each other out of order (POR-13183).
    adapter.updateMessage(gridMessageIndex, { _pdpParallelGrid: { ...cur, tiles } });
    persistPdpGrid(adapter, gridMessageIndex);
  };

  setState({ state: 'regenerating' });

  const ctx = tile.context;

  const productRefs = (adapter.getParsedPrompt() || {}).product_references || [];
  // The synthetic queue below skips buildPdpQueueContext (where targetLanguage is
  // normally derived), so re-derive the product's marketplace language — otherwise
  // a regenerated tile reverts to the reference's language (English). (POR-13612-5)
  const targetLanguage = ctx.targetLanguage
    || marketplaceLanguage(productRefs.find((r) => r.marketplace)?.marketplace)
    || null;

  // If the regenerate prompt names additional product ASIN(s) — e.g. a cross-sell
  // partner ("...AND this second product: B07CTQWM25") — fetch their images (using
  // the ORIGINAL product's marketplace) and composite them in alongside the base
  // product, instead of leaving the ASIN as inert prompt text. (POR-13612-5)
  const originalAsins = new Set(
    productRefs.filter((r) => r.type === 'asin').map((r) => String(r.value).toUpperCase()),
  );
  const extraMarketplace = productRefs.find((r) => r.marketplace)?.marketplace || null;
  const extraAsins = [...new Set(
    (String(extraUserPrompt || '').match(/\bB[A-Z0-9]{9}\b/gi) || []).map((a) => a.toUpperCase()),
  )].filter((a) => !originalAsins.has(a));
  let extraProductUrls = [];
  if (extraAsins.length) {
    const fetched = await Promise.all(
      extraAsins.map((asin) => resolveProductOverlayUrl(
        [{ type: 'asin', value: asin, display_name: asin, marketplace: extraMarketplace }], [],
      )),
    );
    extraProductUrls = fetched.filter(Boolean);
  }

  // Route regenerate through the by-type dispatcher (generatePdpGridTile) so a
  // SCENE tile re-composes (instead of running a plain AI generation, which
  // produced the wrong image), a GS1 tile re-simplifies, and an AI tile keeps
  // its previous behavior (the AI branch falls to generateSinglePdpImage with
  // queue.types[0] === ctx.type at rowIndex 0). (POR-13183)
  const synthTile = {
    rowIndex: 0,
    type: ctx.type,
    label: ctx.label,
    mode: ctx.rowMode === 'scene' ? 'scene' : 'ai',
    sceneUrl: ctx.sceneUrl || null,
    gs1Approach: ctx.gs1Approach || null,
    // The tile's own direction, overlay text and logo: the typed text is a change
    // on top of them, not a replacement — sent alone, a regenerated tile lost its
    // brief scene and headline. (POR-14794)
    userInstructions: tile.userInstructions || ctx.userInstructions || null,
    overlayText: tile.overlayText || ctx.overlayText || null,
    overlayTextMode: tile.overlayTextMode || ctx.overlayTextMode || 'editable',
    brandLogoUrl: tile.brandLogoUrl || ctx.brandLogoUrl || null,
    noProduct: !!(tile.noProduct || ctx.noProduct),
  };
  // A reference image attached to the regenerate prompt is the style to match for
  // THIS run, so it wins over whatever reference the tile was originally built
  // from; without this the attachment was silently dropped. (POR-13766-6)
  const attachedReference = (referenceImageUrls || []).filter(Boolean)[0] || null;
  const effectiveReference = attachedReference || ctx.referenceImageUrl || null;

  const queue = {
    pathType: ctx.pathType || 'mixed',
    types: [ctx.type],
    rowSelections: [{ rowIndex: 0, type: ctx.type, label: ctx.label, mode: 'ai', libraryKey: ctx.libraryKey || null, reference_image_url: effectiveReference }],
    currentIndex: 0,
    size: ctx.size,
    targetLanguage,
    // Additional product images pulled from ASIN(s) named in the regenerate prompt
    // (e.g. a cross-sell partner), composited alongside the base product. (POR-13612-5)
    _extraProductUrls: extraProductUrls,
    // The scene branch composes queue.overlayUrl with the resolved scene, so
    // prefer the product URL (ctx.productUrl set by the SCENE grid worker)
    // before any raw/simplified overlay.
    overlayUrl: ctx.productUrl || ctx.rawOverlayUrl || ctx.overlayUrl || null,
    simplifiedOverlayUrl: ctx.simplifiedOverlayUrl || null,
    productContext: ctx.productContext,
    productGrounding: ctx.productGrounding || null,
    productImageNames: ctx.productImageNames || [],
    referenceImageUrls: ctx.referenceImageUrls || [],
    productImageUrls: ctx.productImageUrls || (ctx.productUrl ? [ctx.productUrl] : []),
    hasProductRefs: ctx.hasProductRefs,
    gs1Approach: ctx.gs1Approach || null,
    _refinePrompt: extraUserPrompt,
    _imageTypeCatalog: await loadImageTypeCatalog(),
    // When the Layered/Static toggle drove this regenerate, force the new mode
    // so the AI builds the matching image (separate = product-free background).
    productModeOverride: productModeOverride || ctx.productMode || null,
  };

  const result = await generatePdpGridTile(adapter, queue, synthTile);
  if (result.ok) {
    // Persist the chosen mode on the tile context so layout creation
    // (assembleGridGeneratedImages → handleCreateMixedPdpLayout) layers it.
    const resolvedMode = productModeOverride || (result.context && result.context.productMode) || ctx.productMode || null;
    commitFinishedTile(adapter, gridMessageIndex, rowIndex, result, {
      context: { ...result.context, productMode: resolvedMode },
      // Switching a Lifestyle tile to Layered is followed by the product cutout in
      // NetworksPageV2; the folder copy is saved after it, not here. (POR-14659)
      skipLibrarySave: productModeOverride === 'separate' && ctx.type === 'lifestyle',
    });
    return true;
  }
  // On failure, revert to done so the tile keeps its previous image.
  setState({ state: 'done' });
  return false;
}

/**
 * Rebuild parsedPrompt.pdp_row_selections from the grid's own tiles — the
 * authoritative list — so buildPdpQueueContext's rowIndex-indexed queue.types
 * can't drift out of sync (e.g. from a tile appended before this reconciliation
 * existed). Cheap no-op when already in sync. (POR-13526)
 */
function syncPdpRowSelectionsFromGrid(adapter, grid) {
  const parsedPrompt = adapter.getParsedPrompt();

  const rowSelections = grid.tiles.map((t) => ({
    rowIndex: t.rowIndex,
    type: t.type,
    label: t.label,
    mode: t.mode || 'ai',
    template_id: t.template_id || null,
    template_thumb: t.template_image_url || null,
    scene: t.sceneUrl || null,
    productMode: (t.context && t.context.productMode) || null,
    overlayTextMode: t.overlayTextMode || 'editable',
  }));

  adapter.setParsedPrompt({ ...parsedPrompt, pdp_row_selections: rowSelections });
  adapter.saveConversationState();
}

/**
 * Generate a tile the user just configured in the grid ("+ Add" → Direction /
 * Overlay text / Reference images → "Generate image"). The config is written onto
 * the tile first so it survives a reload and is reused by a later Regenerate.
 * A reference image is threaded through queue.rowSelections[rowIndex] — the same
 * hook the reference-seeded rows use — so generation matches its style instead of
 * ignoring it. (POR-13766-6)
 */
export async function generateConfiguredPdpGridTile(adapter, gridMessageIndex, rowIndex, config = {}, { asNewVersion = false } = {}) {
  const msg = adapter.getMessages()[gridMessageIndex];
  const grid = msg && msg._pdpParallelGrid;
  if (!grid) return false;
  const tile = grid.tiles.find((t) => t.rowIndex === rowIndex);
  if (!tile) return false;

  const referenceImageUrls = (config.referenceImageUrls || []).filter(Boolean);
  // The panel owns the product images too (pre-filled from the matching slot, then
  // editable), so its list is authoritative — including when the user cleared it.
  const productImageUrls = Array.isArray(config.productImageUrls)
    ? config.productImageUrls.filter(Boolean)
    : tileProductImageUrls(tile);

  const configFields = {
    userInstructions: config.userInstructions || null,
    overlayText: config.overlayText || null,
    overlayTextMode: config.overlayTextMode || 'editable',
    // Panel-owned too, so clearing the logo in the panel actually clears it.
    brandLogoUrl: Object.prototype.hasOwnProperty.call(config, 'brandLogoUrl')
      ? (config.brandLogoUrl || null)
      : (tile.brandLogoUrl || null),
    referenceImageUrls,
    referenceImageUrl: referenceImageUrls[0] || null,
    productImageUrls,
    productImageUrl: productImageUrls[0] || null,
  };
  const configured = { ...tile, ...configFields };

  const setState = (patch) => {
    const cur = adapter.getMessages()[gridMessageIndex]._pdpParallelGrid;
    if (!cur) return;
    const tiles = cur.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...patch } : t));
    adapter.updateMessage(gridMessageIndex, { _pdpParallelGrid: { ...cur, tiles } });
    persistPdpGrid(adapter, gridMessageIndex);
  };

  // Edit without a base reuses this path on a finished tile: keep its image (and
  // versions) on screen while it works, and only write the popup's fields onto the
  // tile once the new version exists — a failed or interrupted edit changes nothing.
  // (POR-14659)
  setState(asNewVersion ? { state: 'regenerating' } : { ...configured, state: 'generating' });

  adapter.setProcessing(true);

  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed');
    // buildPdpQueueContext indexes rowSelections by rowIndex; a configure-first tile
    // was appended after that list was built, so make sure this row exists and
    // carries the reference before generateSinglePdpImage reads it.
    const rowSelections = [...(queue.rowSelections || [])];
    rowSelections[rowIndex] = {
      ...(rowSelections[rowIndex] || {}),
      rowIndex,
      type: configured.type,
      label: configured.label,
      mode: 'ai',
      reference_image_url: configured.referenceImageUrl || null,
    };
    const types = [...(queue.types || [])];
    types[rowIndex] = configured.type;

    const result = await generatePdpGridTile(adapter, { ...queue, rowSelections, types }, configured);

    if (result.ok) {
      commitFinishedTile(adapter, gridMessageIndex, rowIndex, result, asNewVersion ? { tilePatch: configFields } : {});
      return true;
    }

    setState({ state: asNewVersion ? 'done' : 'error' });
    return false;
  } catch (e) {
    console.error('PDP grid: configured tile generation threw', configured.type, rowIndex, e);
    setState({ state: asNewVersion ? 'done' : 'error' });
    return false;
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * "Edit image" on a finished grid tile (POR-14659). With the base, the version on
 * screen is sent to the design endpoint as IMAGE 1 (high-res edit, pinned aspect
 * ratio) together with the popup's direction / baked text / extra images; without
 * it, it is a fresh configured generation. Either way the result is appended as a
 * new version, so ‹ › is the way back. On a Layered tile only the background is
 * edited and the product layer carries over. Returns true on success.
 */
export async function editPdpGridTile(adapter, gridMessageIndex, rowIndex, edit = {}) {
  const msg = adapter.getMessages()[gridMessageIndex];
  const grid = msg && msg._pdpParallelGrid;
  if (!grid) return false;
  const tile = grid.tiles.find((t) => t.rowIndex === rowIndex);
  if (!tile || tile.state !== 'done' || !tile.url) return false;

  const productImageUrls = (edit.productImageUrls || []).filter(Boolean);
  const referenceImageUrls = (edit.referenceImageUrls || []).filter(Boolean);
  const brandLogoUrl = edit.brandLogoUrl || null;
  const userInstructions = String(edit.userInstructions || '').trim() || null;
  const overlayText = String(edit.overlayText || '').trim() || null;
  const overlayTextMode = edit.overlayTextMode === 'part_of_image' ? 'part_of_image' : 'editable';

  if (!edit.useBase) {
    return generateConfiguredPdpGridTile(adapter, gridMessageIndex, rowIndex, buildNoBaseEditConfig(tile, edit), { asNewVersion: true });
  }

  const setState = (state) => applyPdpGridUpdate(adapter, gridMessageIndex, (g) => ({
    ...g,
    tiles: g.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, state } : t)),
  }));

  setState('regenerating');

  const ctx = tile.context || {};
  const parsedPrompt = adapter.getParsedPrompt() || {};
  const productRefs = parsedPrompt.product_references || [];
  const language = ctx.targetLanguage
    || marketplaceLanguage(productRefs.find((r) => r.marketplace)?.marketplace)
    || null;

  try {
    const imageUrl = await postDesignWithRetry(
      {
        image_prompt: buildPdpEditPrompt({
          userInstructions,
          overlayText,
          overlayTextMode,
          productImageCount: productImageUrls.length,
          referenceImageCount: referenceImageUrls.length,
          hasBrandLogo: !!brandLogoUrl,
          language,
        }),
        user_sizes: ctx.size || parsedPrompt.pdp_ai_size || null,
        product_images_names: [],
        // IMAGE 1 = the version on screen → backend original_image_url.
        reference_images_urls: [tile.url, ...productImageUrls, ...referenceImageUrls].map(unwrapShareUrl),
        brand_logo_url: brandLogoUrl,
        notification_thumbnail_url: tile.overlayUrl || null,
        force_edit_high_res: true,
        selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
      },
      { source: adapter.getSource?.() || 'creative_studio_chat' },
    );

    commitFinishedTile(
      adapter,
      gridMessageIndex,
      rowIndex,
      // The product layer (and Cambridge callouts) carry over: only the base changed.
      { imageUrl, attachment: { overlayUrl: tile.overlayUrl || null, _mrhiCallouts: tile._mrhiCallouts || null } },
      { context: ctx, tilePatch: overlayText ? { overlayText, overlayTextMode } : {} },
    );
    return true;
  } catch (e) {
    console.error('PDP grid: edit failed', tile.type, rowIndex, e);
    setState('done');
    return false;
  }
}

/**
 * Retry a tile that FAILED to generate (state 'error'). A failed tile never got
 * a post-generation `context`, so `regeneratePdpGridTile` (which refines a DONE
 * tile from its context) can't run it. Instead rebuild the shared queue from
 * scratch and re-run the original row exactly like the initial grid generation
 * — `generatePdpGridTile` indexes the queue by `tile.rowIndex`. Fires
 * immediately with no feedback prompt; on failure the tile stays retryable. (POR-13183)
 */
export async function retryPdpGridTile(adapter, gridMessageIndex, rowIndex) {
  const msg = adapter.getMessages()[gridMessageIndex];
  const grid = msg && msg._pdpParallelGrid;
  if (!grid) return false;
  const tile = grid.tiles.find((t) => t.rowIndex === rowIndex);
  if (!tile) return false;

  syncPdpRowSelectionsFromGrid(adapter, grid);

  const setState = (patch) => {
    const cur = adapter.getMessages()[gridMessageIndex]._pdpParallelGrid;
    if (!cur) return;
    const tiles = cur.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...patch } : t));
    adapter.updateMessage(gridMessageIndex, { _pdpParallelGrid: { ...cur, tiles } });
    persistPdpGrid(adapter, gridMessageIndex);
  };

  setState({ state: 'generating' });

  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed');
    const result = await generatePdpGridTile(adapter, queue, tile);
    if (result.ok) {
      commitFinishedTile(adapter, gridMessageIndex, rowIndex, result);
      return true;
    }
    setState({ state: 'error' });
    return false;
  } catch (e) {
    console.error('PDP grid: retry tile threw', tile && tile.type, rowIndex, e);
    setState({ state: 'error' });
    return false;
  }
}

/**
 * Append new image types to an already-rendered parallel grid and generate
 * them, without touching the existing tiles. Shared by the grid's "+ Add
 * image type" tile and the add_pdp_image_types chat function, so both paths
 * append/generate identically (POR-13526).
 *
 * Returns the list of type keys actually added (already-present types and
 * unknown keys are silently dropped).
 */
/** The reference image a tile was built from, wherever it ended up. */
function tileReferenceUrl(tile) {
  if (!tile) return null;
  return (tile.context && tile.context.referenceImageUrl) || tile.referenceImageUrl || null;
}

/**
 * The product packshots a tile builds on, as a list. Falls back to the packshot a
 * generated tile actually used (context) and to a use-as-is tile's own image, so a
 * set staged from a brief can always hand something on to a newly added tile.
 */
function tileProductImageUrls(tile) {
  if (!tile) return [];
  if (Array.isArray(tile.productImageUrls) && tile.productImageUrls.length) return tile.productImageUrls;
  if (tile.productImageUrl) return [tile.productImageUrl];
  const ctx = tile.context || {};
  if (Array.isArray(ctx.productImageUrls) && ctx.productImageUrls.length) return ctx.productImageUrls;
  const single = ctx.productUrl || ctx.overlayUrl || tile.original_image_url || null;
  return single ? [single] : [];
}

/** The first product packshot anywhere in the set, in slot order. (POR-13766-8) */
function firstProductImageUrls(tiles) {
  for (const tile of tiles || []) {
    const urls = tileProductImageUrls(tile);
    if (urls.length) return urls;
  }
  return [];
}

/**
 * Seed values for a tile added from the grid's "+ Add". A type ALREADY in the set
 * (i.e. one the brief configured) copies that tile's direction, overlay text and
 * images, so the addition is a second take on the same idea. A brand-new type
 * inherits the first slot's reference + product images only — usually the user
 * wants the same reference with a different prompt. (POR-13766-6)
 */
function preloadForAddedTile(adapter, grid, key) {
  const tiles = (grid && grid.tiles) || [];
  const parsedPrompt = adapter.getParsedPrompt() || {};
  // The set's own images are the fallback for anything the source tile lacks: the
  // added tile must land with a product image whenever the set has one at all,
  // even when the matching slot itself was staged without one. (POR-13766-8)
  const setProducts = firstProductImageUrls(tiles);
  const fallbackRef = tiles.map(tileReferenceUrl).find(Boolean)
    || (parsedPrompt.reference_images_urls || []).filter(Boolean)[0]
    || null;

  const sameType = tiles.filter((t) => t.type === key);
  const source = sameType.find((t) => t.userInstructions || t.overlayText || tileReferenceUrl(t)) || sameType[0] || null;

  if (source) {
    const own = tileProductImageUrls(source);
    const products = own.length ? own : setProducts;
    return {
      userInstructions: source.userInstructions || null,
      overlayText: source.overlayText || null,
      overlayTextMode: source.overlayTextMode || 'editable',
      brandLogoUrl: source.brandLogoUrl || (tiles.map((t) => t.brandLogoUrl).find(Boolean) || null),
      referenceImageUrls: [tileReferenceUrl(source) || fallbackRef].filter(Boolean),
      ...(products.length ? { productImageUrls: products } : null),
    };
  }

  return {
    referenceImageUrls: [fallbackRef].filter(Boolean),
    brandLogoUrl: tiles.map((t) => t.brandLogoUrl).find(Boolean) || null,
    ...(setProducts.length ? { productImageUrls: setProducts } : null),
  };
}

export async function addPdpGridTypes(adapter, gridMessageIndex, typeKeys, { configureFirst = false } = {}) {
  const msg = adapter.getMessages()[gridMessageIndex];
  const grid = msg && msg._pdpParallelGrid;

  if (!grid) {
    return [];
  }

  const present = new Set(grid.tiles.map((t) => t.type));

  // Configure-first additions may REPEAT a type already in the set (e.g. a second
  // take on the brief's Feature & Iconography), so only the fire-and-generate path
  // de-duplicates against what is already there. (POR-13766-6)
  const keysToAdd = [...new Set((typeKeys || []).map(normalizeTypeKey))]
    .filter((key) => BUILT_IN_TYPES[key] && (configureFirst || !present.has(key)));

  if (!keysToAdd.length) {
    return [];
  }

  let nextRowIndex = grid.tiles.reduce((max, t) => Math.max(max, t.rowIndex), -1) + 1;

  const newTiles = keysToAdd.map((key) => ({
    rowIndex: nextRowIndex++,
    type: key,
    label: BUILT_IN_TYPES[key].label,
    mode: 'ai',
    state: configureFirst ? 'configuring' : 'queued',
    url: null,
    overlayUrl: null,
    _mrhiCallouts: null,
    template_id: null,
    template_image_url: null,
    sceneUrl: null,
    gs1Approach: null,
    userInstructions: null,
    overlayText: null,
    overlayTextMode: 'editable',
    brandLogoUrl: null,
    own_image_source: null,
    context: null,
    // Seed direction / overlay / reference / product images from the set so an
    // added tile starts related to the brief and product instead of blank.
    ...(configureFirst ? preloadForAddedTile(adapter, grid, key) : null),
  }));

  applyPdpGridUpdate(adapter, gridMessageIndex, (g) => ({ ...g, tiles: [...g.tiles, ...newTiles] }));

  syncPdpRowSelectionsFromGrid(adapter, { tiles: [...grid.tiles, ...newTiles] });

  const labels = newTiles.map((t) => `**${t.label}**`).join(', ');

  // Configure-first stops here: the tile waits in the grid with its Direction /
  // Overlay text / Reference images panel open, and only generates when the user
  // presses "Generate image". Generating on click produced images unrelated to the
  // brief and product, which made Add unusable. (POR-13766-6)
  if (configureFirst) {
    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: `Added ${labels}. Review the direction and reference image, then press <b>Generate image</b>.`,
    });

    return keysToAdd;
  }

  await adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Adding <b>${newTiles.length}</b> more: ${labels}.`,
  });

  const setTileState = (rowIndex, patch) => {
    const cur = adapter.getMessages()[gridMessageIndex]._pdpParallelGrid;

    if (!cur) {
      return;
    }

    const tiles = cur.tiles.map((t) => (t.rowIndex === rowIndex ? { ...t, ...patch } : t));

    adapter.updateMessage(gridMessageIndex, { _pdpParallelGrid: { ...cur, tiles } });

    persistPdpGrid(adapter, gridMessageIndex);
  };

  adapter.setProcessing(true);

  try {
    const queue = await buildPdpQueueContext(adapter, 'mixed');

    await runPool(newTiles, PDP_GRID_CONCURRENCY, async (tile) => {
      setTileState(tile.rowIndex, { state: 'generating' });

      try {
        const result = await generatePdpGridTile(adapter, queue, tile);

        if (result.ok) {
          setTileState(tile.rowIndex, {
            state: 'done',
            url: result.imageUrl,
            overlayUrl: (result.attachment && result.attachment.overlayUrl) || null,
            _mrhiCallouts: (result.attachment && result.attachment._mrhiCallouts) || null,
            context: result.context,
          });
        } else {
          setTileState(tile.rowIndex, { state: 'error' });
        }
      } catch (e) {
        console.error('PDP grid: add-type tile generation threw', tile.type, tile.rowIndex, e);
        setTileState(tile.rowIndex, { state: 'error' });
      }
    });
  } finally {
    adapter.setProcessing(false);
  }

  return keysToAdd;
}

/**
 * Inspect the current parsedPrompt state and report which inputs the
 * Reference style needs that are missing.
 *
 * Returns { reference, product, missing }:
 *   - reference: URL of the reference image, or null
 *   - product: 'pending' (resolvable from refs) | resolved overlay URL | null
 *   - missing: array of strings ('reference', 'product')
 */
export function getReferenceInputs(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue || {};
  const productRefs = (parsedPrompt.product_references || []).filter((r) => !r._deselected);

  const explicitRefs = parsedPrompt.reference_images_urls || [];
  const referenceImageUrl = explicitRefs[0]
    || (productRefs.find((r) => r.type === 'url' && r._isReference)?.value)
    || null;

  const hasProductRefs = productRefs.length > 0 || !!queue.overlayUrl;

  const missing = [];
  if (!referenceImageUrl) missing.push('reference');
  if (!hasProductRefs) missing.push('product');

  return {
    reference: referenceImageUrl,
    product: hasProductRefs ? (queue.overlayUrl || 'pending') : null,
    missing,
  };
}

/**
 * Pause the queue and post a chat message asking for the missing reference /
 * product inputs. Stores `_pdpQueue.awaitingInput` so the next user message
 * can be inspected by tryResumeReferenceQueue.
 */
export function requestReferenceInputs(adapter, missing) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  let message;
  if (missing.includes('reference') && missing.includes('product')) {
    message = 'To create a Reference-style Mobile Hero I need two things: a reference image showing the style to match, and the product image. Attach or paste the URL of the reference image, and provide the ASIN, GTIN, name, or attach the product image.';
  } else if (missing.includes('reference')) {
    message = 'To create a Reference-style Mobile Hero I need a reference image showing the style to match. Attach it or paste its URL.';
  } else {
    message = 'I also need the product image. Provide the ASIN, GTIN, name, or attach the product image.';
  }

  adapter.addAndSaveMessage({
    role: 'assistant',
    content: message,
  });

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: {
      ...queue,
      awaitingInput: {
        itemIndex: queue.currentIndex,
        missing,
        style: 'reference',
      },
    },
  });
  adapter.saveConversationState();
  adapter.setProcessing(false);
}

/**
 * Called after each user message while the queue is paused on a Reference
 * style awaiting inputs. Re-checks getReferenceInputs and, if both inputs
 * are now satisfied, clears awaitingInput, sets the queue item type to
 * mobile_hero_reference, and resumes generation.
 *
 * Returns true if it handled the message (resumed or re-asked), false if
 * the queue is not in awaitingInput state.
 */
export async function tryResumeReferenceQueue(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue || !queue.awaitingInput || queue.awaitingInput.style !== 'reference') {
    return false;
  }

  const inputs = getReferenceInputs(adapter);
  if (inputs.missing.length > 0) {
    requestReferenceInputs(adapter, inputs.missing);
    return true;
  }

  const newTypes = [...queue.types];
  newTypes[queue.awaitingInput.itemIndex] = 'mobile_hero_reference';

  adapter.setParsedPrompt({
    ...parsedPrompt,
    reference_images_urls: parsedPrompt.reference_images_urls || (inputs.reference ? [inputs.reference] : []),
    _pdpQueue: {
      ...queue,
      types: newTypes,
      awaitingInput: null,
    },
  });
  adapter.saveConversationState();
  await generateNextInPdpQueue(adapter);
  return true;
}

/**
 * Show the GS1 approach picker for the current queue item. Three buttons:
 * use a reference image, describe the changes, or use Optix's judgment.
 * Each click is handled in NetworksPageV2.vue's onWidgetAction.
 */
export function showGs1ApproachPicker(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  adapter.addMessage({
    role: 'assistant',
    content: 'GS1 typically enlarges key elements (like quantity and variant). How do you want to approach it?',
    widgets: [{
      type: 'action_buttons',
      id: 'gs1_approach_picker',
      buttons: [
        { label: 'Use a reference image', iconClass: 'bi bi-image', action: 'gs1_approach:reference', variant: 'primary' },
        { label: 'Describe the changes', iconClass: 'bi bi-pencil', action: 'gs1_approach:describe', variant: 'primary' },
        { label: "Use Optix's judgment", iconClass: 'bi bi-stars', action: 'gs1_approach:auto', variant: 'primary' },
      ],
    }],
    _pdpGs1ApproachContext: {
      queueIndex: queue.currentIndex,
    },
  });
  adapter.setProcessing(false);
  adapter.saveMessages();
}

/**
 * Persist the chosen approach onto the queue item AND clear any awaiting
 * input state, then resume generation.
 */
export async function setGs1Approach(adapter, approach) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  // Hold the loader on through the simplify Gemini call (10–30s) AND the
  // subsequent image generation. generateNextInPdpQueue sets processing(true)
  // again later for the actual generation, but only AFTER the simplify
  // JIT-prep block — which would leave a visible gap where the input bar
  // looks idle while Gemini is working on the simplified packshot.
  adapter.setProcessing(true);

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: {
      ...queue,
      gs1Approach: { ...approach, forItemIndex: queue.currentIndex },
      awaitingInput: null,
    },
  });

  await generateNextInPdpQueue(adapter);
}

/**
 * Non-interactive confirmation message for the auto approach. Then
 * immediately sets the approach and resumes the queue.
 */
export async function showGs1AutoConfirm(adapter) {
  adapter.addMessage({
    role: 'assistant',
    content: "Got it — I'll apply GS1 best-practices: enlarge the quantity and variant, boost legibility, keep the packshot center-stage.",
  });
  adapter.saveMessages();
  await setGs1Approach(adapter, { mode: 'auto' });
}

/**
 * Pause the queue and ask the user to type instructions in the chat.
 * The next user message is intercepted by tryResumeGs1DescribeQueue.
 */
export function showGs1DescribeMessage(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  adapter.addMessage({
    role: 'assistant',
    content: 'Describe the GS1 changes, then press Enter.',
    widgets: [{
      type: 'action_buttons',
      id: 'gs1_describe_back',
      buttons: [
        { label: 'Back', iconClass: 'bi bi-arrow-left', action: 'gs1_back_to_approach', variant: 'plain' },
      ],
    }],
  });

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: {
      ...queue,
      awaitingInput: {
        style: 'gs1_describe',
        itemIndex: queue.currentIndex,
        missing: ['user_instructions'],
      },
    },
  });
  // Drive the dock input highlight + the clickable sample link (POR-13271).
  if (adapter.setPendingPdpRefine) {
    adapter.setPendingPdpRefine({
      rowIndex: queue.currentIndex,
      label: 'Mobile Hero - GS1',
      example: 'make the quantity and flavor larger, keep the brand',
    });
  }
  adapter.setProcessing(false);
  adapter.saveMessages();
}

/**
 * Called by sendMessage when awaitingInput.style === 'gs1_describe'.
 * Captures the user message text as userInstructions and resumes the queue.
 */
export async function tryResumeGs1DescribeQueue(adapter, userInstructionsText) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue || queue.awaitingInput?.style !== 'gs1_describe') {
    return false;
  }

  const trimmed = (userInstructionsText || '').trim();
  if (!trimmed) {
    adapter.addMessage({
      role: 'assistant',
      content: 'I need your instructions to continue — please type what to emphasize and send again.',
    });
    adapter.saveMessages();
    return true;
  }

  if (adapter.setPendingPdpRefine) adapter.setPendingPdpRefine(null);
  await setGs1Approach(adapter, { mode: 'describe', userInstructions: trimmed });
  return true;
}

/**
 * Sub-widget after the user picked "Use a reference image". Offers
 * Upload / Paste / Browse Files / Back. Sets awaitingInput so the
 * next attachment / URL lands as the reference.
 */
export function showGs1ReferenceSubPicker(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  adapter.addMessage({
    role: 'assistant',
    content: "Add a <b>reference image</b> — I'll match its GS1 styling (enlarged quantity, callouts) on your packshot.",
    widgets: [{
      type: 'action_buttons',
      id: 'gs1_reference_subpicker',
      buttons: [
        { label: 'Upload reference', iconClass: 'bi bi-upload', action: 'gs1_reference_upload', variant: 'primary' },
        { label: 'Paste image', iconClass: 'bi bi-clipboard', action: 'gs1_reference_paste', variant: 'primary' },
        { label: 'Browse Files', iconClass: 'bi bi-folder', action: 'gs1_reference_browse', variant: 'primary' },
        { label: 'Back', iconClass: 'bi bi-arrow-left', action: 'gs1_back_to_approach', variant: 'plain' },
      ],
    }],
  });

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: {
      ...queue,
      awaitingInput: {
        style: 'gs1_reference',
        itemIndex: queue.currentIndex,
        missing: ['reference_url'],
      },
    },
  });
  adapter.setProcessing(false);
  adapter.saveMessages();
}

/**
 * Called by sendMessage when awaitingInput.style === 'gs1_reference'.
 * Captures the first attachment / data URI / URL as the reference and
 * resumes the queue. Re-asks if nothing usable came in.
 */
export async function tryResumeGs1ReferenceQueue(adapter, candidateUrls) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue || queue.awaitingInput?.style !== 'gs1_reference') {
    return false;
  }

  const url = (candidateUrls || []).find(Boolean);
  if (!url) {
    adapter.addMessage({
      role: 'assistant',
      content: 'I did not get a usable reference image. Attach a file, paste an image URL, or use Browse Files.',
    });
    adapter.saveMessages();
    return true;
  }

  await setGs1Approach(adapter, { mode: 'reference', referenceUrl: url });
  return true;
}

/**
 * Show the Mobile Hero style picker widget for a queue item whose type is
 * the generic `mobile_hero`. Renders three cards (Cambridge, GS1, Reference)
 * plus a Skip link. The user's click drives `mobile_hero_style_pick:*` widget
 * actions, which resolve the queue item's type and resume the queue.
 *
 * The widget message carries `_pdpStylePickerContext` with the queue index
 * (and the sequence letter when part of a multi-image set) so the action
 * handler knows which queue item to update.
 */
export function showMobileHeroStylePicker(adapter) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  const totalCount = queue.types.length;
  const sequenceLetter = totalCount > 1
    ? String.fromCharCode(65 + queue.currentIndex)
    : '';
  const headerLabel = sequenceLetter
    ? `Mobile Hero ${sequenceLetter}`
    : 'Mobile Hero';

  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `${headerLabel} · pick a style to continue.`,
    widgets: [{
      type: 'mobile_hero_style_picker',
      id: 'mobile_hero_style_picker',
      header: headerLabel,
      cards: [
        {
          key: 'cambridge',
          title: 'Cambridge',
          subtitle: 'Callout style',
          thumbnail: '/img/creative-studio/mobile-hero-cambridge.svg',
          action: 'mobile_hero_style_pick:cambridge',
        },
        {
          key: 'gs1',
          title: 'GS1 →',
          subtitle: 'Editor opens',
          thumbnail: '/img/creative-studio/mobile-hero-gs1.svg',
          action: 'mobile_hero_style_pick:gs1',
        },
        {
          key: 'reference',
          title: 'Reference',
          subtitle: 'Match an image',
          thumbnail: '/img/creative-studio/mobile-hero-reference.svg',
          action: 'mobile_hero_style_pick:reference',
        },
      ],
      skipAction: 'mobile_hero_skip',
      skipLabel: 'Skip this image',
    }],
    _pdpStylePickerContext: {
      queueIndex: queue.currentIndex,
      sequenceLetter,
    },
  });
  adapter.setProcessing(false);
}

/**
 * Mid-queue card for a Gallery-mode row. Posts the chosen template thumbnail
 * with a Keep-only widget (no regenerate/skip/mode-toggle — gallery picks are
 * already finalized by the user up front). Clicking Keep advances the queue.
 *
 * Before rendering, asks the backend to composite the user's product into
 * the template's UPC/GTIN slot so the chat preview shows the user's actual
 * product rather than the template author's placeholder packshot. The
 * endpoint falls back to the bare template thumbnail on any failure, so
 * this call never blocks queue progress for long — short timeout and
 * graceful degradation to `row.template_thumb` if anything goes wrong.
 */
export async function showGalleryRowCard(adapter, row, queue) {
  const totalCount = queue.types.length;
  const isLast = queue.currentIndex === totalCount - 1;
  const showFinish = totalCount > 1 && !isLast;

  const fallbackThumb = row.template_thumb || '';
  const productUrl = queue.overlayUrl || null;

  // Skip the product-into-template composite for `noOverlay` row types
  // (Nutrition Facts, Ingredients, How To, Feature Benefits, Reason To
  // Believe). Those rows are information panels — the template *is* the
  // content, and overlaying the user's packshot on top would damage the
  // very thing the user picked the template for. Show the bare template
  // thumbnail instead so the preview accurately reflects what they'll
  // get at layout-creation time (the gallery branch of
  // handleCreateMixedPdpLayout drops `product_image_url` for the same
  // reason).
  const typeDef = BUILT_IN_TYPES[row.type];
  const isNoOverlayType = !!(typeDef && typeDef.noOverlay);

  let previewThumb = fallbackThumb;
  if (row.template_id && productUrl && !isNoOverlayType) {
    try {
      adapter.setProcessing(true);
      const { data } = await axios.post('/networks/preview-template-with-product', {
        template_id: row.template_id,
        product_image_url: productUrl,
      });
      if (data?.preview_url) {
        previewThumb = data.preview_url;
      }
    } catch (e) {
      console.warn('Gallery preview composite failed, using template thumbnail', e);
    } finally {
      adapter.setProcessing(false);
    }
  }

  // The chat normalizer needs either fileType "image/*", a recognizable image
  // extension in the name, or "/share?file=" in the URL to classify an
  // attachment as an image (otherwise it gets typed as 'file' and the <img>
  // tag isn't rendered). Force a .png suffix on the attachment name so the
  // normalizer always classifies gallery thumbs as images, regardless of the
  // share URL's query-string format.
  const safeName = `${row.label || 'template'}.png`;

  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `<b>${row.label}</b> · template selected`,
    attachments: previewThumb
      ? [{ type: 'url', payload: previewThumb, name: safeName }]
      : [],
    _pdpImageContext: {
      type: row.type,
      label: row.label,
      pathType: 'mixed',
      rowIndex: row.rowIndex,
      rowMode: 'gallery',
      templateId: row.template_id,
      templateThumb: previewThumb,
      templateImageUrl: row.template_image_url || fallbackThumb || null,
      productMode: null,
      isLast,
      showFinish,
      totalCount,
    },
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_image_decision',
      buttons: [
        { label: isLast ? 'Keep' : 'Keep & next', action: 'pdp_image_keep_next', variant: 'primary' },
        ...(showFinish ? [{ label: 'Finish', action: 'pdp_image_finish', variant: 'plain' }] : []),
      ],
    }],
  });
  adapter.setProcessing(false);
}

/**
 * Sequential-queue handler for a use-as-is row (Hero "Original" or a
 * user-supplied "Yours" image). No Gemini call, no decision widget: the row's
 * fixed image is kept immediately and the queue advances. Produces the same
 * kept-message shape (attachments[0].payload + _pdpImageContext + _pdpImageKept)
 * that finalizePdpQueue/findKeptMessageForRow expect. (POR-13271)
 */
export async function keepUseAsIsRow(adapter, row, queue) {
  const url = row.mode === 'yours'
    ? (row.own_image_url || null)
    : (row.original_image_url || queue.overlayUrl || null);
  const safeName = `${row.label || 'image'}.png`;
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: row.mode === 'yours'
      ? `<b>${row.label}</b> · your image`
      : `<b>${row.label}</b> · original (used as-is)`,
    attachments: url ? [{ type: 'url', payload: url, name: safeName }] : [],
    _pdpImageContext: {
      type: row.type,
      label: row.label,
      pathType: 'mixed',
      rowIndex: row.rowIndex,
      rowMode: row.mode,
      productMode: null,
    },
    _pdpImageKept: true,
  });
  const parsedPrompt = adapter.getParsedPrompt();
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: { ...queue, currentIndex: queue.currentIndex + 1 },
  });
  adapter.saveConversationState();
  await generateNextInPdpQueue(adapter);
}

/**
 * Mid-queue widget for a Scene-mode row. Renders the scene picker (Generate /
 * Upload / Paste / Files + Composite + Skip). After Composite, the row's chat
 * card behaves identically to an AI row.
 */
export function showScenePicker(adapter, row, queue) {
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Pick a background for <b>${row.label}</b>:`,
    widgets: [{
      type: 'pdp_scene_picker',
      id: 'pdp_scene_picker',
      rowIndex: row.rowIndex,
      defaultScenePrompt: '',
    }],
    _pdpImageContext: {
      type: row.type,
      label: row.label,
      pathType: 'mixed',
      rowIndex: row.rowIndex,
      rowMode: 'scene',
      productMode: null,
      isLast: queue.currentIndex === queue.types.length - 1,
      showFinish: queue.types.length > 1 && queue.currentIndex !== queue.types.length - 1,
      totalCount: queue.types.length,
    },
    _pdpScenePickerContext: {
      rowIndex: row.rowIndex,
      type: row.type,
      label: row.label,
      pathType: 'mixed',
      totalCount: queue.types.length,
    },
  });
  adapter.setProcessing(false);
}

/**
 * Produce the reference-row image URL + result attachment/context WITHOUT
 * pushing a chat message. Shared by `generateReferenceRow` (which adds the
 * message/decision widget) and the pure `generateSinglePdpImage` core (which
 * returns the result). The returned `context` omits `isLast/showFinish/
 * totalCount`; the caller owns those (they depend on the queue index).
 */
async function generateReferenceRowUrl(adapter, row, queue, { userInstructions = null, overlayText = null, overlayTextMode = 'editable', brandLogoUrl = null } = {}) {
  let imageUrl;
  if (HERO_WHITE_BG_TYPES.includes(row.type)) {
    imageUrl = await generatePdpHeroWhiteBgImageUrl(adapter, {
      type: row.type,
      productContext: queue.productContext,
      size: queue.size,
      productUrl: queue.overlayUrl,
      userInstructions: userInstructions || null,
      overlayText: overlayText || null,
      overlayTextMode,
      brandLogoUrl: brandLogoUrl || null,
      language: queue.targetLanguage || null,
    });
  } else if (isStandaloneLayerType(row.type)) {
    // Standalone layer types (e.g. nutrition_facts) carry explicit "DO NOT
    // draw the product" instructions in their type prompt. The reference
    // flow runs the inputs through buildReferenceDesignPayload's GPT-4o
    // Case A/B classifier, which routinely misroutes a nutrition label to
    // Case A ("replace product") and Gemini produces a hero shot of the
    // wrong flavor. Bypass the reference matcher and mirror the
    // suppressProductRefs path used by the non-reference queue: type prompt
    // only, no product or reference image input.
    imageUrl = await generatePdpHeroWhiteBgImageUrl(adapter, {
      type: row.type,
      productContext: queue.productContext,
      size: queue.size,
      productUrl: null,
      userInstructions: userInstructions || null,
      overlayText: overlayText || null,
      overlayTextMode,
      brandLogoUrl: brandLogoUrl || null,
      language: queue.targetLanguage || null,
    });
  } else {
    imageUrl = await generatePdpReferenceImageUrl(adapter, {
      baseProductUrl: queue.overlayUrl,
      referenceUrl: row.reference_image_url,
      size: queue.size,
      userInstructions: userInstructions || null,
      overlayText: overlayText || null,
      overlayTextMode,
      brandLogoUrl: brandLogoUrl || null,
      language: queue.targetLanguage || null,
      // A cross-sell shows a SECOND product: whatever the user named in a regenerate
      // prompt (queue._extraProductUrls), else the Product 2 chosen when the set was
      // created (queue._crossSellProduct). (POR-13612-5-2)
      extraProductUrls: (queue._extraProductUrls && queue._extraProductUrls.length)
        ? queue._extraProductUrls
        : ((row.type === 'cross_sell' && queue._crossSellProduct) ? [queue._crossSellProduct] : []),
    });
  }

  const label = row.label || (BUILT_IN_TYPES[row.type] && BUILT_IN_TYPES[row.type].label) || 'Reference';
  const attachment = { type: 'url', payload: imageUrl, name: label };
  const context = {
    type: row.type,
    label,
    productContext: queue.productContext,
    size: queue.size,
    productMode: 'integrated',
    referenceImageUrl: row.reference_image_url,
    overlayUrl: queue.overlayUrl,
    pathType: queue.pathType,
    rowIndex: queue.currentIndex,
    rowMode: 'ai',
  };

  return { imageUrl, attachment, context };
}

/**
 * A reference row with NO product (POR-14794). "Match a style" swaps the
 * reference's product for ours; with no product of ours Gemini kept the
 * reference's product or invented one. Instead the reference is only the style:
 * edit it into this tile's scene with every product taken out.
 */
async function generateNoProductReferenceRowUrl(adapter, row, queue, { userInstructions = null, overlayText = null, overlayTextMode = 'editable', brandLogoUrl = null } = {}) {
  const prompt = 'IMAGE 1 is a STYLE REFERENCE only: match its mood, lighting, color palette, setting and composition, and take out any product, packaging, label or brand logo it shows. '
    + buildPdpImagePrompt(row.type, queue.productContext, userInstructions, 'integrated', null, overlayText, brandLogoUrl, null, overlayTextMode, false, queue.targetLanguage || null, true);

  const imageUrl = await postDesignWithRetry(
    {
      image_prompt: prompt,
      user_sizes: queue.size,
      product_images_names: [],
      reference_images_urls: [unwrapShareUrl(row.reference_image_url)],
      brand_logo_url: brandLogoUrl || null,
      selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
    },
    { source: adapter.getSource?.() || 'creative_studio_chat' },
  );

  const label = row.label || (BUILT_IN_TYPES[row.type] && BUILT_IN_TYPES[row.type].label) || 'Reference';
  return {
    imageUrl,
    attachment: { type: 'url', payload: imageUrl, name: label },
    context: {
      type: row.type,
      label,
      productContext: queue.productContext,
      size: queue.size,
      productMode: 'integrated',
      referenceImageUrl: row.reference_image_url,
      overlayUrl: null,
      noProduct: true,
      pathType: queue.pathType,
      rowIndex: queue.currentIndex,
      rowMode: 'ai',
    },
  };
}

/**
 * Generate one reference-seeded AI row: place the target product into the
 * row's source image. Produces the same result card + decision widget as the
 * type-based path so the existing Keep / Refine / Skip handlers work.
 */
async function generateReferenceRow(adapter, row, queue) {
  const label = row.label || (BUILT_IN_TYPES[row.type] && BUILT_IN_TYPES[row.type].label) || 'Reference';
  const totalCount = queue.types.length;
  const indexDisplay = totalCount > 1 ? ` (${queue.currentIndex + 1}/${totalCount})` : '';
  const isLast = queue.currentIndex === totalCount - 1;
  const showFinish = totalCount > 1;

  adapter.setProcessing(true);
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Generating <b>${label}</b> image${indexDisplay}...`,
    _progressDurationMs: 30000,
    _progressStartedAt: Date.now(),
  });

  try {
    // Through the core, with the row's direction, overlay text and product (or
    // none), as the grid builds it — called bare, all of them were dropped. (POR-14794)
    const result = await generateSinglePdpImage(adapter, sequentialRowQueue(queue, row), queue.currentIndex);
    if (!result.ok) throw result.error || new Error('Reference row generation failed');
    const { attachment, context } = result;

    adapter.setProcessing(false);
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `<b>${label}</b>`,
      attachments: [attachment],
      _pdpImageContext: {
        ...context,
        isLast,
        showFinish,
        totalCount,
      },
      widgets: [buildPdpImageDecisionWidget({ isLast, showFinish, totalCount })],
    });
  } catch (e) {
    adapter.setProcessing(false);
    console.error('Failed to generate reference row image', e);
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `I couldn't generate the <b>${label}</b> image. You can skip it or try again.`,
      widgets: [buildPdpImageDecisionWidget({ isLast, showFinish, totalCount })],
      _pdpImageContext: {
        type: row.type,
        label,
        productContext: queue.productContext,
        size: queue.size,
        productMode: 'integrated',
        referenceImageUrl: row.reference_image_url,
        overlayUrl: queue.overlayUrl,
        pathType: queue.pathType,
        rowIndex: queue.currentIndex,
        rowMode: 'ai',
        isLast,
        showFinish,
        totalCount,
      },
    });
  }
}

/**
 * PURE core: generate the single image for `queue.types[index]` and RETURN the
 * result (image URL + result attachment + `_pdpImageContext`) instead of
 * pushing a chat message. Extracted verbatim from `generateNextInPdpQueue`'s
 * generation body so the same code can drive both the sequential one-at-a-time
 * queue and (future) batch paths. The caller owns all chat side effects
 * (progress/result/failure messages, processing flag, pending-job snapshot,
 * resolvePendingDesignJob, finalize). The only injected side effect is `onUuid`,
 * which the design call invokes with the in-flight job uuid so the caller can
 * register its own pending-job snapshot.
 *
 * Returns on success:
 *   { ok: true,  imageUrl, attachment, context, error: null, typeLabel }
 * Returns on a thrown generation error:
 *   { ok: false, imageUrl: null, attachment: null, context: null, error, typeLabel }
 */
export async function generateSinglePdpImage(adapter, queue, index, { onUuid = null } = {}) {
  const imageType = queue.types[index];
  const userPrompt = withRefinement(queue._extraUserPrompt, queue._refinePrompt);
  // What the row asked for, kept on the result so a regenerate rebuilds from it
  // instead of from the typed text alone. (POR-14794)
  const rowPrompt = {
    userInstructions: queue._extraUserPrompt || null,
    overlayText: queue._overlayText || null,
    overlayTextMode: queue._overlayTextMode || 'editable',
    brandLogoUrl: queue._brandLogoUrl || null,
    libraryKey: queue.rowSelections?.[index]?.libraryKey || null,
  };

  // Reference rows (mixed path, AI mode with a source reference image) are
  // generated through the reference helpers — identical to the inline
  // generateReferenceRow path, but returned instead of pushed.
  // EXCEPTION: when the Layered toggle requests 'separate' for a Lifestyle, the
  // reference helper (generateReferenceRowUrl) is hardcoded 'integrated' and
  // bakes the product INTO the reference-styled scene, which then gets the
  // product layered on top = a duplicate. Fall through to the standard
  // generation instead, which honors 'separate' and builds a product-FREE
  // lifestyle background + overlay (the same path non-reference Lifestyle uses).
  // Static (integrated) still keeps the reference style. (POR-13271)
  const wantSeparateLifestyle = queue.productModeOverride === 'separate' && imageType === 'lifestyle';
  if (queue.pathType === 'mixed' && queue.rowSelections?.[index]?.reference_image_url && !wantSeparateLifestyle) {
    const row = queue.rowSelections[index];
    const refLabel = row.label || (BUILT_IN_TYPES[row.type] && BUILT_IN_TYPES[row.type].label) || 'Reference';
    const totalCount = queue.types.length;
    const isLast = index === totalCount - 1;
    const showFinish = totalCount > 1;
    try {
      const generateRow = queue._noProduct ? generateNoProductReferenceRowUrl : generateReferenceRowUrl;
      const { imageUrl, attachment, context } = await generateRow(adapter, row, queue, {
        userInstructions: userPrompt,
        overlayText: queue._overlayText || null,
        overlayTextMode: queue._overlayTextMode || 'editable',
        brandLogoUrl: queue._brandLogoUrl || null,
      });
      return {
        ok: true,
        imageUrl,
        attachment,
        context: { ...context, ...rowPrompt, isLast, showFinish, totalCount },
        error: null,
        typeLabel: refLabel,
      };
    } catch (e) {
      return { ok: false, imageUrl: null, attachment: null, context: null, error: e, typeLabel: refLabel };
    }
  }

  const typeLabel = imageType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  // queue.productModeOverride: set when the grid's Layered/Static toggle
  // regenerates a tile, so 'separate' builds a product-FREE background (the
  // product is layered at layout creation) instead of baking it in — otherwise
  // the layered endpoint would stamp the product a SECOND time. (POR-13271)
  // With no product there is nothing to layer: Layered would be an empty background
  // left for a product that never comes, so the tile is built whole. (POR-14794)
  const productMode = queue._noProduct
    ? 'integrated'
    : (queue.productModeOverride || getDefaultProductMode(imageType, queue.pathType));
  const totalCount = queue.types.length;
  const isLast = index === totalCount - 1;

  const overlayTextForPrompt = queue._overlayText || null;
  // Lifestyle integrated: feed the EXACT product packshot as the BASE image (handled
  // below) so Gemini PRESERVES the real product and only builds the scene AROUND it,
  // instead of REDRAWING an unfaithful approximation from a loose product "reference"
  // (which produced a recolored can with garbled label text). Pairs the "keep the base
  // product" prompt variant with forcing the requested aspect ratio server-side. (POR-13612)
  const lifestyleFromProduct = imageType === 'lifestyle'
    && productMode === 'integrated'
    && !!queue.overlayUrl;
  // Grounded infographics NEED the product brief; await the (already in-flight)
  // deferred brief here so it overlapped the non-grounded tiles instead of
  // blocking the whole set up-front. The `_groundingPromise` guard means this only
  // engages when the parallel grid pre-started the brief — the sequential path
  // (no deferral, productGrounding already resolved or null-fallback) stays
  // byte-identical. Non-grounded types never wait. (POR-13612)
  let grounding = queue.productGrounding || null;
  if (!grounding && queue._groundingPromise && isGroundableInfographicType(imageType)) {
    grounding = await ensureGrounding(queue);
  }
  // For Hero / Cambridge / GS1 in Layered mode we want a truly empty bg (the
  // product is overlaid as a separate layer; for Cambridge the rectangles
  // are painted deterministically by the backend; for GS1 the bg MUST be
  // pure white per spec). If we pass the product image as reference, Gemini
  // tends to invent an "ad" composition with hallucinated patterns or
  // callouts ("Instant Refresh", swirling backdrops, etc). Strip product
  // refs in those cases so Gemini just produces white pixels.
  // In Layered (separate) mode we must NOT feed the product image to Gemini —
  // with the packshot as input it tends to DRAW the product into the background
  // anyway (despite the "no product" prompt), which then gets the product
  // overlaid on top = a duplicate. Hero/Cambridge/GS1 want a white bg; Lifestyle
  // wants a themed scene with empty space — both come from the prompt, and
  // neither should have the product baked in (it's layered separately). (POR-13271)
  const suppressProductRefs = (
    (imageType === 'hero' || imageType === 'mobile_hero_cambridge' || imageType === 'mobile_hero_gs1' || imageType === 'lifestyle')
    && productMode === 'separate'
  ) || isStandaloneLayerType(imageType);
  const usesSimplifiedOverlay = imageType === 'mobile_hero_gs1';
  // GS1 uses the simplified packshot as both the layered overlay AND the
  // reference image for integrated generation so the integrated result
  // matches the decluttered version. Cambridge keeps the raw packshot.
  const effectiveOverlayUrl = usesSimplifiedOverlay && queue.simplifiedOverlayUrl
    ? queue.simplifiedOverlayUrl
    : queue.overlayUrl;

  let productNamesPayload = suppressProductRefs ? [] : (queue.productImageNames || []);
  let referenceUrlsPayload = suppressProductRefs ? [] : (queue.referenceImageUrls || []);
  let productImageUrlsPayload = suppressProductRefs ? [] : (queue.productImageUrls || []);

  if (usesSimplifiedOverlay && productMode === 'integrated' && queue.simplifiedOverlayUrl) {
    // Pass the simplified packshot via reference_images_urls — that field
    // is resolved server-side via isShareUrl() / copyShareUrlToTemp() and
    // becomes an actual image reference for Gemini. product_images_names
    // would treat the URL as a name to look up by GTIN/ASIN/product name,
    // fail every lookup, and leave Gemini with no image at all (which is
    // why it hallucinated random products like Frito-Lay).
    productNamesPayload = [];
    productImageUrlsPayload = [];
    referenceUrlsPayload = [queue.simplifiedOverlayUrl];
  }

  // Lifestyle-from-product: drop the name-based product reference so the existing
  // "no product names" branch below routes the EXACT packshot URL into
  // reference_images_urls → the backend treats it as the base image to PRESERVE
  // (original_image_url), and Gemini keeps the real product while building the scene
  // around it. Paired with preserve_base_product (forces the square aspect ratio so
  // the tall packshot base doesn't get 'cover'-cropped). (POR-13612)
  if (lifestyleFromProduct) {
    productNamesPayload = [];
  }

  // The infographic types must not show the product, so an attached packshot goes
  // to Gemini as a product reference, never as the base image it edits. (POR-14765)
  if (isGroundableInfographicType(imageType)) {
    ({ productNames: productNamesPayload, productImageUrls: productImageUrlsPayload } = attachedProductAsReference({
      productNames: productNamesPayload,
      productImageUrls: productImageUrlsPayload,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
    }));
  }

  // The product packshot is routed as a BASE image (reference_images_urls →
  // backend original_image_url) whenever there is NO product name/ASIN to look up —
  // i.e. for ATTACHED / pasted product images (type 'url'), and for
  // lifestyle-from-product. In that case the backend omits image_config → Gemini
  // returns a non-square image → the 'cover' resize crops the edges (the reported
  // "still cut off" case, reproduced by pasting a product image instead of typing a
  // name). Flag it so the backend forces the requested aspect ratio — same outcome
  // the name/ASIN path already gets (there the product goes as product_images and
  // referenceImagesPaths is empty, so the ratio is already forced). Excludes the
  // "match a style" flow (a real style reference in referenceUrlsPayload). (POR-13612)
  const productRoutedAsBase = productNamesPayload.length === 0
    && productImageUrlsPayload.length > 0
    && referenceUrlsPayload.length === 0;

  // Built here, not above, because the fidelity rule may only claim "IMAGE 1 IS the
  // product" once we know the packshot really is the base image — which is only
  // settled after the payload lists above. Lifestyle keeps its own, proven condition
  // so POR-13612 behaviour is untouched; the widening applies to the other types that
  // bake the product in. (POR-13881-2)
  const baseIsProduct = lifestyleFromProduct
    || (productRoutedAsBase && PRODUCT_FIDELITY_TYPES.includes(imageType));
  // Look the creative direction up under the row's Library key when it has one:
  // a type CS cannot generate natively (or a company's custom type) reaches here
  // as its resolved stand-in, and the direction the company configured is filed
  // under the original key. Falls back to `imageType`, so rows that never came
  // from the Library behave exactly as before. (POR-14039)
  const directionKey = queue.rowSelections?.[index]?.libraryKey || imageType;
  // In a session started from a brief, a tile's direction is the brief's own ask,
  // so it may override the type's default look. (POR-14794)
  const fromBrief = !!adapter.getParsedPrompt?.()?.activeBriefId;
  const prompt = buildPdpImagePrompt(imageType, queue.productContext, userPrompt, productMode, grounding, overlayTextForPrompt, queue._brandLogoUrl || null, getTypeDirection(queue._imageTypeCatalog || [], directionKey), queue._overlayTextMode || 'editable', baseIsProduct, queue.targetLanguage || null, !!queue._noProduct, fromBrief);

  const showFinish = totalCount > 1;

  // For compositional separate-mode types (hero / Cambridge / GS1) Gemini
  // returns just a white background; the chat composes the product overlay
  // via CSS afterwards. The bell notification has no CSS layer, so its
  // thumbnail would be a blank square. Pass the overlay (product packshot)
  // URL along so the backend can use it as the thumbnail in the bell payload.
  // Standalone layer types (e.g. Nutrition Facts) and integrated/lifestyle
  // results don't need this — the generated image is already visually rich.
  const notificationThumbnailUrl = (
    productMode === 'separate'
    && effectiveOverlayUrl
    && !isStandaloneLayerType(imageType)
  ) ? effectiveOverlayUrl : null;

  // GS1 + integrated: the simplified packshot IS the desired final image
  // (clean packshot on white background, with the band drawn in by the
  // simplify step). The design call would only re-decluter / re-style on
  // top of an already-clean image, which Gemini gets wrong (gigantic
  // text labels rendered outside the pack frame, etc.). Skip the design
  // call entirely and use the simplified URL as the final image.
  const skipDesignCall = imageType === 'mobile_hero_gs1'
    && productMode === 'integrated'
    && !!queue.simplifiedOverlayUrl;

  try {
    const imageUrl = skipDesignCall
      ? queue.simplifiedOverlayUrl
      : await postDesignWithRetry(
        {
          image_prompt: prompt,
          user_sizes: queue.size,
          product_images_names: productNamesPayload,
          reference_images_urls: [
            ...referenceUrlsPayload,
            ...(productNamesPayload.length ? [] : productImageUrlsPayload.map(unwrapShareUrl)),
          ],
          brand_logo_url: queue._brandLogoUrl || null,
          notification_thumbnail_url: notificationThumbnailUrl,
          // Product routed as a base image (attachment / lifestyle-from-product) →
          // force the requested aspect ratio so the non-square base output doesn't get
          // 'cover'-cropped. (POR-13612)
          ...(productRoutedAsBase ? { preserve_base_product: true } : {}),
          selected_tentpole: adapter.getSelectedTentpole?.()?.label || null,
        },
        {
          source: adapter.getSource?.() || 'creative_studio_chat',
          onUuid: (uuid) => {
            if (onUuid) onUuid(uuid);
          },
        },
      );

    const attachment = { type: 'url', payload: imageUrl, name: typeLabel };
    if (productMode === 'separate' && effectiveOverlayUrl && !isStandaloneLayerType(imageType)) {
      attachment.overlayUrl = effectiveOverlayUrl;
    }
    // Cambridge MRHI: surface the editable text placeholders in the chat
    // preview via CSS so the user sees the same layout the template will
    // render (Flavor in the top rectangle rotated 90°, Count in the bottom
    // rectangle horizontal). The actual editable fields are injected by the
    // backend at layout-creation time.
    if (imageType === 'mobile_hero_cambridge' && productMode === 'separate') {
      attachment._mrhiCallouts = {
        type: 'cambridge',
        flavorText: 'PRODUCT OR FLAVOR',
        countText: 'Count',
      };
    }

    const context = {
      type: imageType,
      label: typeLabel,
      pathType: queue.pathType,
      allTypes: queue.types,
      index,
      size: queue.size,
      productMode,
      productContext: queue.productContext,
      productGrounding: grounding || queue.productGrounding || null,
      productImageNames: queue.productImageNames || [],
      referenceImageUrls: queue.referenceImageUrls || [],
      productImageUrls: queue.productImageUrls || [],
      overlayUrl: effectiveOverlayUrl || queue.overlayUrl || null,
      // Generated without a product (POR-14794) — no Layered/Static toggle for it.
      noProduct: !!queue._noProduct,
      ...rowPrompt,
      rawOverlayUrl: queue.overlayUrl || null,
      simplifiedOverlayUrl: queue.simplifiedOverlayUrl || null,
      // Snapshot the GS1 approach + user_instructions so the Regenerate
      // button re-runs simplify with the same mode (auto / reference /
      // describe) the initial generation used. Without this, regen
      // defaults to 'auto' even when the user generated in 'describe'
      // mode — and auto-mode + free-form user text is far less reliable
      // than describe-mode (where the prompt is built around the text).
      gs1Approach: queue.gs1Approach || null,
      hasProductRefs: queue.hasProductRefs,
      rowIndex: index,
      rowMode: queue.pathType === 'mixed' ? 'ai' : null,
      isLast,
      showFinish,
      totalCount,
    };

    return { ok: true, imageUrl, attachment, context, error: null, typeLabel };
  } catch (e) {
    return { ok: false, imageUrl: null, attachment: null, context: null, error: e, typeLabel };
  }
}

/**
 * Point the picker widget at the row the sequential queue is currently on, so it
 * can re-open the Customize popover for rows that have NOT been generated yet.
 * Rows before the cursor are done (check badge); the cursor row is in flight or
 * awaiting "Keep & next" and has its own "Refine with feedback". (POR-13869)
 *
 * No-op when the run has no picker (single-image / non-picker flows). The parallel
 * grid never reaches this function, so parallel widgets never get a cursor and the
 * picker keeps its current fully-locked behaviour there.
 */
function syncPickerSequentialCursor(adapter, cursor) {
  const messages = adapter.getMessages() || [];
  const index = findPickerWidgetMessageIndex(messages);

  if (index === -1) return;

  const widgets = messages[index].widgets || [];

  if (widgets.some((w) => w && w.id === 'pdp_image_set_picker' && w.sequentialCursor === cursor)) {
    return; // already in step — skip the save round-trip
  }

  // Commit in memory SYNCHRONOUSLY: the check badge and the wand's visibility must
  // not wait for the network, and a second sync in the same tick (the use-as-is
  // re-entry) has to read THIS cursor instead of the stale one.
  adapter.updateMessage(index, { widgets: withPickerSequentialCursor(widgets, cursor) });

  // Persist through the shared chain. Re-find the picker and re-read its CURRENT
  // widgets at execution time — messages are appended between two syncs, so the
  // index above can be stale by then.
  // THE SAFETY PROPERTY IS THE CHAIN'S ORDERING, not the re-read: saves run one
  // at a time, so the last-QUEUED save is the last POST issued and the last echo
  // applied, and that is by construction the newest cursor. The re-read is an
  // optimization on top — because both syncs happen in one synchronous turn, the
  // queued saves collapse onto identical widgets and the echoes become no-ops.
  // Do not mistake the collapse for the guarantee.
  queuePickerMessageSave(() => {
    const latest = adapter.getMessages() || [];
    const latestIndex = findPickerWidgetMessageIndex(latest);

    if (latestIndex === -1) return null;

    return adapter.updateAndSaveMessage(latestIndex, { widgets: latest[latestIndex].widgets || [] });
  });
}

/**
 * Generate the image at the current queue index. When the queue is exhausted,
 * finalize with a "Create editable layout" message.
 */
export async function generateNextInPdpQueue(adapter) {
  const isStaleConversation = makeConversationStaleCheck(adapter);
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  syncPickerSequentialCursor(adapter, queue.currentIndex);

  if (queue.currentIndex >= queue.types.length) {
    finalizePdpQueue(adapter);
    return;
  }

  if (queue.pathType === 'mixed' && Array.isArray(queue.rowSelections)) {
    const row = queue.rowSelections[queue.currentIndex];
    if (row && (row.mode === 'original' || row.mode === 'yours')) {
      return keepUseAsIsRow(adapter, row, queue);
    }
    if (row && row.mode === 'gallery') {
      return showGalleryRowCard(adapter, row, queue);
    }
    if (row && row.mode === 'scene') {
      return showScenePicker(adapter, row, queue);
    }
    if (row && row.mode === 'ai' && row.reference_image_url) {
      return generateReferenceRow(adapter, row, queue);
    }
    // mode === 'ai' (no reference) falls through to the existing AI generation path below
  }

  const imageType = queue.types[queue.currentIndex];

  // Generic mobile_hero: defer generation until the user picks a style.
  // The widget action handler (NetworksPageV2.vue) rewrites the queue type
  // to mobile_hero_cambridge / mobile_hero_gs1 / mobile_hero_reference and
  // then re-enters generateNextInPdpQueue.
  if (imageType === 'mobile_hero') {
    showMobileHeroStylePicker(adapter);
    return;
  }

  // Just-in-time prep when the user resolved a generic mobile_hero into a
  // specific style via the widget AFTER startPdpQueue ran. startPdpQueue only
  // primes simplification / reference URLs when the type was known up front,
  // so we patch any missing prep here. Idempotent: skips work already done.
  // GS1 needs the user to pick an approach (auto / reference / describe)
  // before we can call simplify with the right params. If no approach is
  // set for the current queue index, show the picker and return.
  if (
    imageType === 'mobile_hero_gs1'
    && (!queue.gs1Approach || queue.gs1Approach.forItemIndex !== queue.currentIndex)
  ) {
    showGs1ApproachPicker(adapter);
    return;
  }

  if (imageType === 'mobile_hero_gs1' && queue.overlayUrl && !queue.simplifiedOverlayUrl) {
    const approach = queue.gs1Approach?.mode || 'auto';
    const payload = { image_url: queue.overlayUrl, approach };
    if (approach === 'reference' && queue.gs1Approach?.referenceUrl) {
      payload.reference_url = queue.gs1Approach.referenceUrl;
    }
    if (approach === 'describe' && queue.gs1Approach?.userInstructions) {
      payload.user_instructions = queue.gs1Approach.userInstructions;
    }
    try {
      const { data } = await axios.post('/networks/simplify-product-image', payload);
      const simplifiedOverlayUrl = data?.simplified_url || null;
      if (simplifiedOverlayUrl) {
        // The local `queue` object still drives THIS generation's payload below,
        // so keep the direct assignment.
        queue.simplifiedOverlayUrl = simplifiedOverlayUrl;
        // ...but re-read parsedPrompt AT WRITE TIME. Simplify is a Gemini
        // round-trip (seconds to tens of seconds) and the picker is already live
        // at this cursor (syncPickerSequentialCursor ran above), so a pending-row
        // edit can land in _pdpQueue.rowSelections / pdp_row_selections while we
        // wait. Writing back the pre-await snapshot would silently revert it —
        // SET_PARSED_PROMPT is a shallow merge, so the spread clobbers
        // pdp_row_selections too, and the widget keeps showing the lost edit
        // because its localRows never re-sync. (POR-13869)
        const latest = adapter.getParsedPrompt() || {};
        adapter.setParsedPrompt({
          ...latest,
          _pdpQueue: { ...(latest._pdpQueue || queue), simplifiedOverlayUrl },
        });
        adapter.saveConversationState();
      }
    } catch (e) {
      console.warn('Could not simplify product image; using raw overlay', e);
    }
  }
  if (imageType === 'mobile_hero_reference') {
    const promptRefs = (parsedPrompt.reference_images_urls || []).filter(Boolean);
    const queueRefs = queue.referenceImageUrls || [];
    const merged = [...new Set([...queueRefs, ...promptRefs])];
    if (merged.length !== queueRefs.length) {
      queue.referenceImageUrls = merged;
      // Same re-read rule as the GS1 branch above. No await runs before this one
      // today (the branches are mutually exclusive on imageType), but a
      // pending-row edit landing mid-step must never be reverted by a stale
      // snapshot, so both _pdpQueue writes in this function stay on one rule.
      // (POR-13869)
      const latest = adapter.getParsedPrompt() || {};
      adapter.setParsedPrompt({
        ...latest,
        _pdpQueue: { ...(latest._pdpQueue || queue), referenceImageUrls: merged },
      });
      adapter.saveConversationState();
    }
  }

  const typeLabel = imageType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  // queue.productModeOverride: set when the grid's Layered/Static toggle
  // regenerates a tile, so 'separate' builds a product-FREE background (the
  // product is layered at layout creation) instead of baking it in — otherwise
  // the layered endpoint would stamp the product a SECOND time. (POR-13271)
  const productMode = queue.productModeOverride || getDefaultProductMode(imageType, queue.pathType);
  const totalCount = queue.types.length;
  const indexDisplay = totalCount > 1 ? ` (${queue.currentIndex + 1}/${totalCount})` : '';
  const isLast = queue.currentIndex === totalCount - 1;

  adapter.setProcessing(true);
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Generating <b>${typeLabel}</b> image${indexDisplay}...`,
    // Drives the indeterminate-but-time-budgeted progress bar in the chat.
    // 30s is a rough average for /networks/design + Gemini; the bar tops
    // out at 100% if generation runs longer, and gets superseded by the
    // result message when it finishes earlier. The timestamp lets the
    // renderer hide the bar when the chat is reopened after the duration
    // has already elapsed (i.e. the work is over and nothing should animate).
    _progressDurationMs: 30000,
    _progressStartedAt: Date.now(),
  });

  // Pre-build the result message with everything except the URL. If the user
  // navigates away mid-generation, the chat mount logic uses this snapshot to
  // rebuild the same composite (overlay + Cambridge callouts + ctx) instead of
  // showing just the raw Gemini background. This mirrors the core's overlay
  // selection (GS1 simplified packshot vs raw) so the snapshot composite
  // matches the finished result.
  const showFinish = totalCount > 1;
  const usesSimplifiedOverlay = imageType === 'mobile_hero_gs1';
  const effectiveOverlayUrl = usesSimplifiedOverlay && queue.simplifiedOverlayUrl
    ? queue.simplifiedOverlayUrl
    : queue.overlayUrl;
  const pendingAttachment = { type: 'url', payload: PENDING_DESIGN_JOB_PLACEHOLDER, name: typeLabel };
  if (productMode === 'separate' && effectiveOverlayUrl && !isStandaloneLayerType(imageType)) {
    pendingAttachment.overlayUrl = effectiveOverlayUrl;
  }
  if (imageType === 'mobile_hero_cambridge' && productMode === 'separate') {
    pendingAttachment._mrhiCallouts = {
      type: 'cambridge',
      flavorText: 'PRODUCT OR FLAVOR',
      countText: 'Count',
    };
  }
  const pendingResultMessage = {
    role: 'assistant',
    content: `<b>${typeLabel}</b>`,
    attachments: [pendingAttachment],
    _pdpImageContext: {
      type: imageType,
      label: typeLabel,
      productContext: queue.productContext,
      size: queue.size,
      productMode,
      productImageNames: queue.productImageNames,
      referenceImageUrls: queue.referenceImageUrls,
      overlayUrl: effectiveOverlayUrl,
      rawOverlayUrl: queue.overlayUrl,
      simplifiedOverlayUrl: queue.simplifiedOverlayUrl,
      // Snapshot the GS1 approach used at generation time so the
      // Regenerate button can re-call simplify with the same approach
      // (auto / reference / describe) for a fresh Gemini attempt.
      gs1Approach: queue.gs1Approach || null,
      hasProductRefs: queue.hasProductRefs,
      pathType: queue.pathType,
      rowIndex: queue.currentIndex,
      rowMode: queue.pathType === 'mixed' ? 'ai' : null,
      isLast,
      showFinish,
      totalCount,
    },
    widgets: [buildPdpImageDecisionWidget({ isLast, showFinish, totalCount })],
  };

  let pendingUuid = null;

  // Generation body lives in the pure core. The wrapper owns the pending-job
  // snapshot: when the design call reports its uuid, register the snapshot so a
  // mid-flight reopen can rebuild the same composite.
  // Thread the row's per-image direction (the wand text, POR-13271) into the
  // design prompt for this AI row, mirroring the grid's generatePdpGridTile.
  const seqRow = (queue.pathType === 'mixed' && Array.isArray(queue.rowSelections))
    ? queue.rowSelections[queue.currentIndex]
    : null;
  const seqTileQueue = sequentialRowQueue(queue, seqRow);
  const result = await generateSinglePdpImage(adapter, seqTileQueue, queue.currentIndex, {
    onUuid: (uuid) => {
      pendingUuid = uuid;
      adapter.pushPendingDesignJob({
        uuid,
        flow: 'pdp',
        messageSnapshot: pendingResultMessage,
      });
    },
  });

  // The user left this chat while the image generated ("New" / opened another
  // conversation) — discard the result instead of appending it to (and advancing
  // a queue into) the now-active chat. Clean up the pending recovery entry. (POR-13504)
  if (isStaleConversation()) {
    if (pendingUuid) adapter.resolvePendingDesignJob(pendingUuid);
    return;
  }

  try {
    if (result.ok) {
      adapter.addAndSaveMessage({
        role: 'assistant',
        content: `<b>${typeLabel}</b>`,
        attachments: [result.attachment],
        _pdpImageContext: result.context,
        widgets: [buildPdpImageDecisionWidget({ isLast, showFinish, totalCount })],
      });

      if (pendingUuid) adapter.resolvePendingDesignJob(pendingUuid);
    } else {
      if (pendingUuid) adapter.resolvePendingDesignJob(pendingUuid);
      // Always expose Retry, Skip, and Finish — Skip used to be omitted on the
      // last item, which left the user stranded when the final item failed
      // (their only path forward was "Finish with what I have so far", which
      // discards the failed slot AND finalizes; no way to drop just the failed
      // step and keep the queue available for, say, regenerating earlier items).
      const failureButtons = [
        { label: 'Retry', action: 'pdp_queue_retry', variant: 'primary' },
        { label: 'Skip', action: 'pdp_queue_skip_failure', variant: 'secondary' },
        { label: 'Finish', action: 'pdp_queue_finish_from_failure', variant: 'plain' },
      ];

      adapter.addAndSaveMessage({
        role: 'assistant',
        content: `Failed to generate <b>${typeLabel}</b> image.`,
        _pdpQueueFailure: true,
        widgets: [{
          type: 'action_buttons',
          id: 'pdp_queue_failure',
          buttons: failureButtons,
        }],
      });
    }
  } finally {
    adapter.setProcessing(false);
  }
}

/**
 * Mark the kept image and advance the queue when the image is the current one.
 * When the user regenerates an earlier (already-past) image and re-clicks Keep,
 * we simply re-mark it kept without disturbing the queue position.
 */
export async function keepAndAdvancePdpQueue(adapter, messageIndex) {
  const message = adapter.getMessages()[messageIndex];
  const ctx = message?._pdpImageContext;

  if (!ctx) {
    return;
  }

  const queue = await getQueueOrRebuild(adapter, ctx);

  if (!queue) {
    return;
  }

  const isCurrent = queue.types?.[queue.currentIndex] === ctx.type;

  await adapter.updateAndSaveMessage(messageIndex, {
    _pdpImageKept: true,
    _pdpImageSkipped: false,
    widgets: [],
  });

  if (!isCurrent) {
    return;
  }

  // Derive the next queue from the LATEST _pdpQueue, not from the `queue`
  // captured before the awaited message save: a pending-row edit can land in
  // that window and would otherwise revert in _pdpQueue.rowSelections while
  // surviving in pdp_row_selections, silently splitting the two. Only the
  // advance itself is ours — it must stay relative to the index we validated
  // against ctx.type above. (POR-13869)
  const latestParsed = adapter.getParsedPrompt() || {};
  const nextQueue = {
    ...(latestParsed._pdpQueue || queue),
    currentIndex: queue.currentIndex + 1,
  };

  adapter.setParsedPrompt({
    ...latestParsed,
    _pdpQueue: nextQueue,
  });

  await adapter.saveConversationState?.();

  if (nextQueue.currentIndex >= nextQueue.types.length) {
    finalizePdpQueue(adapter);
    return;
  }

  await generateNextInPdpQueue(adapter);
}

/**
 * Retry the current queue step — just re-runs generateNextInPdpQueue without
 * advancing the index. Used when generation failed.
 */
export async function retryPdpQueueStep(adapter, messageIndex) {
  // Clear the failure widget so the user can't double-click and disable the
  // message so it's clear retry is in flight. Without this the user has no
  // visual confirmation that retry kicked off — long /networks/design calls
  // can take 30s+, which feels like "nothing happened."
  adapter.updateAndSaveMessage(messageIndex, { widgets: [], disabled: true });
  adapter.addAndSaveMessage({
    role: 'assistant',
    content: 'Retrying...',
  });
  await generateNextInPdpQueue(adapter);
}

/**
 * Advance past a failed step without retrying. Skips the current index and
 * moves on to the next (or finalizes if the failed item was the last).
 */
export async function skipFailedPdpQueueStep(adapter, messageIndex) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  adapter.updateAndSaveMessage(messageIndex, { widgets: [], disabled: true });
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: { ...queue, currentIndex: queue.currentIndex + 1 },
  });
  adapter.saveConversationState();
  await generateNextInPdpQueue(adapter);
}

/**
 * Finalize the queue after a failure, using whatever kept images exist.
 */
export async function finishPdpQueueFromFailure(adapter, messageIndex) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  adapter.updateAndSaveMessage(messageIndex, { widgets: [] });
  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: { ...queue, currentIndex: queue.types.length },
  });
  adapter.saveConversationState();
  finalizePdpQueue(adapter);
}

/**
 * Skip the current image in the queue without keeping it. Advances to the
 * next image (or finalizes if it was the last).
 */
export async function skipPdpQueue(adapter, messageIndex) {
  const message = adapter.getMessages()[messageIndex];
  const ctx = message?._pdpImageContext;

  if (!ctx) {
    return;
  }

  const queue = await getQueueOrRebuild(adapter, ctx);

  if (!queue) {
    console.warn('Skip clicked but _pdpQueue could not be rebuilt', {
      ctx,
      message,
      parsedPrompt: adapter.getParsedPrompt(),
    });
    return;
  }

  const isCurrent = queue.types?.[queue.currentIndex] === ctx.type;

  await adapter.updateAndSaveMessage(messageIndex, {
    widgets: [],
    _pdpImageSkipped: true,
    _pdpImageKept: false,
  });

  if (!isCurrent) {
    return;
  }

  // Same re-read as keepAndAdvancePdpQueue: the awaited message save above opens
  // a window for a pending-row edit to land in _pdpQueue, so everything except
  // the advance comes from the fresh read. (POR-13869)
  const latestParsed = adapter.getParsedPrompt() || {};

  adapter.setParsedPrompt({
    ...latestParsed,
    _pdpQueue: {
      ...(latestParsed._pdpQueue || queue),
      currentIndex: queue.currentIndex + 1,
    },
  });

  await adapter.saveConversationState?.();

  await generateNextInPdpQueue(adapter);
}

/**
 * Stop the queue early and finalize using whatever images have been kept so
 * far. Behaves like "Skip + End": the current (un-kept) image is discarded
 * from the final layout. Use case: "I'm good with what I have, create the
 * layout now with the images I've already kept."
 *
 * Guard: if the user clicks Finish before keeping any image, the widget is
 * preserved so they can still Keep/Refine/Skip — and a friendly hint is shown.
 */
export async function finishPdpQueue(adapter, messageIndex) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  if (!queue) return;

  // Treat the current image as kept on Finish: the user has visually
  // approved it by clicking Finish on its card, so include it in the
  // final layout (same outcome as if they had clicked Keep & next, then
  // Finish, but in one click).
  const messagesNow = adapter.getMessages() || [];
  const currentMsg = messagesNow[messageIndex];
  if (currentMsg && currentMsg._pdpImageContext && !currentMsg._pdpImageKept) {
    adapter.updateAndSaveMessage(messageIndex, { _pdpImageKept: true, widgets: [] });
  } else {
    adapter.updateAndSaveMessage(messageIndex, { widgets: [] });
  }

  // Re-read after the update so the kept count includes the current one.
  const allMessages = adapter.getMessages() || [];
  const keptCount = allMessages.filter((m) => m._pdpImageKept && m._pdpImageContext).length;

  if (keptCount === 0) {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: 'No images kept yet — keep at least one image before finishing.',
    });
    return;
  }

  adapter.setParsedPrompt({
    ...parsedPrompt,
    _pdpQueue: { ...queue, currentIndex: queue.types.length },
  });
  adapter.saveConversationState();

  adapter.saveMessages();
  finalizePdpQueue(adapter, { autoCreate: false });
}

/**
 * Emit the final "Create layout" message after the queue is done. Rebuilds the
 * image list from the chat messages so any regenerations made after "Keep &
 * next" are still reflected in the final layout. Different pathType maps to a
 * different final button/action.
 *
 * When `autoCreate` is true, the helper appends a `_pdpGeneratedImages`
 * message WITHOUT the confirmation button and immediately triggers the
 * layout creation via the adapter — this is the path used by the Finish
 * button, which should fast-forward straight to the layout.
 */
function finalizePdpQueue(adapter, { autoCreate = false } = {}) {
  const parsedPrompt = adapter.getParsedPrompt();
  const queue = parsedPrompt._pdpQueue;
  const allMessages = adapter.getMessages() || [];

  // Queue is over — every row is settled, so the picker shows a check on all of them.
  if (queue && Array.isArray(queue.types)) {
    syncPickerSequentialCursor(adapter, queue.types.length);
  }

  let generated = [];
  let hasAiOrScene = false;

  if (queue?.pathType === 'mixed' && Array.isArray(queue.rowSelections)) {
    for (const row of queue.rowSelections) {
      const msg = findKeptMessageForRow(allMessages, row.rowIndex, row.type);
      if (!msg) continue;
      const ctx = msg._pdpImageContext || {};
      const url = msg.attachments?.[0]?.payload || null;
      // For separate-mode AI rows (Hero / Cambridge / GS1) the chat preview is
      // a CSS composite: a blank generated background plus a CSS-overlaid
      // packshot. When the user picks "Create Layout" (non-editable) the
      // layout builder needs the overlay URL to produce a flat tile that
      // matches the preview — using `url` alone would render the blank bg.
      const overlayUrl = ctx.overlayUrl || ctx.simplifiedOverlayUrl || null;
      generated.push({
        rowIndex: row.rowIndex,
        type: row.type,
        label: row.label,
        rowMode: row.mode,
        url,
        overlayUrl,
        // Fall back to ctx.templateId so gallery rows still survive when the
        // queue's rowSelections array was rehydrated without template_id (e.g.
        // a stale state snapshot from before the user picked a template).
        template_id: row.template_id || ctx.templateId || null,
        template_image_url: row.template_image_url || ctx.templateImageUrl || ctx.templateThumb || null,
        productMode: ctx.productMode || null,
        overlayText: row.overlayText || null,
        overlayTextMode: row.overlayTextMode || 'editable',
      });
      if (row.mode === 'ai' || row.mode === 'scene') hasAiOrScene = true;
    }
  } else {
    // Legacy non-mixed queue path — preserved unchanged behavior
    const typesInOrder = queue?.types || [];
    for (const type of typesInOrder) {
      const msg = [...allMessages].reverse().find((m) => m._pdpImageContext?.type === type && m._pdpImageKept);
      const url = msg?.attachments?.[0]?.payload;
      if (url) {
        const ctx = msg._pdpImageContext || {};
        const overlayUrl = ctx.overlayUrl || ctx.simplifiedOverlayUrl || null;
        generated.push({
          type,
          url,
          overlayUrl,
          productMode: ctx.productMode || null,
          label: ctx.label,
          rowMode: 'ai',
        });
        hasAiOrScene = true;
      }
    }
  }

  if (generated.length === 0) {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: 'No images kept yet — keep at least one image before finishing.',
    });
    return;
  }

  if (autoCreate) {
    adapter.addAndSaveMessage({
      role: 'assistant',
      content: `Adding <b>${generated.length}</b> PDP image${generated.length > 1 ? 's' : ''} to your layout...`,
      _pdpGeneratedImages: generated,
      _pdpHasAiOrScene: hasAiOrScene,
    });
    const messages = adapter.getMessages() || [];
    const targetIndex = messages.length - 1;
    if (typeof adapter.triggerPdpLayoutCreation === 'function') {
      adapter.triggerPdpLayoutCreation(targetIndex, queue?.pathType || 'mixed');
    }
    return;
  }

  const isSingle = generated.length === 1;
  const buttons = [
    { label: 'Create layout', action: 'create_pdp_mixed_layout:false', variant: 'primary' },
  ];
  if (hasAiOrScene && !isSingle) {
    buttons.push({
      label: 'Create editable layout',
      action: 'create_pdp_mixed_layout:true',
      variant: 'primary',
    });
  }

  adapter.addAndSaveMessage({
    role: 'assistant',
    content: `Generated <b>${generated.length}</b> PDP image${isSingle ? '' : 's'}. Click below to create the layout.`,
    _pdpGeneratedImages: generated,
    _pdpHasAiOrScene: hasAiOrScene,
    widgets: [{
      type: 'action_buttons',
      id: 'pdp_mixed_create_layout',
      buttons,
    }],
  });
}

/**
 * Build a tailored prompt for a specific PDP image type.
 */
export function buildPdpImagePrompt(imageType, productContext, userPrompt, productMode, productGrounding = null, overlayText = null, brandLogoUrl = null, catalogDirection = null, overlayTextMode = 'editable', baseIsProduct = false, language = null, noProduct = false, directionLeads = false) {
  // When a real product brief was extracted, prefer its identified name over the
  // raw product id/code so ${product} reads e.g. "OREO Chocolate Sandwich
  // Cookies" instead of a Salsify id. (POR-13307)
  const product = (productGrounding && productGrounding.name && String(productGrounding.name).trim())
    ? String(productGrounding.name).trim()
    : (productContext || 'the product');
  const typeLabel = TYPE_LABELS[imageType] || imageType;

  // Separate-layer treatment (themed background + no product drawn) is the
  // default for Hero when a product image is available. Other image types
  // default to integrated, but the user can override per-image.
  const isSeparateLayer = productMode === 'separate';

  // All integrated prompts enforce NO themed background by default. The image
  // must use a plain white (or very light neutral) backdrop unless the user
  // specifically asks for a themed scene via the refine-with-feedback flow.
  const noBgRule = `CRITICAL REQUIREMENT: the background MUST be plain pure white (#FFFFFF) — solid white only, NOT colored, NOT gradient, NOT textured, NOT themed, NO props, NO scene, NO bathroom, NO kitchen, NO outdoor setting, NO swirls, NO graffiti, NO hand-lettering, NO painted words, NO background text, NO background letters, NO patterns, NO shapes, NO QR codes, NO QR-code-like blocks, NO pixelated patterns, NO checkerboard, NO checkered patterns, NO halftone, NO dithering, NO noise, NO scattered dots, NO decorative backdrop. The white background is non-negotiable — any text, callouts, or labels must appear only as part of the foreground composition described below, never on or behind the background. Only the subject matter described — nothing else.`;

  // White-background infographic types (Feature-Benefits, How-To, Ingredients,
  // Reason-To-Believe) stay retailer-compliant with a pure white backdrop, but
  // the FOREGROUND should feel on-brand instead of generic and dull. Pull the
  // color palette and visual style from the product's own packaging (available
  // to the model as the provided product reference image) and apply it to the
  // icons, headers, dividers, and badges — never to the background. (POR-13612)
  const brandAccentRule = `BRAND STYLING: keep the background pure white as required above, but make the FOREGROUND on-brand rather than a generic grey/blue template. Derive the color palette, accent colors, and overall visual style from the product's own packaging and branding (use the provided product reference image only as a color/style guide — do NOT redraw the product). Apply those brand colors to the icons, headings, section titles, dividers, underlines, number badges, and text accents so the composition clearly reflects THIS product's identity. Keep the icons clean, modern, and polished — styled to match the brand's look and feel, not flat generic grey. Do NOT tint, wash, or color the white background itself; the brand colors belong only to the foreground elements.`;

  const integratedPrompts = {
    hero: `A retailer-ready hero product image for ${product}. ${noBgRule} The product must be rendered large, centered, and prominently displayed, occupying a significant portion of the frame. Professional studio product photography with clean, even lighting, accurate colors, sharp focus, and soft natural shadows. The background behind and around the product MUST be pure white (#FFFFFF) — no graffiti, no hand-lettered words, no painted text, no decorative patterns, no scene, no environment, no shadow on the background, no colored wash. No text, no graphics, no watermarks, no additional objects on or around the product — only the product itself on a plain white background. This image must comply with retailer (e.g. Amazon) main-image guidelines.`,
    mobile_hero_cambridge: `A Cambridge Mobile Ready Hero Image (MRHI) for ${product}.
Square composition. ${noBgRule}
Render the product packshot AS-IS in its ORIGINAL form — DO NOT declutter, DO NOT remove marketing badges, callouts, slogans, banners, or sub-text. Keep the pack EXACTLY as it would appear on the retail shelf. Offset the product to the LEFT of center to leave room for two off-pack callouts on the right.
Along the RIGHT edge of the canvas, render TWO solid black rectangles stacked vertically, both flush against the right edge of the canvas:
- TOP rectangle: spans from the top of the canvas down to approximately 73% of the canvas height. Width approximately 22% of the canvas width. Inside, render the product's primary VARIANT or FLAVOR name (e.g. "CHOCOLATE", "OREO-FLAVORED", "ORIGINAL") in bold WHITE sans-serif text rotated 90° clockwise so it reads from bottom to top. The text fills most of the rectangle's height and is centered within it.
- BOTTOM rectangle: starts at approximately 78% of the canvas height and reaches the bottom of the canvas. Width approximately 22% of the canvas width (same as top). Inside, render the product's COUNT / SIZE / NET CONTENT (e.g. "12 BARS", "200ML", "5.71 OZ") in bold WHITE sans-serif text horizontally, on two lines if needed, centered within the rectangle.
- Leave a small gap (~5% of canvas height) between the two rectangles.
Use a clean, bold, condensed sans-serif typeface (Helvetica / Roboto / Open Sans Condensed style) for both callouts.
The two black rectangles must have CLEAN SOLID FILL (#000000) with crisp straight edges — NO pixelation, NO QR-code patterns, NO checkerboard, NO halftone, NO dithering, NO noise inside or along the edges of the rectangles.
Background: pure white (#FFFFFF) on the area to the LEFT of the rectangles where the product sits, AND on any area between, around, or outside the rectangles. Solid white only — NO pixelated patterns, NO QR-code-like blocks, NO checkered patterns, NO halftone, NO dithering, NO noise, NO scattered shapes, NO decorative dots, NO graphics of any kind in the white area.
NO other text, callouts, lozenges, badges, decorative shapes, or design elements outside the pack besides the two solid black rectangles described above.`,
    mobile_hero_gs1: `A GS1 Mobile Ready Hero Image (MRHI) for ${product}.
VERTICAL full-bleed composition: the product MUST touch the top and bottom edges of the canvas with NO white space, NO padding, NO margin on the top or bottom. Do not scale the product down — it must use the full vertical height of the canvas.
Render a single, decluttered packshot, centered horizontally. Plain pure white (#FFFFFF) background only on the sides if needed — solid white only, NOT colored, NOT gradient, NOT textured, NOT themed, NO props, NO scene, NO swirls, NO graffiti, NO hand-lettering, NO painted words, NO background text, NO background letters, NO patterns, NO shapes, NO decorative elements behind or around the product.
AGGRESSIVELY REMOVE from the pack design: marketing icons and badges (e.g. "heart healthy", "no. 1 brand", "award-winning"), calorie / nutritional info boxes, ingredient callouts, fine print, sub-descriptor lines, side-of-pack info, banner overlays, roundels, and any text that would be illegible at thumbnail size.
KEEP intact: the brand logo, the pack's primary background illustration / artwork, the contents visual (e.g. bowl of oatmeal, dish, drink, garnish, fruit), and the original brand colors and identity.
The primary variant / flavor name on the pack must be PROMINENT — bold, sans-serif, centered in its color-blocked section, using the original typeface and noticeably larger than other on-pack text.
The 4Ws (brand, product type, variant, net content) must remain clearly readable on the pack itself at thumbnail size on a mobile device.
NO additional text, captions, titles, callouts, badges, or design elements outside or on top of the pack — the image tile must be just the clean, recognisable product.`,
    mobile_hero_reference: `Generate a mobile hero image for ${product}. You are given TWO input images in this exact order: (1) FIRST IMAGE = THE PRODUCT — this is the actual product packshot to render, copy its exact appearance, brand, label, shape, and proportions; (2) SECOND IMAGE = STYLE REFERENCE — this is ONLY a visual style guide; copy its color palette, gradients, decorative shapes, geometric blocks, panels, layout structure, typography treatment, and overall design language. CRITICAL RULES: do NOT use the FIRST IMAGE for style — its colors and artwork are NOT the design language. do NOT depict, redraw, or include the product shown in the SECOND IMAGE — that product is irrelevant; use only its surrounding STYLE. Compose the final hero by placing the FIRST IMAGE's product into a layout that matches the SECOND IMAGE's style — same colors, same panels/blocks, same composition logic. Replace any literal text from the SECOND IMAGE with text appropriate for ${product}, or omit text entirely. CRITICAL: do NOT default to a plain white or studio background — the background must visibly replicate the SECOND IMAGE's style and mood. Vertical mobile-friendly composition, readable at thumbnail size.`,
    ingredients: `An ingredients callout image for ${product}. Clean design highlighting key ingredients with visual icons (illustrative line/flat icons, NOT photos) and short text callouts. ${noBgRule} IMPORTANT: DO NOT draw, include, render, or photograph the product packshot/bottle/can/box anywhere in the composition. The product image will NOT be added separately either — show ONLY the ingredient icons and callouts on a clean white background. The product is referenced solely so you know what ingredients to feature; the product itself is NOT shown. ${brandAccentRule}`,
    nutrition_facts: `A "Nutrition Facts" image for ${product} in the standard FDA nutrition label format. ${noBgRule} Black text on pure white: title "Nutrition Facts" top-left in bold, serving size, calories, % Daily Values table with nutrients. Use only the data available for this product; do not invent values — if a value is unknown, omit the row. Strict typography, no illustrations, no decorative elements. IMPORTANT: DO NOT draw, render, include, or photograph the product packshot/bottle/can/box anywhere in the image. The product is referenced solely so you know what nutrition values to display; the product itself is NOT shown. Output is purely the FDA label, edge-to-edge, with NO product imagery overlaid or composited.`,
    lifestyle: `A lifestyle product image for ${product}. You are given the actual product packshot as a PRODUCT REFERENCE IMAGE — render THAT EXACT product faithfully: same brand, same label and wording, same colors, same packaging shape and proportions. Do NOT invent, swap, redraw, or substitute a different product, a different variant, or a different brand, and do NOT redesign the packaging — the product in the scene MUST be visually identical to the reference. Show that product in a realistic, appealing usage environment that matches the product's category and audience — e.g. a beauty product on a modern bathroom counter, a kitchen gadget on a warm-lit kitchen counter, outdoor gear in a natural outdoor setting. Warm, inviting, softly lit, photo-real. Include relevant props that make sense for the context (towels, plants, morning light, etc.), but keep the product as the clear focal point. This image INTENTIONALLY has a themed background — do NOT use a plain white background here.`,
    how_to: `An instructional "How to Use" image for ${product} on a PURE WHITE (#FFFFFF) background. BACKGROUND REQUIREMENT (HIGHEST PRIORITY): the entire canvas background MUST be pure white (#FFFFFF) — NO tinted backdrop, NO mint/teal/green/blue/pink/pastel wash, NO gradient, NO texture, NO themed scene. Even if the product's packaging has strong brand colors, DO NOT carry those colors into the background — keep the background strictly #FFFFFF solid. CONTENT: arrange 3–4 numbered steps in a clean grid (or row). Each step = a simple illustration of the action being performed with the product, plus a short numbered label like "1. SHAKE WELL". Illustrations can be colored, but the area around and between them must remain pure white. No scene, no environment, no shadows on the background — just the step illustrations and text floating on white. ${brandAccentRule}`,
    feature_benefits: `A "Feature-Benefits" PDP image for ${product}. ${noBgRule} Visual breakdown of 3–4 key features paired with the corresponding benefit in short plain language (feature on top, benefit right under it). Use clean icons or callouts (illustrative line/flat icons, NOT photos), tight typography, and a balanced grid. Keep copy short and benefit-first — the reader must immediately understand why the feature matters. IMPORTANT: DO NOT draw, include, render, or photograph the product packshot/bottle/can/box anywhere in the composition. The product image will NOT be added separately either — show ONLY the feature-benefit pairs (icon + text) on a clean white background. The product is referenced solely so you know what features/benefits to call out; the product itself is NOT shown. ${brandAccentRule}`,
    reason_to_believe: `A "Reason to Believe" product detail page image for ${product}. Show key benefits, trust signals, or unique selling points using clean text callouts and illustrative icons (line/flat icons, NOT photos). ${noBgRule} IMPORTANT: DO NOT draw, include, render, or photograph the product packshot/bottle/can/box anywhere in the composition. The product image will NOT be added separately either — show ONLY the trust signals, benefit callouts, and supporting icons on a clean white background. The product is referenced solely so you know which trust signals are relevant; the product itself is NOT shown. ${brandAccentRule}`,
    product_gallery: `A polished "product gallery" PDP image for ${product}. Render the actual provided product packshot faithfully — same brand, label, shape, colors and proportions — large and prominently displayed as the clear focal point. Place it on the background the user describes; if none is given, default to a neutral, softly textured backdrop (e.g. stone, linen, or a muted gradient). This image INTENTIONALLY has a styled background — do NOT use a plain pure-white studio background. If the user provides a tagline, a brand-logo cue, or overlay text, render it tastefully and legibly as part of the composition (tagline/logo small and along an edge or corner; overlay text as a clean caption). Premium, editorial product-photography look, soft natural lighting, accurate colors, sharp focus.`,
    cross_sell: `A cross-sell promotional PDP image for ${product}. Feature the actual provided product packshot faithfully as the hero, styled to invite the shopper to discover more of the brand's range. Derive the color palette and mood from the product's own branding and packaging colors so the image feels on-brand. Keep the layout clean and marketing-forward with room for a short call-to-action. If the user provides overlay text (e.g. "Find your ritual"), render it prominently and legibly. Do NOT invent competitor products or unrelated logos; the focus stays on this product and the brand's look. The background may be a soft branded color or gradient — not a plain studio white.`,
    category_gallery: `A category / collection lifestyle PDP image for ${product}. Build the lifestyle scene the user describes (e.g. a serene bathroom counter, a styled vanity, a calm spa setting) and place the provided product naturally within it as the focal point; the scene may suggest a broader product collection if the user describes it that way. Warm, inviting, photo-real, softly lit, with props that fit the context. This image INTENTIONALLY has a themed background — do NOT use a plain white background. If the user provides overlay text (e.g. "Elevate your self-care"), render it cleanly and legibly over the scene without obscuring the product.`,
    feature_iconography_around: `A PDP image for ${product} with benefit icons integrated AROUND the product. Render the actual provided product packshot faithfully, centered and prominent. Arrange clean illustrative benefit icons (simple line/flat icons, NOT photos) in a balanced ring/layout around the product, each paired with a short text label. Use the SPECIFIC benefits the user lists (e.g. Longest Lasting, Sensory Experience, Sustainable, Vegan, Nourishing, Luxury); if the user lists none, choose a few sensible benefits for this product category. Keep the background light and uncluttered (white or a very light neutral) so the product and the icons read clearly. Tight, legible typography; icons evenly distributed.`,
    feature_iconography_bottom: `A PDP image for ${product} with benefit icons integrated along the BOTTOM of the image. Render the actual provided product packshot faithfully in the upper / central portion of the frame, prominent and clear. Across the bottom, render a clean horizontal band/row of illustrative benefit icons (simple line/flat icons, NOT photos), each with a short text label beneath or beside it. Use the SPECIFIC benefits the user lists (e.g. Longest Lasting, Sustainable, Vegan, Nourishing); if the user lists none, choose a few sensible benefits for this product category. Keep the background light and uncluttered (white or a very light neutral) so the product and the icon strip read clearly. Tight, legible typography.`,
    // Legacy keys — preserved so old stored prompts still render something reasonable.
    comparison: `A product comparison image for ${product}. Before/after or side-by-side comparison highlighting product benefits. ${noBgRule} Clean layout with the comparison panels on a plain white background.`,
    features: `A product features highlight image for ${product}. Visual breakdown of key features with callouts and icons. ${noBgRule} Clean layout on a plain white background.`,
    product_detail: `A professional product detail page image for ${product}. Clean, commercial quality product photography. ${noBgRule}`,
  };

  // Layered / separate-layer prompts: produce a BACKGROUND image where the
  // actual product will be overlaid later as a separate layer. By default we
  // keep the backdrop plain (no themed scene) so the result stays retailer-
  // friendly; themed scenes are only used when the user explicitly asks via
  // the refine-with-feedback flow.
  const plainLayeredBg = `CRITICAL REQUIREMENT: the background MUST be plain pure white (#FFFFFF) — solid white only, NOT colored, NOT gradient, NOT textured, NOT themed, NO props, NO scene, NO swirls, NO graffiti, NO hand-lettering, NO painted words, NO background text, NO background letters, NO patterns, NO shapes, NO QR codes, NO QR-code-like blocks, NO pixelated patterns, NO checkerboard, NO checkered patterns, NO halftone, NO dithering, NO noise, NO scattered dots, NO decorative elements. DO NOT draw, include, show, or depict any product, bottle, can, box, or packaging. Leave the entire frame clean and empty, ready for a separate product layer to be placed on top.`;

  const separatePrompts = {
    // Hero Layered is a plain white canvas — the product is added as a separate
    // layer in the template. Gemini occasionally art-directs decorative content
    // (graffiti patterns, themed scenes, hand-lettered words) into what should
    // be a blank white image, so the prompt is intentionally aggressive with
    // explicit "no X" prohibitions covering every category we've seen leak in.
    hero: `A completely blank, plain pure white (#FFFFFF) background image at the requested size. Solid white fill edge-to-edge. No content of any kind: no product, no bottle, no can, no box, no packaging, no scene, no environment, no countertop, no shelf, no shapes, no rectangles, no callouts, no badges, no logos, no text, no letters, no words, no graffiti, no hand-lettering, no patterns, no swirls, no decorative elements, no gradient, no noise, no texture, no shadow. Just a solid #FFFFFF canvas. The product packshot is added by the template builder as a separate layer.`,
    mobile_hero_cambridge: `A completely blank, plain pure white (#FFFFFF) background image at the requested size. Solid white fill edge-to-edge. No content of any kind: no product, no scene, no shapes, no rectangles, no callouts, no text, no gradient, no noise, no texture. Just a solid #FFFFFF canvas. The Cambridge MRHI callout rectangles and the product packshot are added by the template builder as separate layers.`,
    mobile_hero_gs1: `A vertical BACKGROUND for a GS1 Mobile Ready Hero Image (MRHI). ${plainLayeredBg} The decluttered packshot will be overlaid as a separate layer in the center, occupying the full vertical height of the canvas. The Quantity text placeholder is added separately by the template builder — do NOT draw any text, icons, or design elements in this background.`,
    mobile_hero_reference: `Generate a vertical BACKGROUND for a Mobile Hero. You are given TWO input images in this exact order: (1) FIRST IMAGE = the actual product (provided ONLY so you understand its category/silhouette/proportions — do NOT draw it, do NOT incorporate its colors or artwork into the background); (2) SECOND IMAGE = STYLE REFERENCE — copy ITS color palette, gradients, decorative shapes, geometric blocks, panels, layout structure, and overall design language to build the background. CRITICAL RULES: do NOT depict any product, packaging, bottle, can, box, label, or product silhouette in this image — the actual product overlay is added separately by the template builder. do NOT use the FIRST IMAGE's colors or artwork for the background — its visual identity is NOT the design language. do NOT include the product depicted in the SECOND IMAGE — that product is irrelevant; reproduce ONLY the surrounding style. Replicate the SECOND IMAGE's mood and design language faithfully — this is the core requirement. CRITICAL: do NOT default to plain white or empty canvas — the background must visibly match the SECOND IMAGE's style. Leave a clear focal area near the center where the product will be overlaid. Do NOT include any literal text or callouts from the SECOND IMAGE — replicate only the colors, shapes, gradients, and composition.`,
    ingredients: `An ingredients BACKGROUND LAYOUT for ${product}. Icons and short callouts representing key ingredients, positioned around the edges. ${plainLayeredBg}`,
    nutrition_facts: `A complete "Nutrition Facts" image for ${product} in the standard FDA nutrition label format. Black text on pure white background — no themed scene, no decoration. IMPORTANT: DO NOT draw, render, include, or photograph the product packshot/bottle/can/box anywhere in the image. The product is referenced solely so you know what nutrition values to display; the product itself is NOT shown. Render the FULL label edge-to-edge: title "Nutrition Facts" top-left in bold, serving size, calories, % Daily Values table with nutrients. Use only the data available for this product; if a value is unknown, omit the row. Do NOT leave empty space for any other content - this image is the entire deliverable.`,
    lifestyle: `A lifestyle BACKGROUND SCENE for ${product}. Create a realistic, photo-real environment where this product would naturally be used — e.g. for a beauty product a bathroom counter or vanity, for a kitchen gadget a kitchen, for outdoor gear an outdoor setting. Warm, inviting, softly lit, with props that fit the context (towels, plants, natural light, styled surfaces). This image INTENTIONALLY has a themed background — do NOT use a plain white background here. IMPORTANT: do NOT draw, include, show, or depict the actual product, bottle, can, or packaging — leave a natural empty space (countertop, table, shelf) in the composition where a separate product image will be placed on top as a movable overlay layer.`,
    how_to: `A "How to Use" instructional BACKGROUND LAYOUT for ${product}. Include numbered step panels with simple line illustrations of the USAGE (hands performing the action — NOT the product itself). ${plainLayeredBg} The product will be overlaid as a separate layer.`,
    feature_benefits: `A "Feature-Benefits" BACKGROUND LAYOUT for ${product}. Grid of 3–4 feature-benefit pairs with icons and short copy positioned around the edges. ${plainLayeredBg} Keep the central area empty for the product to be overlaid as a separate layer.`,
    reason_to_believe: `A "Reason to Believe" BACKGROUND LAYOUT for ${product}. Add supporting elements only — trust badges, benefit icons, short text callouts — positioned around the edges. ${plainLayeredBg} Leave the main area empty for the product to be placed as a separate layer.`,
    product_gallery: `A "product gallery" BACKGROUND for ${product}. Create the styled backdrop the user describes, or default to a neutral, softly textured backdrop (e.g. stone, linen, or a muted gradient). Leave a clear empty focal area in the center where the product will be placed as a separate overlay layer. If the user provides a tagline, brand-logo cue, or overlay text, render it tastefully along an edge or corner, away from the central focal area. IMPORTANT: do NOT draw, include, show, or depict the product, bottle, can, box, or packaging — only the background and any requested text/branding.`,
    cross_sell: `A cross-sell promotional BACKGROUND for ${product}. Use a soft branded color palette and a clean, marketing-forward backdrop. Leave a clear empty focal area where the product will be placed as a separate overlay layer. If the user provides overlay text (e.g. "Find your ritual"), render it prominently, away from the focal area. IMPORTANT: do NOT draw, include, or depict the product or any packaging — only the background and requested text.`,
    category_gallery: `A category / collection lifestyle BACKGROUND SCENE for ${product}. Build the lifestyle environment the user describes (e.g. a serene bathroom scene), warm, photo-real, softly lit, with fitting props. This image INTENTIONALLY has a themed background — do NOT use a plain white background. Leave a natural empty space (countertop, shelf, table) where a separate product image will be placed on top as an overlay. If the user provides overlay text, render it cleanly over the scene, away from the product space. IMPORTANT: do NOT draw, include, or depict the actual product or packaging.`,
    feature_iconography_around: `A benefit-icon BACKGROUND LAYOUT for ${product} with icons arranged AROUND a central empty area. Render clean illustrative benefit icons (line/flat, NOT photos) in a balanced ring around the center, each with a short label, using the SPECIFIC benefits the user lists (e.g. Longest Lasting, Vegan, Sustainable); if none are given, choose sensible ones for the category. Keep the center empty for the product to be overlaid as a separate layer. Light, uncluttered background (white or very light neutral). IMPORTANT: do NOT draw, include, or depict the product or packaging.`,
    feature_iconography_bottom: `A benefit-icon BACKGROUND LAYOUT for ${product} with an icon strip along the BOTTOM. Render a clean horizontal band of illustrative benefit icons (line/flat, NOT photos) across the bottom, each with a short label, using the SPECIFIC benefits the user lists; if none are given, choose sensible ones for the category. Keep the upper / central area empty for the product to be overlaid as a separate layer. Light, uncluttered background (white or very light neutral). IMPORTANT: do NOT draw, include, or depict the product or packaging.`,
    // Legacy keys — preserved for backward compatibility.
    comparison: `A product comparison BACKGROUND LAYOUT for ${product}. Before/after or side-by-side design with arrows, labels, and neutral visuals. ${plainLayeredBg} Leave empty spaces for the product to be placed as separate layers.`,
    features: `A features highlight BACKGROUND LAYOUT for ${product}. Visual breakdown with callouts, icons, and arrows around the edges. ${plainLayeredBg} Leave space for the product to be placed as a separate layer.`,
    product_detail: `A product detail page BACKGROUND for ${product}. ${plainLayeredBg} Leave the main area empty for the product to be overlaid as a separate layer.`,
  };

  const prompts = isSeparateLayer ? separatePrompts : integratedPrompts;
  let finalPrompt = prompts[imageType] || prompts.product_detail;

  // A brief's How To direction sets its own look ("premium home styling") — the
  // retailer white-background default would override it. (POR-14794)
  if (directionLeads && !isSeparateLayer && imageType === 'how_to' && userPrompt && userPrompt.trim()) {
    finalPrompt = `An instructional "How to Use" image for ${product}. CONTENT: arrange 3–4 numbered steps in a clean grid (or row). Each step shows the action being performed, with a short numbered label like "1. SHAKE WELL". STYLE: the "Additional context" below sets the look, the setting and the background (for example styled home photography) — follow it ahead of any default style for this image type, while keeping every step clear and easy to read. If it does not say what the background should be, use a clean, light, uncluttered one.`;
  }

  // Lifestyle-from-product (POR-13612): the ACTUAL product packshot is supplied as the
  // BASE image (IMAGE 1) to preserve — NOT a loose reference — so instruct Gemini to
  // keep the product pixel-identical and only build the scene AROUND it. This replaces
  // the default integrated lifestyle prompt (which had Gemini redraw the product into an
  // unfaithful, recolored can with garbled label text). The generic per-row user
  // direction / catalog direction still append below.
  if (baseIsProduct && !isSeparateLayer && imageType === 'lifestyle') {
    finalPrompt = `A lifestyle product image for ${product}. IMAGE 1 (the base image) IS the actual product — you MUST keep the product itself EXACTLY as it appears in that image: do NOT redraw, re-render, restyle, recolor, relabel, move or rewrite the label, or alter its packaging, text, logo, colors, shape or proportions in ANY way. Preserve the product pixel-for-pixel and keep all label text legible and unchanged. Your ONLY task is to REPLACE the plain background around the product and EXTEND the canvas into a realistic, appealing lifestyle usage environment that fits the product's category and audience (e.g. a beauty product on a modern bathroom counter or vanity, a kitchen gadget on a warm-lit kitchen counter, outdoor gear in a natural outdoor setting). Composite the preserved product naturally into the scene with consistent perspective, matching lighting, and soft realistic shadows/reflections so it sits believably in the environment. Warm, inviting, softly lit, photo-real, with relevant props (towels, plants, morning light, etc.). This image INTENTIONALLY has a themed background — do NOT use a plain white background. The product must remain the clear focal point and stay visually identical to the input image.`;
  } else if (baseIsProduct && !isSeparateLayer && PRODUCT_FIDELITY_TYPES.includes(imageType)) {
    // Same problem as lifestyle, every other type that BAKES THE PRODUCT IN: the real
    // packshot is IMAGE 1, but these prompts only ask to "render the product packshot
    // faithfully" — and "render" reads as "draw it again", so Gemini returned a
    // plausible-looking bottle with invented label artwork, different on every run
    // ("it's like 2 different products are being used"). Their composition text is
    // still what we want, so APPEND the fidelity rule instead of replacing the prompt
    // the way lifestyle does (its replacement restates the whole composition).
    // (POR-13881-2)
    finalPrompt += ` PRODUCT FIDELITY (HIGHEST PRIORITY): IMAGE 1 (the base image) IS a photograph of the real product. Use that exact photograph — do NOT redraw, re-render, restyle, recolor, relabel, rewrite or re-typeset it, and do NOT alter its packaging, label artwork, label text, logo, colors, shape or proportions in ANY way. Keep every word on the label exactly as it appears, and do NOT invent brand names, product names, taglines or label graphics that are not already in IMAGE 1. Where the instructions above say to "render" the product, that means COMPOSITE the unmodified product from IMAGE 1 into the layout: you may reposition, scale and light it to fit the composition, but its appearance must stay faithful to IMAGE 1 pixel for pixel. If the label is small or too low-resolution to read, reproduce it exactly as it looks rather than inventing legible text in its place.`;
  }

  // No product for this tile (POR-14794): the product prompts all assume a product
  // image, and with none Gemini invented one. Infographics keep their composition.
  const showsNoProduct = noProduct && !isSeparateLayer;
  if (showsNoProduct) {
    finalPrompt = PRODUCT_FREE_TYPES.includes(imageType)
      ? `${finalPrompt} ${NO_PRODUCT_RULE}`
      : `A ${typeLabel} PDP image for ${product}, with NO product in it. ${NO_PRODUCT_RULE} Build only the scene, people and setting the directions below describe — photo-real, warm and softly lit, with a themed background rather than a plain white studio backdrop — as a complete composition, without an empty spot left for a product.`;
  }

  // IMPORTANT: tell Gemini to generate ONLY this single image type,
  // not multiple panels or a combined image.
  finalPrompt += ` CRITICAL: Generate ONLY a single ${typeLabel} image. Do NOT include multiple image types, panels, or sections for other image types (hero, how to, reason to believe, etc.) in this one image. This image represents ONLY "${typeLabel}".`;

  // Inject the extracted product brief so infographic copy reflects the REAL
  // product instead of generic/unrelated content (the OREO-pack → smart-water-
  // bottle hallucination). Only the GROUNDABLE infographic types carry a brief;
  // when a slice is empty the prompt still benefits from the real product name
  // resolved above. (POR-13307)
  if (productGrounding && GROUNDABLE_INFOGRAPHIC_TYPES.includes(imageType)) {
    const g = productGrounding;
    let items = '';
    if (imageType === 'feature_benefits' && Array.isArray(g.feature_benefits)) {
      items = g.feature_benefits
        .slice(0, 4)
        .filter((fb) => fb && (fb.feature || fb.benefit))
        .map((fb) => `${fb.feature || ''}${fb.feature && fb.benefit ? ' — ' : ''}${fb.benefit || ''}`)
        .join('; ');
    } else if (imageType === 'reason_to_believe' && Array.isArray(g.reasons_to_believe)) {
      items = g.reasons_to_believe.slice(0, 4).filter(Boolean).join('; ');
    } else if (imageType === 'ingredients' && Array.isArray(g.ingredients)) {
      items = g.ingredients.slice(0, 6).filter(Boolean).join('; ');
    } else if (imageType === 'how_to' && Array.isArray(g.how_to)) {
      items = g.how_to.slice(0, 4).filter(Boolean).map((s, i) => `${i + 1}. ${s}`).join(' ');
    }
    if (items) {
      finalPrompt += ` PRODUCT GROUNDING (MANDATORY — the copy MUST be about ${product} specifically; do NOT invent or substitute unrelated features, categories, or sample content such as water bottles, electronics, or anything that does not match this product): use ONLY these real points: ${items}.`;
    }
  }

  // Hero / Cambridge / GS1 in separate mode are deliberately blank white
  // canvases — appending the user's free-form prompt context can only
  // contaminate them (we've seen Gemini pull themed words, colors, or scenes
  // out of unrelated user copy). Skip the user-context append for these.
  const isBlankBgSeparate = isSeparateLayer && (
    imageType === 'hero'
    || imageType === 'mobile_hero_cambridge'
    || imageType === 'mobile_hero_gs1'
    || imageType === 'mobile_hero_reference'
  );

  // Layer the catalog's default creative direction (editable per image type in
  // the Library catalog) as a reusable creative seed BEFORE the per-row user
  // instructions. When catalogDirection is null/empty, this block is a no-op and
  // the prompt is byte-identical to what it was before. (POR-13484)
  if (catalogDirection && catalogDirection.trim() && !isBlankBgSeparate) {
    finalPrompt += ` Creative direction: ${catalogDirection.trim()}`;
  }

  if (userPrompt && !isBlankBgSeparate) {
    // Strip references to other image types to avoid confusing Gemini
    const cleanedUserPrompt = userPrompt
      .replace(/\b(image set|multiple images|hero image|reason to believe image|how to image|lifestyle image|comparison image|features image|ingredients image)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (cleanedUserPrompt) {
      finalPrompt += ` Additional context: ${cleanedUserPrompt}`;
    }
  }

  if (overlayTextMode === 'part_of_image' && overlayText && overlayText.trim() && !isBlankBgSeparate) {
    finalPrompt += overlayTextPrompt(overlayText);
  }

  if (brandLogoUrl && brandLogoUrl.trim() && !isBlankBgSeparate) {
    finalPrompt += ` A brand logo has been provided as a dedicated BRAND LOGO IMAGE reference — incorporate it prominently in the design. Use ONLY that logo graphic; do NOT substitute it with any logo from the product packaging or from training knowledge.`;
  }

  // The copy must read in the ORIGINAL product's language (from its Amazon
  // marketplace, e.g. amazon.com.mx → Spanish), not English. null → no-op
  // (byte-identical to before), so non-Amazon-URL products are unaffected.
  // (POR-13612-3)
  if (language && String(language).trim() && !isBlankBgSeparate) {
    finalPrompt += ` CRITICAL LANGUAGE REQUIREMENT: ALL visible text in this image — headlines, callouts, badges, labels, feature lists, benefit claims, and any product text — MUST be written in ${String(language).trim()}. Do NOT use any other language.`;
  }

  // Last word, so a catalog or user direction that mentions the product cannot
  // bring one back. (POR-14794)
  if (showsNoProduct) {
    finalPrompt += ' REMINDER: no product or packaging anywhere in this image.';
  }

  return finalPrompt;
}

function getAttachmentImageUrl(att) {
  if (!att) return null;

  const url = att.payload || att.url || att.preview || null;
  if (!url) return null;

  const fileType = String(att.fileType || att.mime_type || '');
  const name = String(att.name || att.path || '').toLowerCase();

  const looksLikeImage =
    fileType.startsWith('image/') ||
    /\.(png|jpe?g|webp|gif)$/i.test(name) ||
    String(url).includes('/share?file=');

  return looksLikeImage ? url : null;
}

function getLatestUserAttachmentRefs(messages = []) {
  const latestUserMessage = [...(messages || [])]
    .reverse()
    .find((m) => {
      return m.role === 'user' &&
        Array.isArray(m.attachments) &&
        m.attachments.some((att) => getAttachmentImageUrl(att));
    });

  if (!latestUserMessage) return [];

  return latestUserMessage.attachments
    .map((att) => {
      const url = getAttachmentImageUrl(att);
      if (!url) return null;

      return {
        type: 'url',
        value: url,
        display_name: att.name || 'Attached product image',
      };
    })
    .filter(Boolean);
}

function rebuildQueueFromImageContext(ctx) {
  if (!ctx) return null;

  const types = Array.isArray(ctx.allTypes) && ctx.allTypes.length
    ? ctx.allTypes
    : ctx.type
      ? [ctx.type]
      : [];

  return {
    pathType: ctx.pathType || 'ai_generate',
    types,
    currentIndex: typeof ctx.index === 'number' ? ctx.index : 0,
    size: ctx.size || '1500x1500',
    overlayUrl: ctx.overlayUrl || ctx.rawOverlayUrl || null,
    simplifiedOverlayUrl: ctx.simplifiedOverlayUrl || null,
    productContext: ctx.productContext || '',
    productImageNames: ctx.productImageNames || [],
    referenceImageUrls: ctx.referenceImageUrls || [],
    hasProductRefs:
      !!ctx.overlayUrl ||
      !!ctx.rawOverlayUrl ||
      !!ctx.productContext ||
      !!(ctx.productImageNames || []).length ||
      !!(ctx.referenceImageUrls || []).length,
  };
}

async function getQueueOrRebuild(adapter, ctx) {
  const parsedPrompt = adapter.getParsedPrompt() || {};
  let queue = parsedPrompt._pdpQueue;

  if (!queue && ctx) {
    queue = rebuildQueueFromImageContext(ctx);

    if (queue) {
      adapter.setParsedPrompt({
        ...parsedPrompt,
        _pdpQueue: queue,
      });
      await adapter.saveConversationState?.();
    }
  }

  return queue;
}
